import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type Map from 'ol/Map';
import type { LayerVisibilityState, LayerOpacityState } from '../utils/MapLayerManager';
import { defaultBsriDateRange } from '../utils/bsriDateRange';
import { cropfieldDelineateFromViewport, type CropfieldDelineationModel } from '../utils/cropfieldDelineateViewport';
import {
  assertFarmLevelMapZoom,
  FARM_LEVEL_MIN_ZOOM,
  getOlMapZoom,
  isMapZoomedForFarmLevel,
} from '../utils/farmLevelMapZoom';
import { crownSegmentationFromViewport, treeDetectFromViewport } from '../utils/treeCrownViewport';
 
import LayerSwitchConfirmDialog from './LayerSwitchConfirmDialog';
import InfoPopupDialog from './InfoPopupDialog';
import DroneServiceRequestModal from './DroneServiceRequestModal';
import { FieldServiceRequestCta } from '../../carbon-projects/FieldServicesLayer';
import { isFieldServiceSensorNavSection } from '../../carbon-projects/fieldServicesNav';
import BlsForecastCard from './BlsForecastCard';
import type { DroneServiceType } from '../../../services/droneServiceService';

/** Agricultural drought VHI (PH MODIS) — hidden while CADI forecast is the primary drought layer. */
const SHOW_AGRICULTURAL_DROUGHT_VHI_LAYER = true;

/** Past Yield WMS — hidden while CADI is the featured multi-temporal drought layer. */
const SHOW_PAST_YIELD_LAYER = false;

/** Black Sigatoka Risk (BSRI) — banana disease risk timelapse (Davao AOI). */
const SHOW_BSRI_LAYER = true;

/** Layperson explanations opened from the header info button. */
const HEADER_LAYER_INFO: Record<string, { title: string; body: string }> = {
  planting: {
    title: 'Rice Planting Method',
    body: 'Shows whether rice was direct-seeded or transplanted. Pick one dry season at a time. This helps compare how fields were established.',
  },
  growthStage: {
    title: 'Rice Crop Growth Stage',
    body: 'Shows which growth stage rice fields are in during the 2026 wet season (September). Each color is a stage, from early growth to harvest. Areas without rice stay transparent.',
  },
  irrigation: {
    title: 'Irrigation Type',
    body: 'Shows how cropland is watered for the second half of September 2026. Fields are marked as irrigated, rainfed, or no irrigation. Land with no active crop stays transparent.',
  },
  lst: {
    title: 'Land Surface Temperature',
    body: 'Daytime temperature of the ground from MODIS. Choose a date range, then load the map. Warmer colors mean hotter ground.',
  },
  vhi: {
    title: 'Agricultural Drought (VHI)',
    body: 'Vegetation Health Index over Philippine cropland. Lower values mean the crop is more stressed by drought. Choose a date range to load the map.',
  },
  bsri: {
    title: 'Black Sigatoka Risk',
    body: 'Weekly risk of Black Sigatoka disease on banana. Recent and upcoming weeks use the ECMWF IFS forecast at about 9 km. Earlier weeks use ERA5-Land. Choose a date range to see where the risk is higher.',
  },
  maize: {
    title: 'Maize Area Map',
    body: 'Where maize is grown in the Philippines.',
  },
  sugarCane: {
    title: 'Sugarcane Area Map',
    body: 'Where sugarcane is grown in the Philippines.',
  },
  rice: {
    title: 'Rice Area Map',
    body: 'Where rice is grown in the Philippines.',
  },
  coconut: {
    title: 'Coconut Area Map',
    body: 'Coconut stands in Region 4A. Most of them are in eastern Quezon.',
  },
  isabela: {
    title: 'Isabela PhilRice drone',
    body: 'Drone maps from the Isabela PhilRice site. Turn on the NDVI or ortho image to view it on the map.',
  },
  cavite: {
    title: 'Cavite Silang drone',
    body: 'Drone maps from the Cavite Silang site. Turn on the NDVI or ortho image to view it on the map.',
  },
  riceForecast: {
    title: 'Rice Yield Forecast',
    body: 'Rice yield predictions for Philippine regions from MODIS NDVI. Choose a place, then run the forecast.',
  },
  awd: {
    title: 'AWD maps',
    body: 'Alternate Wetting and Drying maps are not available yet.',
  },
  trees: {
    title: 'Tree detection',
    body: 'Finds individual trees in the current map view or in an uploaded image.',
  },
  crowns: {
    title: 'Crown segmentation',
    body: 'Outlines each tree crown in the current map view or in an uploaded image.',
  },
  fields: {
    title: 'Field boundary delineation',
    body: 'Draws field boundaries from the current map view or an uploaded image.',
  },
  // One entry per province in the drone registry (`droneSites.ts`).
  ...Object.fromEntries(
    getDroneProvinceNavEntries().map(({ id, province }) => [
      id,
      {
        title: `${province} drone`,
        body: `Drone maps from Leads Agri surveys in ${province}. Open a site, then turn on NDVI, the orthomosaic or the elevation (DEM) to view it on the map.`,
      },
    ])
  ),
};
import type { FloodDamageData } from '../../../types/floodDamage';
import floodDamageDataJson from '../../../assets/floodDamageData.json';
import {
  DEFAULT_FLOOD_DAMAGE_EVENT,
  FLOOD_DAMAGE_COLUMN_HELP,
  flattenFloodDamagePlaces,
  formatFloodDamagePhp,
  formatFloodDamagePhpExact,
  listFloodDamageEvents,
  placesForFloodEvent,
  resolveFloodDamageEventKey,
} from '../utils/floodDamagePlaces';
import logo from '../../../assets/logo-transparent.png';
import { RICE_GROWTH_STAGE_LABEL } from '../constants/riceGrowthStageLayers';
import RiceGrowthStageAreaChart from './RiceGrowthStageAreaChart';
import { IRRIGATION_TYPE_LABEL } from '../constants/irrigationTypeLayers';
import IrrigationAreaChart from './IrrigationAreaChart';
import MultitemporalZonalPanel from './MultitemporalZonalPanel';
import type {
  MultitemporalZonalLayer,
  MultitemporalZonalPanelState,
} from '../utils/multitemporalZonalAnalytics';
import {
  CADI_DROUGHT_DESCRIPTION,
  CADI_DROUGHT_LEGEND_DESCRIPTION,
} from '../constants/cadiDroughtLayers';
import {
  DEFAULT_PEST_RISK_PEST_KEY,
  PEST_RISK_DESCRIPTION,
  PEST_RISK_LEGEND_DESCRIPTION,
  PEST_RISK_PESTS,
  type PestRiskPestKey,
} from '../constants/pestRiskLayers';
import {
  onStartDateChange,
  onEndDateChange,
  clampDateRangeToAvailability,
  normalizeDateRange
} from '../utils/dateRangeInputs';
import type { StrawBurnFetchPayload } from '../../../config/farmWebgisApi';
import { buildCaviteSilangPreviewUrl } from '../constants/caviteSilangLayers';
import { buildIsabelaPhilricePreviewUrl } from '../constants/isabelaPhilriceLayers';
import {
  droneProvinceNavId,
  getDroneProvinceNavEntries,
  getDroneSitesByProvince,
  type DroneSitesControls,
} from '../constants/droneSites';
import DroneSitesSection from './DroneSitesSection';
import {
  DRONE_MISSIONS,
  type DroneMissionConfig,
} from '../constants/dronePrescriptionLayers';
import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import {
  DIGISAKA_API_CROWN_SEGMENTATION,
  DIGISAKA_API_FIELDDELINEATION,
  DIGISAKA_API_TREEDETECTION,
  digisakaAuthRequestInit,
  digisakaMeteredAuthHeaders,
  gptInferenceJsonHeaders,
  gptInferenceMultipartAuthHeaders,
} from '../../../config/digisakaGptInference';
import {
  isDroneSitesAllowed,
  isMapLayerAllowed,
  isPanelTabUnlocked,
  isTreeTabInferenceToolAllowed,
  isEntitlementAllowed,
  LAYER_TOGGLE_PANEL_TAB_ORDER,
  type LayerTogglePanelTabId,
} from '../../../config/analysisLayerEntitlements';
import { notifyInferenceComputeUsage } from '../../../utils/inferenceComputeBudget';
import { buildLargeScaleRiceForecastMonthOptions } from '../utils/riceForecastSeason';
import { useTheme } from '../theme/ThemeContext';
import { getMapLegendTheme, getLayerToggleTheme } from '../theme/themeStyles';

const DIGISAKA_API_BASE = DIGISAKA_API_ORIGIN + '/api';

function buildRiceForecastCsv(data: Record<string, unknown> | null): string {
  if (!data || typeof data !== 'object' || !('forecasts' in data)) return '';
  const forecasts = data.forecasts as Record<string, Record<string, unknown>>;
  const escape = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const header = [
    'month',
    'mean_yield_t_ha',
    'min_yield_t_ha',
    'max_yield_t_ha',
    'base_yield_t_ha',
    'rice_price_php_per_kg',
  ];
  const lines = [header.map(escape).join(',')];
  for (const [month, f] of Object.entries(forecasts)) {
    if (f && typeof f === 'object' && 'error' in f) continue;
    const fs = (f.region_statistics || {}) as Record<string, unknown>;
    lines.push(
      [
        month,
        fs.mean_yield,
        fs.min_yield,
        fs.max_yield,
        f.base_yield,
        f.rice_price,
      ]
        .map(escape)
        .join(','),
    );
  }
  return lines.join('\n');
}

interface LayerTogglePanelProps {
  layerVisibility: LayerVisibilityState;
  layerOpacity: LayerOpacityState;
  onLayerToggle: (layerType: keyof LayerVisibilityState, visible: boolean) => void;
  onOpacityChange: (layerType: keyof LayerOpacityState, opacity: number) => void;
  legendUrls?: Record<string, string>;
  cropAreaLegends?: Record<string, { colors: string[]; labels: string[]; description: string; totalArea?: number }>;
  loadingLayers?: Set<string>;
  onForecastDataUpdate?: (forecastData: any) => void;
  onForecastResultsUpdate?: (forecastResults: any) => void;
  onStrawBurnResultsUpdate?: (strawBurnResults: any) => void;
  onFloodLayerShow?: (layerInfo: { wmsUrl: string; layerName: string; bbox: [number, number, number, number] }) => void;
  onBSRIDateRangeUpdate?: (dateRange: { start_date: string; end_date: string }) => void;
  bsriIsLoading?: boolean;
  riceGrowthStageLoading?: boolean;
  irrigationTypeLoading?: boolean;
  onBSRIAnalyseRequest?: (dateRange: { start_date: string; end_date: string }) => void;
  onStrawBurnDataFetch?: (payload: StrawBurnFetchPayload) => void;
  strawBurnIsLoading?: boolean;
  strawBurnData?: any;
  onTreeDetectionDataUpdate?: (data: any) => void;
  onCrownSegmentationResultsUpdate?: (data: any) => void;
  onCropfieldDelineationUpdate?: (data: any) => void;
  mapCenter?: [number, number];
  mapZoom?: number;
  onSwitchToLargeScaleMode?: () => void;
  embedded?: boolean; // When true, render content directly without toggle button wrapper
  onExpandedTabChange?: (tab: LayerTogglePanelTabId | null) => void;
  onFloodDamageSelect?: (data: FloodDamageData | null) => void;
  selectedFloodDamageId?: string | null;
  onLSTPHDateRangeUpdate?: (dateRange: { start_date: string; end_date: string }) => void;
  lstIsLoading?: boolean;
  onVHIPHDateRangeUpdate?: (dateRange: { start_date: string; end_date: string }) => void;
  vhiIsLoading?: boolean;
  clearMultiTemporalAPITiles?: () => void;
  /** Current OpenLayers map — required for “Use current map view” (field delineation, tree, crown) */
  getOlMap?: () => Map | null;
  /** From GET /auth/me `allowed_layer_keys` (null = show all tools) */
  allowedEntitlementSet?: Set<string> | null;
  /**
   * Controlled expanded tab — when provided (even as `null`), the parent owns which tab is open.
   * `undefined` = uncontrolled (legacy behavior, internal state).
   * Used by `MapToolRail` so the external icon rail can drive tab selection.
   */
  controlledExpandedTab?: LayerTogglePanelTabId | null;
  /** Fires when the panel tries to change tabs while controlled (e.g. user toggles current tab off). */
  onExpandedTabRequestChange?: (tab: LayerTogglePanelTabId | null) => void;
  /** Hide the built-in vertical tab strip — the parent (e.g. `MapToolRail`) renders the icons itself. */
  hideTabStrip?: boolean;
  /** When set, only this header section stays visible. Close in the header clears it. */
  pinnedSectionId?: string | null;
  /** Header info button asked to explain this section. */
  infoSectionId?: string | null;
  onInfoSectionHandled?: () => void;
  /** Turn off the layer that is open in this panel. */
  onClosePinnedSection?: () => void;
  /** Active rice pest/disease for pest risk maps (from MapView). */
  pestRiskPestKey?: PestRiskPestKey;
  onPestRiskPestChange?: (pestKey: PestRiskPestKey) => void;
  /** Farm polygon for drone prescription analytics (TiTiler /statistics). */
  fieldGeoJson?: Record<string, unknown> | null;
  farmId?: string | number | null;
  /** Trigger TiTiler stats + map overlay when NDVI card is clicked. */
  onRequestDronePrescriptionStats?: (mission: DroneMissionConfig) => void;
  onClearDronePrescriptionStats?: () => void;
  /** Leads Agri drone sites by province (registry-driven; see `droneSites.ts`). */
  droneSites?: DroneSitesControls;
  /** CADI / pest municipality summaries — shown inside the layer card, not a second popup. */
  zonalState?: MultitemporalZonalPanelState;
  zonalLayer?: MultitemporalZonalLayer;
  zonalFrameLabel?: string;
  zonalPestLabel?: string;
  zonalProvinces?: string[];
  zonalSelectedProvince?: string;
  onZonalProvinceChange?: (province: string) => void;
  onZonalMunicipalitySelect?: (municipality: string | null) => void;
}

function isAnalysisAbortError(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === 'AbortError'
    : error instanceof Error && error.name === 'AbortError';
}

function opacitySliderProps(isDark: boolean, value: number) {
  const fill = isDark ? '#34d399' : '#008540';
  const track = isDark ? '#3a3a3a' : '#c5d4b8';
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  return {
    className: `w-full h-1.5 rounded-lg appearance-none cursor-pointer opacity-slider ${
      isDark ? 'opacity-slider--dark' : 'opacity-slider--light'
    }`,
    style: {
      background: `linear-gradient(to right, ${fill} 0%, ${fill} ${pct}%, ${track} ${pct}%, ${track} 100%)`,
    } as React.CSSProperties,
  };
}

function CancelAnalysisButton({ isDark, onClick }: { isDark: boolean; onClick: () => void }) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        className={`mt-2 w-full rounded-[10px] border px-3 py-2 text-sm font-semibold ${
          isDark
            ? 'border-white/15 text-[#e6edf3] hover:bg-white/[0.06]'
            : 'border-[#e5e7eb] bg-white text-[#111827] hover:bg-[#f3f4f6]'
        }`}
      >
        Cancel analysis
      </button>
      {confirmOpen &&
        createPortal(
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="cancel-analysis-title">
            <button
              type="button"
              className="absolute inset-0 bg-black/50"
              aria-label="Keep analysis running"
              onClick={() => setConfirmOpen(false)}
            />
            <div
              className={`relative z-10 w-full max-w-sm rounded-[14px] border p-4 shadow-xl ${
                isDark ? 'border-white/10 bg-[#161b22] text-[#e6edf3]' : 'border-[#e5e7eb] bg-white text-[#111827]'
              }`}
            >
              <h3 id="cancel-analysis-title" className="text-base font-semibold">
                Cancel this analysis?
              </h3>
              <p className={`mt-2 text-sm leading-relaxed ${isDark ? 'text-[#c9d1d9]' : 'text-[#374151]'}`}>
                If you tapped cancel by mistake, you can keep it running.
              </p>
              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmOpen(false)}
                  className={`flex-1 rounded-[10px] border px-3 py-2 text-sm font-semibold ${
                    isDark ? 'border-white/15 hover:bg-white/[0.06]' : 'border-[#e5e7eb] hover:bg-[#f3f4f6]'
                  }`}
                >
                  Keep running
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmOpen(false);
                    onClick();
                  }}
                  className="flex-1 rounded-[10px] bg-[#008540] px-3 py-2 text-sm font-semibold text-white"
                >
                  Yes, cancel
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function AiSourceChoices({
  isDark,
  mode,
  onModeChange,
  disabled,
  mapDetail,
  fileDetail,
  accept,
  fileName,
  onFile,
  mapReady,
  zoomReady,
}: {
  isDark: boolean;
  mode: 'mapView' | 'file';
  onModeChange: (mode: 'mapView' | 'file') => void;
  disabled: boolean;
  mapDetail: string;
  fileDetail: string;
  accept: string;
  fileName: string | null;
  onFile: (file: File | null) => void;
  mapReady: boolean;
  zoomReady: boolean;
}) {
  const choiceClass = (active: boolean) =>
    `flex min-w-0 flex-col items-start gap-1 rounded-[12px] border px-2.5 py-2.5 text-left transition-colors ${
      active
        ? isDark
          ? 'border-[#34d399] bg-[rgba(52,211,153,.12)]'
          : 'border-[#008540] bg-[rgba(0,133,64,.08)]'
        : isDark
          ? 'border-[#2d3748] bg-[#1c2432] hover:border-white/20'
          : 'border-[#e5e7eb] bg-white hover:border-[#d1d5db]'
    } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`;

  return (
    <div className="mb-3">
      <p className={`mb-2 text-sm font-medium ${isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'}`}>
        Where should we look?
      </p>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" disabled={disabled} onClick={() => onModeChange('mapView')} className={choiceClass(mode === 'mapView')}>
          <span className={`flex items-center gap-1.5 text-sm font-semibold ${isDark ? 'text-[#e6edf3]' : 'text-[#111827]'}`}>
            <i className="ri-map-2-line" aria-hidden />
            On the map
          </span>
          <span className={`text-sm leading-snug ${isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'}`}>{mapDetail}</span>
        </button>
        <button type="button" disabled={disabled} onClick={() => onModeChange('file')} className={choiceClass(mode === 'file')}>
          <span className={`flex items-center gap-1.5 text-sm font-semibold ${isDark ? 'text-[#e6edf3]' : 'text-[#111827]'}`}>
            <i className="ri-upload-2-line" aria-hidden />
            From a file
          </span>
          <span className={`text-sm leading-snug ${isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'}`}>{fileDetail}</span>
        </button>
      </div>

      {mode === 'mapView' && (
        <div className="mt-2">
          {!mapReady ? (
            <p className={`text-sm leading-snug ${isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'}`}>
              Keep the map open beside this panel, then try again.
            </p>
          ) : !zoomReady ? (
            <p
              className={`rounded-[10px] border px-2.5 py-2 text-sm leading-snug ${
                isDark
                  ? 'border-amber-700/60 bg-amber-950/40 text-amber-200'
                  : 'border-amber-200 bg-amber-50 text-amber-900'
              }`}
            >
              <i className="ri-zoom-in-line mr-1" aria-hidden />
              Zoom in to farm level (zoom {FARM_LEVEL_MIN_ZOOM}+) to draw fields or run AI delineation on the map view.
            </p>
          ) : (
            <p className={`text-sm leading-snug ${isDark ? 'text-[#c9d1d9]' : 'text-[#374151]'}`}>
              Move the map until your farm fills the screen, then press the button below.
            </p>
          )}
        </div>
      )}

      {mode === 'file' && (
        <label
          className={`mt-2 flex items-center gap-2 rounded-[12px] border border-dashed px-3 py-2.5 ${
            disabled ? 'pointer-events-none opacity-60' : 'cursor-pointer'
          } ${
            isDark
              ? 'border-white/15 bg-[#1c2432] text-[#e6edf3]'
              : 'border-[#d1d5db] bg-[#f7faf2] text-[#111827]'
          }`}
        >
          <span
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] ${
              isDark ? 'bg-[rgba(52,211,153,.16)] text-[#34d399]' : 'bg-[rgba(0,133,64,.12)] text-[#008540]'
            }`}
          >
            <i className="ri-folder-upload-line" aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold">{fileName ? 'Change file' : 'Choose a file'}</span>
            <span className={`text-sm ${isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'}`}>
              {fileName || 'A photo or map file from your computer'}
            </span>
          </span>
          <input
            type="file"
            accept={accept}
            className="sr-only"
            disabled={disabled}
            onChange={(e) => onFile(e.target.files?.[0] ?? null)}
          />
        </label>
      )}
    </div>
  );
}

export default function LayerTogglePanel({
  layerVisibility,
  layerOpacity,
  onLayerToggle,
  onOpacityChange,
  legendUrls = {},
  cropAreaLegends = {},
  loadingLayers = new Set(),
  onForecastDataUpdate,
  onForecastResultsUpdate,
  onStrawBurnResultsUpdate,
  onFloodLayerShow,
  onBSRIDateRangeUpdate,
  bsriIsLoading = false,
  riceGrowthStageLoading = false,
  irrigationTypeLoading = false,
  onBSRIAnalyseRequest,
  onStrawBurnDataFetch,
  strawBurnIsLoading = false,
  strawBurnData,
  onTreeDetectionDataUpdate,
  onCrownSegmentationResultsUpdate,
  onCropfieldDelineationUpdate,
  mapCenter,
  mapZoom,
  onSwitchToLargeScaleMode,
  embedded = false,
  onExpandedTabChange,
  onFloodDamageSelect,
  selectedFloodDamageId = null,
  onLSTPHDateRangeUpdate,
  lstIsLoading = false,
  onVHIPHDateRangeUpdate,
  vhiIsLoading = false,
  clearMultiTemporalAPITiles,
  getOlMap,
  allowedEntitlementSet = null,
  controlledExpandedTab,
  onExpandedTabRequestChange,
  hideTabStrip = false,
  pinnedSectionId = null,
  infoSectionId = null,
  onInfoSectionHandled,
  onClosePinnedSection,
  pestRiskPestKey: pestRiskPestKeyProp,
  onPestRiskPestChange,
  fieldGeoJson = null,
  farmId = null,
  onRequestDronePrescriptionStats,
  onClearDronePrescriptionStats,
  droneSites,
  zonalState,
  zonalFrameLabel = '',
  zonalPestLabel,
  zonalProvinces = [],
  zonalSelectedProvince = '',
  onZonalProvinceChange,
  onZonalMunicipalitySelect,
}: LayerTogglePanelProps) {
  const { isDark } = useTheme();
  const lg = useMemo(() => getMapLegendTheme(isDark), [isDark]);
  const lt = useMemo(() => getLayerToggleTheme(isDark), [isDark]);
  const isCompactEmbedded = embedded && hideTabStrip;
  const compactLayerPad = isCompactEmbedded ? 'p-1' : 'p-1.5 sm:p-2';
  const compactSectionPad = isCompactEmbedded ? 'p-1 px-1.5' : 'p-1.5 sm:p-3';
  const compactCardPad = isCompactEmbedded ? 'p-1.5' : 'p-2 sm:p-3';
  const canShow = (k: keyof LayerVisibilityState) => isMapLayerAllowed(allowedEntitlementSet, k);
  const canShowBlsForecast =
    isEntitlementAllowed(allowedEntitlementSet, 'blsForecast') ||
    isEntitlementAllowed(allowedEntitlementSet, 'bls_forecast') ||
    canShow('bsri');
  const isFarmLevelMapZoom = isMapZoomedForFarmLevel(mapZoom);

  const canShowTreeInferenceTree = useMemo(
    () => isTreeTabInferenceToolAllowed(allowedEntitlementSet, 'tree'),
    [allowedEntitlementSet]
  );
  const canShowTreeInferenceCrown = useMemo(
    () => isTreeTabInferenceToolAllowed(allowedEntitlementSet, 'crown'),
    [allowedEntitlementSet]
  );
  const canShowTreeInferenceField = useMemo(
    () => isTreeTabInferenceToolAllowed(allowedEntitlementSet, 'field'),
    [allowedEntitlementSet]
  );

  const [isOpen, setIsOpen] = useState(embedded); // Auto-open when embedded
  const [droneServiceType, setDroneServiceType] = useState<DroneServiceType | null>(null);

  // Tab state: uncontrolled by default, controlled by `controlledExpandedTab` (used by `MapToolRail`).
  // We keep the public names `expandedTab` / `setExpandedTab` so existing code paths continue unchanged.
  const isExpandedTabControlled = controlledExpandedTab !== undefined;
  const [internalExpandedTab, setInternalExpandedTab] = useState<LayerTogglePanelTabId | null>('forecast');
  const expandedTab = isExpandedTabControlled ? (controlledExpandedTab ?? null) : internalExpandedTab;
  const setExpandedTab: React.Dispatch<React.SetStateAction<LayerTogglePanelTabId | null>> = (value) => {
    const next = typeof value === 'function'
      ? (value as (prev: LayerTogglePanelTabId | null) => LayerTogglePanelTabId | null)(expandedTab)
      : value;
    if (isExpandedTabControlled) {
      onExpandedTabRequestChange?.(next);
    } else {
      setInternalExpandedTab(next);
    }
  };
  
  // Notify parent when expanded tab changes
  // LST Philippines (MODIS) state
  const [lstAvailMinDate, setLstAvailMinDate] = useState<string | null>(null);
  const [lstAvailMaxDate, setLstAvailMaxDate] = useState<string | null>(null);
  const [lstStartDate, setLstStartDate] = useState<string>('');
  const [lstEndDate, setLstEndDate] = useState<string>('');
  const [isLstExpanded, setIsLstExpanded] = useState<boolean>(false);

  // VHI Philippines state
  const [vhiAvailMinDate, setVhiAvailMinDate] = useState<string | null>(null);
  const [vhiAvailMaxDate, setVhiAvailMaxDate] = useState<string | null>(null);
  const [vhiStartDate, setVhiStartDate] = useState<string>('');
  const [vhiEndDate, setVhiEndDate] = useState<string>('');
  const [isVhiExpanded, setIsVhiExpanded] = useState<boolean>(false);
  const [isCadiExpanded, setIsCadiExpanded] = useState<boolean>(true);
  const [showCadiInfo, setShowCadiInfo] = useState<boolean>(false);
  const [genericSectionInfo, setGenericSectionInfo] = useState<{ title: string; body: string } | null>(null);
  const [showFloodDamageInfo, setShowFloodDamageInfo] = useState(false);
  const [isPestRiskExpanded, setIsPestRiskExpanded] = useState<boolean>(true);
  const [showPestRiskInfo, setShowPestRiskInfo] = useState<boolean>(false);
  const [showStrawBurnInfo, setShowStrawBurnInfo] = useState<boolean>(false);
  const [localPestRiskPestKey, setLocalPestRiskPestKey] = useState<PestRiskPestKey>(DEFAULT_PEST_RISK_PEST_KEY);
  const pestRiskPestKey = pestRiskPestKeyProp ?? localPestRiskPestKey;

  const handlePestRiskPestSelect = (pestKey: PestRiskPestKey) => {
    if (onPestRiskPestChange) {
      onPestRiskPestChange(pestKey);
    } else {
      setLocalPestRiskPestKey(pestKey);
    }
  };

  // Planting Method section
  const [isPlantingMethodExpanded, setIsPlantingMethodExpanded] = useState<boolean>(false);
  const [isRiceGrowthStageExpanded, setIsRiceGrowthStageExpanded] = useState<boolean>(false);
  /** Ticked on click, before the parent visibility state and tiles catch up. */
  const [growthStageArmed, setGrowthStageArmed] = useState(false);
  const growthStageSawTiles = useRef(false);

  useEffect(() => {
    if (riceGrowthStageLoading) growthStageSawTiles.current = true;
  }, [riceGrowthStageLoading]);

  useEffect(() => {
    if (!growthStageArmed) return;
    if (!layerVisibility.riceGrowthStage) return;
    if (riceGrowthStageLoading) return;
    if (!growthStageSawTiles.current) return;
    growthStageSawTiles.current = false;
    setGrowthStageArmed(false);
  }, [growthStageArmed, layerVisibility.riceGrowthStage, riceGrowthStageLoading]);

  useEffect(() => {
    if (!growthStageArmed) return;
    const timer = window.setTimeout(() => setGrowthStageArmed(false), 8000);
    return () => window.clearTimeout(timer);
  }, [growthStageArmed]);

  const growthStageChecked = layerVisibility.riceGrowthStage || growthStageArmed;
  const growthStageBusy = growthStageArmed && (!layerVisibility.riceGrowthStage || riceGrowthStageLoading || !growthStageSawTiles.current);

  const [isIrrigationTypeExpanded, setIsIrrigationTypeExpanded] = useState<boolean>(false);
  const [irrigationArmed, setIrrigationArmed] = useState(false);
  const irrigationSawTiles = useRef(false);

  useEffect(() => {
    if (irrigationTypeLoading) irrigationSawTiles.current = true;
  }, [irrigationTypeLoading]);

  useEffect(() => {
    if (!irrigationArmed) return;
    if (!layerVisibility.irrigationType) return;
    if (irrigationTypeLoading) return;
    if (!irrigationSawTiles.current) return;
    irrigationSawTiles.current = false;
    setIrrigationArmed(false);
  }, [irrigationArmed, layerVisibility.irrigationType, irrigationTypeLoading]);

  useEffect(() => {
    if (!irrigationArmed) return;
    const timer = window.setTimeout(() => setIrrigationArmed(false), 8000);
    return () => window.clearTimeout(timer);
  }, [irrigationArmed]);

  const irrigationChecked = layerVisibility.irrigationType || irrigationArmed;
  const irrigationBusy = irrigationArmed && (!layerVisibility.irrigationType || irrigationTypeLoading || !irrigationSawTiles.current);

  // Isabela PhilRice drone COGs (under Drone Data tab)
  const [isIsabelaPhilriceExpanded, setIsIsabelaPhilriceExpanded] = useState<boolean>(false);
  const [isCaviteSilangExpanded, setIsCaviteSilangExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (onExpandedTabChange) {
      onExpandedTabChange(expandedTab);
    }
  }, [expandedTab, onExpandedTabChange]);

  // Registry drone layers belong to the Drone Data tab only — drop them when another tab opens.
  const activeDroneSiteLayerId = droneSites?.activeLayerId ?? null;
  const toggleDroneSiteLayer = droneSites?.onToggle;
  useEffect(() => {
    if (
      activeDroneSiteLayerId &&
      expandedTab !== 'droneData' &&
      expandedTab !== 'fieldServices'
    ) {
      toggleDroneSiteLayer?.(activeDroneSiteLayerId, false);
    }
  }, [expandedTab, activeDroneSiteLayerId, toggleDroneSiteLayer]);

  // Fetch LST Philippines availability when multi-temporal tab is active
  useEffect(() => {
    if (expandedTab !== 'multiTemporal') return;
    if (lstAvailMinDate && lstAvailMaxDate) return;

    const fetchAvailability = async () => {
      try {
        const res = await fetch(
          DIGISAKA_API_ORIGIN + '/api/lst-philippines/availability/',
          digisakaAuthRequestInit({ headers: { ...digisakaMeteredAuthHeaders() } })
        );
        if (!res.ok) throw new Error(`LST availability error: ${res.status}`);
        const data = await res.json();
        const min = data.min_date as string;
        const max = data.max_date as string;
        setLstAvailMinDate(min);
        setLstAvailMaxDate(max);

        // Default date range: last 3 months from max availability date,
        // but not earlier than the minimum available date.
        const maxDateObj = new Date(max);
        const startDateObj = new Date(maxDateObj.getTime());
        startDateObj.setMonth(startDateObj.getMonth() - 3);

        const minDateObj = new Date(min);
        const effectiveStart =
          startDateObj < minDateObj ? minDateObj : startDateObj;

        const formatDate = (d: Date) => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        setLstStartDate(formatDate(effectiveStart));
        setLstEndDate(formatDate(maxDateObj));
      } catch (err) {
        console.error('Error fetching LST availability:', err);
      }
    };

    fetchAvailability();
  }, [expandedTab, lstAvailMinDate, lstAvailMaxDate]);

  // Fetch VHI Philippines availability when multi-temporal tab is active
  useEffect(() => {
    if (!SHOW_AGRICULTURAL_DROUGHT_VHI_LAYER) return;
    if (expandedTab !== 'multiTemporal') return;
    if (vhiAvailMinDate && vhiAvailMaxDate) return;

    const fetchAvailability = async () => {
      try {
        const res = await fetch(
          DIGISAKA_API_ORIGIN + '/api/vhi-philippines/availability/',
          digisakaAuthRequestInit({ headers: { ...digisakaMeteredAuthHeaders() } })
        );
        if (!res.ok) throw new Error(`VHI availability error: ${res.status}`);
        const data = await res.json();
        const min = data.min_date as string;
        const max = data.max_date as string;
        setVhiAvailMinDate(min);
        setVhiAvailMaxDate(max);

        const maxDateObj = new Date(max);
        const startDateObj = new Date(maxDateObj.getTime());
        startDateObj.setMonth(startDateObj.getMonth() - 3);

        const minDateObj = new Date(min);
        const effectiveStart =
          startDateObj < minDateObj ? minDateObj : startDateObj;

        const formatDate = (d: Date) => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        setVhiStartDate(formatDate(effectiveStart));
        setVhiEndDate(formatDate(maxDateObj));
      } catch (err) {
        console.error('Error fetching VHI availability:', err);
      }
    };

    fetchAvailability();
  }, [expandedTab, vhiAvailMinDate, vhiAvailMaxDate]);

  // Clear conflicting layers when tab changes
  useEffect(() => {
    if (!expandedTab) {
      // If no tab is expanded, clear all multi-temporal and drone data layers
      // Always clear regardless of visibility state to ensure proper cleanup
      // Also clear any active rice forecast layer when leaving the forecast tab
      onLayerToggle('riceForecast', false);
      layerConfigs.cropArea.forEach(config => {
        onLayerToggle(config.key, false);
      });
      layerConfigs.droneData.forEach(config => {
        onLayerToggle(config.key, false);
      });
      onLayerToggle('isabelaPhilriceNDVI', false);
      onLayerToggle('isabelaPhilriceOrtho', false);
      onLayerToggle('caviteSilangNDVI', false);
      onLayerToggle('caviteSilangOrtho', false);
      layerConfigs.multiTemporal.forEach(config => {
        onLayerToggle(config.key, false);
      });
      // Also clear any API-based multi-temporal tiles (LST/VHI Philippines)
      if (clearMultiTemporalAPITiles) {
        clearMultiTemporalAPITiles();
      }
      ([
        'yield', 'vhi', 'lst', 'bsri',
        'cadiDrought', 'pestRisk',
        'plantingMethodDry2025', 'plantingMethodDry2024',
        'riceGrowthStage', 'irrigationType',
      ] as const).forEach((key) => {
        if (layerVisibility[key]) onLayerToggle(key, false);
      });
      return;
    }

    // Use a small timeout to ensure state has updated
    const timeoutId = setTimeout(() => {
      // Whenever we are on a non-forecast tab, ensure the rice forecast layer is cleared
      if (expandedTab !== 'forecast') {
        onLayerToggle('riceForecast', false);
      }

      // Crop type maps are not part of this panel. Drop them when any other tool is open.
      if (expandedTab !== 'cropArea') {
        layerConfigs.cropArea.forEach(config => {
          onLayerToggle(config.key, false);
        });
      }

      // Leaving Multi-temporal maps drops the raster, legend, and zonal result.
      if (expandedTab !== 'multiTemporal') {
        ([
          'yield', 'vhi', 'lst', 'bsri',
          'cadiDrought', 'pestRisk',
          'plantingMethodDry2025', 'plantingMethodDry2024',
          'riceGrowthStage', 'irrigationType',
        ] as const).forEach((key) => {
          if (layerVisibility[key]) onLayerToggle(key, false);
        });
        clearMultiTemporalAPITiles?.();
      }

      // If on multiTemporal, clear droneData layers
      if (expandedTab === 'multiTemporal') {
        layerConfigs.droneData.forEach(config => {
          onLayerToggle(config.key, false);
        });
        onLayerToggle('isabelaPhilriceNDVI', false);
        onLayerToggle('isabelaPhilriceOrtho', false);
        onLayerToggle('caviteSilangNDVI', false);
        onLayerToggle('caviteSilangOrtho', false);
      }
      // If on droneData, clear multiTemporal layers (including WMS layers)
      else if (expandedTab === 'droneData' || expandedTab === 'fieldServices') {
        layerConfigs.multiTemporal.forEach(config => {
          onLayerToggle(config.key, false);
        });
        if (clearMultiTemporalAPITiles) {
          clearMultiTemporalAPITiles();
        }
      }
      // If on any other tab, clear both droneData and multiTemporal (including WMS layers)
      else {
        layerConfigs.droneData.forEach(config => {
          onLayerToggle(config.key, false);
        });
        onLayerToggle('isabelaPhilriceNDVI', false);
        onLayerToggle('isabelaPhilriceOrtho', false);
        onLayerToggle('caviteSilangNDVI', false);
        onLayerToggle('caviteSilangOrtho', false);
        layerConfigs.multiTemporal.forEach(config => {
          onLayerToggle(config.key, false);
        });
        if (clearMultiTemporalAPITiles) {
          clearMultiTemporalAPITiles();
        }
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [expandedTab]); // Only depend on expandedTab, not layerVisibility to avoid loops
  const legendRef = useRef<HTMLDivElement>(null);

  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingTab, setPendingTab] = useState<LayerTogglePanelTabId | null>(null);

  // Flood damage state — flattened municipalities, filtered by flood event
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [floodDamageEventKey, setFloodDamageEventKey] = useState(DEFAULT_FLOOD_DAMAGE_EVENT);
  const floodDamagePlaces = useMemo(
    () => flattenFloodDamagePlaces(floodDamageDataJson as FloodDamageData[]),
    []
  );
  const floodDamageEvents = useMemo(
    () => listFloodDamageEvents(floodDamagePlaces),
    [floodDamagePlaces]
  );
  const activeFloodDamageEventKey = resolveFloodDamageEventKey(
    floodDamageEvents,
    floodDamageEventKey
  );
  const availableFloodDamageMunicipalities = useMemo(
    () => placesForFloodEvent(floodDamagePlaces, activeFloodDamageEventKey),
    [floodDamagePlaces, activeFloodDamageEventKey]
  );
  const visibleFloodDamageMunicipalities = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return availableFloodDamageMunicipalities;
    return availableFloodDamageMunicipalities.filter((place) =>
      place.municipalityName.toLowerCase().includes(q)
    );
  }, [availableFloodDamageMunicipalities, searchQuery]);

  const handleFloodDamageEventChange = (eventKey: string) => {
    setFloodDamageEventKey(eventKey);
    setSearchQuery('');
    if (!selectedFloodDamageId) return;
    const current = floodDamagePlaces.find((place) => place.id === selectedFloodDamageId);
    const currentName = current?.municipalityName?.toLowerCase();
    const next = placesForFloodEvent(floodDamagePlaces, eventKey).find(
      (place) => place.municipalityName.toLowerCase() === currentName
    );
    onFloodDamageSelect?.(next ?? null);
  };

  // Rice forecast state
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [isForecastLoading, setIsForecastLoading] = useState(false);
  const [isForecastTifDownloading, setIsForecastTifDownloading] = useState(false);
  const [forecastError, setForecastError] = useState<string>('');
  const [forecastResults, setForecastResults] = useState<any>(null);

  /** Rice forecast: cascading location (catalog + EE NAME_1 / NAME_2) */
  const [forecastProvinceOptions, setForecastProvinceOptions] = useState<string[]>([]);
  const [forecastSelectedProvince, setForecastSelectedProvince] = useState<string>('');
  const [forecastMunicipalityOptions, setForecastMunicipalityOptions] = useState<string[]>([]);
  const [forecastSelectedMunicipality, setForecastSelectedMunicipality] = useState<string>('');
  const [forecastProvincesLoading, setForecastProvincesLoading] = useState(false);
  const [forecastMunicipalitiesLoading, setForecastMunicipalitiesLoading] = useState(false);

  // BSRI state
  const [bsriStartDate, setBsriStartDate] = useState<string>(() => defaultBsriDateRange().start_date);
  const [bsriEndDate, setBsriEndDate] = useState<string>(() => defaultBsriDateRange().end_date);
  const [isBsriExpanded, setIsBsriExpanded] = useState<boolean>(false);
  const prevBsriIsLoadingRef = useRef(bsriIsLoading);

  // Trigger "Analyse with Digisaka GPT?" only after BSRI data finishes loading
  useEffect(() => {
    if (prevBsriIsLoadingRef.current && !bsriIsLoading && layerVisibility.bsri) {
      if (onBSRIAnalyseRequest) {
        onBSRIAnalyseRequest(normalizeDateRange(bsriStartDate, bsriEndDate));
      }
    }
    prevBsriIsLoadingRef.current = bsriIsLoading;
  }, [bsriIsLoading, layerVisibility.bsri, onBSRIAnalyseRequest, bsriStartDate, bsriEndDate]);

  // Straw Burn state
  const [provinces, setProvinces] = useState<string[]>([]);
  const [selectedProvince, setSelectedProvince] = useState<string>('');
  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [selectedStrawBurnMunicipality, setSelectedStrawBurnMunicipality] = useState<string>('');
  /** Province = whole province AOI; Municipality = single LGU (requires municipality pick). */
  const [strawBurnScope, setStrawBurnScope] = useState<'province' | 'municipality'>('municipality');
  const currentYear = 2026;
  const [strawBurnStartDate, setStrawBurnStartDate] = useState<string>(`${currentYear}-03-01`);
  const [strawBurnEndDate, setStrawBurnEndDate] = useState<string>(`${currentYear}-04-30`);
  const [isLoadingProvinces, setIsLoadingProvinces] = useState(false);
  const [isLoadingMunicipalities, setIsLoadingMunicipalities] = useState(false);
  const [strawBurnError, setStrawBurnError] = useState<string>('');

  // Tree Detection state
  const [treeDetectionImage, setTreeDetectionImage] = useState<File | null>(null);
  const [isTreeDetectionLoading, setIsTreeDetectionLoading] = useState(false);
  const [treeDetectionStep, setTreeDetectionStep] = useState<'idle' | 'uploading' | 'detecting'>('idle');
  const [treeDetectionError, setTreeDetectionError] = useState<string>('');
  const [treeDetectionResults, setTreeDetectionResults] = useState<any>(null);
  const [treeDetectionParams] = useState({
    tile_size: 1280,
    overlap: 128,
    resolution: 0.5,
    conf_threshold: 0.2
  });

  // Crown Segmentation state
  const [crownSegmentationImage, setCrownSegmentationImage] = useState<File | null>(null);
  const [isCrownSegmentationLoading, setIsCrownSegmentationLoading] = useState(false);
  const [crownSegmentationStep, setCrownSegmentationStep] = useState<'idle' | 'uploading' | 'segmenting'>('idle');
  const [crownSegmentationError, setCrownSegmentationError] = useState<string>('');
  const [crownSegmentationResults, setCrownSegmentationResults] = useState<any>(null);
  const [crownSegmentationParams] = useState({
    tile_size: 1280,
    overlap: 128,
    resolution: 0.5,
    conf_threshold: 0.2,
    sam_model_type: 'vit_b',
    device: 'cpu'
  });
  /** file = user upload; mapView = use visible map extent (same as field delineation) */
  const [treeDetectionInputMode, setTreeDetectionInputMode] = useState<'file' | 'mapView'>('mapView');
  const [crownSegmentationInputMode, setCrownSegmentationInputMode] = useState<'file' | 'mapView'>('mapView');

  // Collapsible sections state
  const [isTreeDetectionExpanded, setIsTreeDetectionExpanded] = useState(false);
  const [isCrownSegmentationExpanded, setIsCrownSegmentationExpanded] = useState(false);

  // Crop-field boundary delineation (YOLO11-seg — requires georeferenced GeoTIFF)
  const [cropfieldDelineationImage, setCropfieldDelineationImage] = useState<File | null>(null);
  const [isCropfieldDelineationLoading, setIsCropfieldDelineationLoading] = useState(false);
  const [cropfieldDelineationStep, setCropfieldDelineationStep] = useState<'idle' | 'uploading' | 'delineating'>('idle');
  const [cropfieldDelineationError, setCropfieldDelineationError] = useState<string>('');
  const [cropfieldDelineationResults, setCropfieldDelineationResults] = useState<any>(null);
  const [cropfieldDelineationParams] = useState({
    tile_size: 1280,
    overlap: 128,
    resolution: 1.0,
    conf_threshold: 0.25,
    include_preview: false,
    tta: false,
    smooth_epsilon: 4,
    max_polygon_vertices: 100,
  });
  const cropfieldDelineationModel: CropfieldDelineationModel = 'dino';
  const [isCropfieldDelineationExpanded, setIsCropfieldDelineationExpanded] = useState(false);
  /** file = user upload; mapView = use visible map extent */
  const [cropfieldDelineationInputMode, setCropfieldDelineationInputMode] = useState<'file' | 'mapView'>('mapView');

  const treeDetectionStepTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const crownSegmentationStepTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cropfieldDelineationStepTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const treeAbortRef = useRef<AbortController | null>(null);
  const crownAbortRef = useRef<AbortController | null>(null);
  const cropfieldAbortRef = useRef<AbortController | null>(null);
  const forecastAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      forecastAbortRef.current?.abort();
      treeAbortRef.current?.abort();
      crownAbortRef.current?.abort();
      cropfieldAbortRef.current?.abort();
    };
  }, [expandedTab]);
  const UPLOAD_STEP_DURATION_LARGE_MS = 2200; // TIF / large files: show "uploading" longer
  const UPLOAD_STEP_DURATION_SMALL_MS = 500;  // JPG, PNG, small files: quick transition

  // Short upload step for small or lightweight image types (jpg, png); longer for TIF/large files
  const getUploadStepDurationMs = (file: File): number => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const isTif = ext === 'tif' || ext === 'tiff';
    const isLarge = file.size > 5 * 1024 * 1024; // 5MB
    return isTif || isLarge ? UPLOAD_STEP_DURATION_LARGE_MS : UPLOAD_STEP_DURATION_SMALL_MS;
  };

  const isTifFile = (file: File | null): boolean => {
    if (!file) return false;
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    return ext === 'tif' || ext === 'tiff';
  };

  useEffect(() => {
    return () => {
      if (treeDetectionStepTimeoutRef.current) clearTimeout(treeDetectionStepTimeoutRef.current);
      if (crownSegmentationStepTimeoutRef.current) clearTimeout(crownSegmentationStepTimeoutRef.current);
      if (cropfieldDelineationStepTimeoutRef.current) clearTimeout(cropfieldDelineationStepTimeoutRef.current);
    };
  }, []);

  const forecastMonths = buildLargeScaleRiceForecastMonthOptions(new Date());

  // Available regions for rice forecast
  const forecastRegions = [
    'All Regions',
    'BARMM',
    'CAR',
    'Region I',
    'Region II',
    'Region III',
    'Region IV-A',
    'Region IV-B',
    'Region V',
    'Region VI',
    'Region VII',
    'Region VIII',
    'Region IX',
    'Region X',
    'Region XI',
    'Region XII',
    'Region XIII',
    'NCR'
  ];

  useEffect(() => {
    let cancelled = false;
    if (!selectedRegion) {
      setForecastProvinceOptions([]);
      setForecastSelectedProvince('');
      setForecastSelectedMunicipality('');
      setForecastMunicipalityOptions([]);
      return;
    }
    setForecastSelectedProvince('');
    setForecastSelectedMunicipality('');
    setForecastMunicipalityOptions([]);
    setForecastProvincesLoading(true);
    const url =
      selectedRegion === 'All Regions'
        ? `${DIGISAKA_API_BASE}/rice-yield-forecast/provinces/`
        : `${DIGISAKA_API_BASE}/rice-yield-forecast/provinces/?region=${encodeURIComponent(selectedRegion)}`;
    void (async () => {
      try {
        const res = await fetch(url, digisakaAuthRequestInit({ headers: gptInferenceJsonHeaders() }));
        if (!res.ok) throw new Error(`provinces ${res.status}`);
        const j = await res.json();
        const list: string[] = Array.isArray(j.provinces) ? j.provinces : [];
        if (!cancelled) setForecastProvinceOptions(list);
      } catch (e) {
        console.error('Forecast provinces load failed', e);
        if (!cancelled) setForecastProvinceOptions([]);
      } finally {
        if (!cancelled) setForecastProvincesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRegion]);

  useEffect(() => {
    let cancelled = false;
    if (!forecastSelectedProvince) {
      setForecastMunicipalityOptions([]);
      setForecastSelectedMunicipality('');
      return;
    }
    setForecastSelectedMunicipality('');
    setForecastMunicipalitiesLoading(true);
    const prov = encodeURIComponent(forecastSelectedProvince);
    void (async () => {
      try {
        const res = await fetch(
          `${DIGISAKA_API_BASE}/municipalities/by-province/${prov}`,
          digisakaAuthRequestInit({ headers: gptInferenceJsonHeaders() })
        );
        if (!res.ok) throw new Error(`municipalities ${res.status}`);
        const j = await res.json();
        const list: string[] = Array.isArray(j.municipalities) ? j.municipalities : [];
        if (!cancelled) setForecastMunicipalityOptions(list.sort((a, b) => a.localeCompare(b)));
      } catch (e) {
        console.error('Forecast municipalities load failed', e);
        if (!cancelled) setForecastMunicipalityOptions([]);
      } finally {
        if (!cancelled) setForecastMunicipalitiesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [forecastSelectedProvince]);

  const layerConfigs = {
    multiTemporal: [
      {
        key: 'yield' as const,
        label: 'Past Yield',
        icon: 'ri-plant-line',
        color: 'text-green-500',
        description: 'Crop yield predictions',
        hasLegend: true
      },
      {
        key: 'bsri' as const,
        label: 'Black Sigatoka Risk',
        icon: 'ri-plant-fill',
        color: 'text-yellow-600',
        description: 'Black Sigatoka disease risk for banana plantations',
        hasLegend: true
      },
    ],
    // GeoServer drone NDVI WMS removed — use Isabela / Cavite Titiler COGs below
    droneData: [] as const,
    cropArea: [
      {
        key: 'maize' as const,
        label: 'Maize Area Map',
        icon: 'ri-seedling-line',
        color: 'text-yellow-600',
        description: ''
      },
      {
        key: 'sugarCane' as const,
        label: 'Sugarcane Area Map',
        icon: 'ri-plant-line',
        color: 'text-green-600',
        description: ''
      },
      {
        key: 'rice' as const,
        label: 'Rice Area Map',
        icon: 'ri-seedling-fill',
        color: 'text-green-700',
        description: ''
      },
      {
        key: 'coconut' as const,
        label: 'Coconut Area Map',
        icon: 'ri-plant-fill',
        color: 'text-amber-700',
        description: ''
      }
    ]
  };

  // Get currently selected layer for Multi-Temporal Maps (only one can be selected)
  const getSelectedMultiTemporalLayer = (): string | null => {
    for (const config of layerConfigs.multiTemporal) {
      if (layerVisibility[config.key]) {
        return config.key;
      }
    }
    return null;
  };

  const handleMultiTemporalLayerToggle = (layerKey: keyof LayerVisibilityState) => {
    const currentSelected = getSelectedMultiTemporalLayer();

    // If clicking the currently selected layer, deselect it
    if (currentSelected === layerKey) {
      onLayerToggle(layerKey, false);
      return;
    }

    // When switching to Past Yield (yield) from the Rice Yield Forecast panel,
    // always clear the rice forecast layer and forecast results so only one
    // forecast-related raster is visible on the map.
    if (layerKey === 'yield') {
      onLayerToggle('riceForecast', false);
      // Clear forecast data and results in parent (this also clears map layer)
      onForecastDataUpdate?.(null);
      onForecastResultsUpdate?.(null);
    }

    // For WMS layers (yield, vhi, lst), we need to ensure proper clearing
    // The key is to directly enable the new layer, which will trigger MapView's handleLayerToggle
    // to automatically clear other WMS layers and reset their state
    const isWMSLayer = ['yield', 'vhi', 'lst'].includes(layerKey);
    
    // Clear planting method layers before switching to any multi-temporal layer
    if (layerVisibility.plantingMethodDry2025) onLayerToggle('plantingMethodDry2025', false);
    if (layerVisibility.plantingMethodDry2024) onLayerToggle('plantingMethodDry2024', false);
    if (layerVisibility.riceGrowthStage) onLayerToggle('riceGrowthStage', false);
    if (layerVisibility.irrigationType) onLayerToggle('irrigationType', false);
    if (layerVisibility.cadiDrought) onLayerToggle('cadiDrought', false);
    if (layerVisibility.pestRisk) onLayerToggle('pestRisk', false);

    if (isWMSLayer) {
      // For WMS layers, directly enable the new layer
      // MapView's handleLayerToggle will automatically:
      // 1. Clear all other WMS layers (yield, vhi, lst) via onWMSLayerToggle
      // 2. Update layerVisibility state immediately for UI responsiveness
      // 3. The handleWMSLayerToggle in parent will use resetLayerState to properly clear previous layers
      onLayerToggle(layerKey, true);
    } else {
      // For non-WMS layers, clear all multi-temporal layers and enable immediately
      layerConfigs.multiTemporal.forEach(config => {
        if (config.key !== layerKey) {
          onLayerToggle(config.key, false);
        }
      });
      // Then turn on the new layer
      onLayerToggle(layerKey, true);
    }
  };

  // Clear all multi-temporal layers (including planting method)
  const clearAllMultiTemporalLayers = () => {
    layerConfigs.multiTemporal.forEach(config => {
      if (layerVisibility[config.key]) {
        onLayerToggle(config.key, false);
      }
    });
    if (layerVisibility.plantingMethodDry2025) onLayerToggle('plantingMethodDry2025', false);
    if (layerVisibility.plantingMethodDry2024) onLayerToggle('plantingMethodDry2024', false);
    if (layerVisibility.riceGrowthStage) onLayerToggle('riceGrowthStage', false);
    if (layerVisibility.irrigationType) onLayerToggle('irrigationType', false);
    if (layerVisibility.cadiDrought) onLayerToggle('cadiDrought', false);
    if (layerVisibility.pestRisk) onLayerToggle('pestRisk', false);
  };

  const handleCropAreaLayerToggle = (layerKey: keyof LayerVisibilityState) => {
    const isVisible = layerVisibility[layerKey];
    onLayerToggle(layerKey, !isVisible);
  };

  const getVisibleLayersCount = (tab: LayerTogglePanelTabId) => {
    // If we're on a conflicting tab, don't count layers from the other tab
    // This ensures badges update immediately when switching tabs, even if state hasn't synced yet
    if (tab === 'forecast') {
      return layerVisibility.riceForecast ? 1 : 0;
    }
    if (tab === 'multiTemporal') {
      if (expandedTab && expandedTab !== 'multiTemporal') {
        return 0;
      }
      let count = layerConfigs.multiTemporal.filter(
        (config) =>
          layerVisibility[config.key] &&
          (config.key !== 'yield' || SHOW_PAST_YIELD_LAYER) &&
          (config.key !== 'bsri' || SHOW_BSRI_LAYER)
      ).length;
      if (SHOW_AGRICULTURAL_DROUGHT_VHI_LAYER && layerVisibility.vhi) count++;
      if (layerVisibility.lst) count++;
      if (layerVisibility.plantingMethodDry2025) count++;
      if (layerVisibility.plantingMethodDry2024) count++;
      if (layerVisibility.riceGrowthStage) count++;
      if (layerVisibility.irrigationType) count++;
      if (layerVisibility.cadiDrought) count++;
      if (layerVisibility.pestRisk) count++;
      return count;
    }
    if (tab === 'droneData' || tab === 'fieldServices') {
      if (expandedTab && expandedTab !== tab) {
        return 0;
      }
      let count = layerConfigs.droneData.filter(config => layerVisibility[config.key]).length;
      if (layerVisibility.isabelaPhilriceNDVI) count++;
      if (layerVisibility.isabelaPhilriceOrtho) count++;
      if (layerVisibility.caviteSilangNDVI) count++;
      if (layerVisibility.caviteSilangOrtho) count++;
      if (droneSites?.activeLayerId) count++;
      return count;
    }
    if (tab === 'cropArea') {
      return layerConfigs.cropArea.filter(config => layerVisibility[config.key]).length;
    }
    if (tab === 'disasterLossDamage') {
      return 0; // Flood damage doesn't have toggleable layers
    }
    if (tab === 'lowCarbon') {
      return layerVisibility.strawBurn ? 1 : 0;
    }
    if (tab === 'treeDetection') {
      return layerVisibility.treeDetection ? 1 : 0;
    }
    return 0;
  };

  const renderMultiTemporalLayerItem = (config: typeof layerConfigs.multiTemporal[0]) => {
    if (!canShow(config.key)) return null;
    const isSelected = layerVisibility[config.key];
    const opacity = layerOpacity[config.key];

    return (
      <div key={config.key} className={`rounded-[10px] ${compactLayerPad} border transition-all duration-200 cursor-pointer ${
        isSelected ? lt.layerSelected : lt.layerIdle
      }`}
      onClick={() => handleMultiTemporalLayerToggle(config.key)}
      >
        {/* Layer Header */}
        <div className="flex items-center justify-between mb-0.5 sm:mb-1">
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            <div className="flex items-center space-x-1 sm:space-x-1.5">
              {/* Radio button style indicator */}
              <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full border flex items-center justify-center flex-shrink-0 ${
                isSelected ? lt.radioOn : (isDark ? 'border-white/20' : 'border-[#e5e7eb]')
              }`}>
                {isSelected && (
                  <div className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${lt.radioDot}`}></div>
                )}
              </div>
              <i className={`${config.icon} ${config.color} text-xs sm:text-sm`}></i>
              <span className={`text-sm font-medium ${
                isSelected ? lt.selectedText : lt.heading
              }`}>
                {config.label}
              </span>
            </div>
          </div>

          {/* Selection indicator */}
          <div className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full flex-shrink-0 ${isSelected ? lt.radioDot : (isDark ? 'bg-white/20' : 'bg-[#e5e7eb]')}`}></div>
        </div>

        {config.description ? (
        <p className={`text-sm mb-1 sm:mb-1.5 ${
          isSelected ? lt.selectedText : lt.muted
        }`}>
          {config.description}
        </p>
        ) : null}

        {/* Opacity Slider */}
        {isSelected && (
          <div className="space-y-0.5 sm:space-y-1" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <span className={`text-sm ${lt.selectedText}`}>Opacity</span>
              <span className={`text-sm font-medium ${lt.selectedText}`}>{Math.round(opacity * 100)}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={opacity}
              onChange={(e) => onOpacityChange(config.key, parseFloat(e.target.value))}
              {...opacitySliderProps(isDark, opacity)}
            />
          </div>
        )}




      </div>
    );
  };

  const renderCropAreaLayerItem = (config: typeof layerConfigs.cropArea[0]) => {
    if (!canShow(config.key)) return null;
    const isVisible = layerVisibility[config.key];
    const opacity = layerOpacity[config.key];
    const isLoading = loadingLayers.has(config.key);

    const handleToggle = () => {
      if (isLoading) return; // Prevent multiple clicks during loading

      // Just call the parent's toggle function - loading state is managed by MapView
      onLayerToggle(config.key, !isVisible);
    };

    return (
      <div key={config.key} className={`rounded-[10px] ${compactLayerPad} border transition-all duration-200 cursor-pointer ${
        isVisible ? lt.layerSelected : lt.layerIdle
      } ${isLoading ? 'opacity-75' : ''}`}>
        {/* Layer Header */}
        <div className="flex items-center justify-between mb-0.5 sm:mb-1">
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            <label className="flex items-center space-x-1 sm:space-x-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={isVisible}
                onChange={handleToggle}
                disabled={isLoading}
                className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded text-[#008540] focus:ring-[#008540] disabled:opacity-50 flex-shrink-0 ${isDark ? 'border-white/20 accent-[#34d399]' : 'border-[#e5e7eb] accent-[#008540]'}`}
              />
              {isLoading ? (
                <div className={`animate-spin h-2.5 w-2.5 sm:h-3 sm:w-3 border-2 rounded-full border-t-transparent ${isDark ? 'border-[#34d399]' : 'border-[#008540]'}`}></div>
              ) : (
                <i className={`${config.icon} ${config.color} text-xs sm:text-sm`}></i>
              )}
              <span className={`text-sm font-medium ${
                isVisible ? lt.selectedText : lt.heading
              } ${isLoading ? 'opacity-75' : ''}`}>
                {config.label}
                {isLoading && <span className={`text-sm ml-1 ${lt.selectedText}`}>(Loading...)</span>}
              </span>
            </label>
          </div>

          {/* Status indicator */}
          <div className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full flex-shrink-0 ${
            isLoading ? 'bg-yellow-500 animate-pulse' :
            isVisible ? lt.radioDot : (isDark ? 'bg-white/20' : 'bg-[#e5e7eb]')
          }`}></div>
        </div>

        {config.description ? (
        <p className={`text-sm mb-1 sm:mb-1.5 ${
          isVisible ? lt.selectedText : lt.muted
        }`}>
          {config.description}
        </p>
        ) : null}

        {/* Loading Progress */}
        {isLoading && (
          <div className="mb-1 sm:mb-1.5">
            <div className={`flex items-center justify-between text-sm ${lt.selectedText} mb-0.5`}>
              <span>Fetching crop area data...</span>
            </div>
            <div className={`w-full rounded-full h-0.5 sm:h-1 ${isDark ? 'bg-[#253347]' : 'bg-[#e5e7eb]'}`}>
              <div className={`h-0.5 sm:h-1 rounded-full animate-pulse ${isDark ? 'bg-[#008540]' : 'bg-[#008540]'}`} style={{ width: '60%' }}></div>
            </div>
          </div>
        )}

        {/* Opacity Slider */}
        {isVisible && !isLoading && (
          <div className="space-y-0.5 sm:space-y-1">
            <div className="flex items-center justify-between">
              <span className={`text-sm ${lt.selectedText}`}>Opacity</span>
              <span className={`text-sm font-medium ${lt.selectedText}`}>{Math.round(opacity * 100)}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={opacity}
              onChange={(e) => onOpacityChange(config.key, parseFloat(e.target.value))}
              className="w-full h-1 sm:h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer opacity-slider"
            />
          </div>
        )}

      </div>
    );
  };

  // Handle rice forecast
  const handleRiceForecastSubmit = async () => {
    if (!selectedMonth || !selectedRegion) {
      setForecastError('Please select both month and region');
      return;
    }

    forecastAbortRef.current?.abort();
    const abortController = new AbortController();
    forecastAbortRef.current = abortController;

    setIsForecastLoading(true);
    setForecastError('');
    setForecastResults(null);
    // Clear forecast results in parent
    if (onForecastResultsUpdate) {
      onForecastResultsUpdate(null);
    }

    try {
      const response = await fetch(
        `${DIGISAKA_API_BASE}/rice-yield-forecast/`,
        digisakaAuthRequestInit({
          method: 'POST',
          headers: {
            ...gptInferenceJsonHeaders(),
          },
          body: JSON.stringify({
            region_name: selectedRegion,
            month: selectedMonth,
            ...(forecastSelectedProvince
              ? { province_name: forecastSelectedProvince }
              : {}),
            ...(forecastSelectedProvince && forecastSelectedMunicipality
              ? { municipality_name: forecastSelectedMunicipality }
              : {}),
          }),
          signal: abortController.signal,
        })
      );

      if (abortController.signal.aborted) return;

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      notifyInferenceComputeUsage(response);

      const data = await response.json();
      if (abortController.signal.aborted) return;

      if (data.success) {
        // Store forecast results
        setForecastResults(data);

        // Pass forecast data to parent for map visualization (includes centroid info for zooming)
        if (onForecastDataUpdate) {
          onForecastDataUpdate(data);
        }

        // Pass forecast results to parent for display below map
        if (onForecastResultsUpdate) {
          onForecastResultsUpdate(data);
        }

        // Before enabling rice forecast, clear any active Past Yield (yield) layer
        onLayerToggle('yield', false);

        // Enable rice forecast layer (only this forecast raster should stay visible)
        onLayerToggle('riceForecast', true);

        // The forecast data will be visualized through the results panel below
      } else {
        setForecastError('Failed to generate forecast. Please try again.');
      }

    } catch (error) {
      if (abortController.signal.aborted || isAnalysisAbortError(error)) return;
      console.error('Error fetching rice forecast:', error);
      setForecastError('Failed to fetch forecast data. Please try again.');
    } finally {
      if (!abortController.signal.aborted) setIsForecastLoading(false);
    }
  };

  /** GeoTIFF export: specific region, or “All Regions” with a province (server-side AOI cap). */
  const handleDownloadForecastTif = async () => {
    if (!selectedMonth || !selectedRegion) {
      setForecastError('Please select both month and region');
      return;
    }
    if (selectedRegion === 'All Regions' && !forecastSelectedProvince) {
      setForecastError('GeoTIFF download requires a region, or “All Regions” with a province selected.');
      return;
    }

    setIsForecastTifDownloading(true);
    setForecastError('');
    try {
      const response = await fetch(
        `${DIGISAKA_API_BASE}/rice-yield-forecast/geotiff/`,
        digisakaAuthRequestInit({
          method: 'POST',
          headers: { ...gptInferenceJsonHeaders() },
          body: JSON.stringify({
            region_name: selectedRegion,
            month: selectedMonth,
            scale: 1000,
            ...(forecastSelectedProvince
              ? { province_name: forecastSelectedProvince }
              : {}),
            ...(forecastSelectedProvince && forecastSelectedMunicipality
              ? { municipality_name: forecastSelectedMunicipality }
              : {}),
          }),
        })
      );

      if (!response.ok) {
        let msg = `Download failed (${response.status})`;
        const ct = response.headers.get('content-type');
        try {
          if (ct && ct.includes('application/json')) {
            const err = await response.json();
            if (err?.detail) {
              msg = typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail);
            }
          } else {
            const text = await response.text();
            if (text) msg = text.slice(0, 400);
          }
        } catch {
          /* ignore parse errors */
        }
        throw new Error(msg);
      }

      notifyInferenceComputeUsage(response);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeRegion = selectedRegion.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_');
      const prov = forecastSelectedProvince
        ? `_${forecastSelectedProvince.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_')}`
        : '';
      const muni = forecastSelectedMunicipality
        ? `_${forecastSelectedMunicipality.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_')}`
        : '';
      a.download = `rice_yield_forecast_${safeRegion}${prov}${muni}_${selectedMonth}.tif`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: unknown) {
      console.error('Error downloading forecast GeoTIFF:', error);
      setForecastError(
        error instanceof Error ? error.message : 'Failed to download GeoTIFF. Please try again.'
      );
    } finally {
      setIsForecastTifDownloading(false);
    }
  };

  const triggerClientDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const forecastExportBaseFilename = () => {
    const scope =
      (forecastResults as { scope_label?: string } | null)?.scope_label ||
      `${selectedRegion}${forecastSelectedProvince ? `_${forecastSelectedProvince}` : ''}${forecastSelectedMunicipality ? `_${forecastSelectedMunicipality}` : ''}`;
    return scope.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_').slice(0, 120) || 'rice_forecast';
  };

  const handleExportForecastJson = () => {
    if (!forecastResults) return;
    const blob = new Blob([JSON.stringify(forecastResults, null, 2)], {
      type: 'application/json;charset=utf-8',
    });
    triggerClientDownload(blob, `${forecastExportBaseFilename()}_${selectedMonth}.json`);
  };

  const handleExportForecastCsv = () => {
    if (!forecastResults) return;
    const csv = buildRiceForecastCsv(forecastResults as Record<string, unknown>);
    if (!csv) return;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    triggerClientDownload(blob, `${forecastExportBaseFilename()}_${selectedMonth}.csv`);
  };

  // Handle tree detection
  const handleTreeDetectionSubmit = async () => {
    if (treeDetectionInputMode === 'file' && !treeDetectionImage) {
      setTreeDetectionError('Please select an image file first.');
      return;
    }
    if (treeDetectionInputMode === 'mapView' && !getOlMap?.()) {
      setTreeDetectionError('Map is not ready. Use the analysis panel with the map visible, or choose file upload.');
      return;
    }
    if (treeDetectionInputMode === 'mapView') {
      const zoomCheck = assertFarmLevelMapZoom(getOlMapZoom(getOlMap?.() ?? null) ?? mapZoom);
      if (!zoomCheck.ok) {
        setTreeDetectionError(
          `Zoom in to farm level (zoom ${FARM_LEVEL_MIN_ZOOM}+) to draw fields or run AI delineation on the map view.`,
        );
        return;
      }
    }
    if (isCrownSegmentationLoading || isCropfieldDelineationLoading) {
      setTreeDetectionError('Another analysis is still running. Wait for it to finish, then try again.');
      return;
    }

    const abortController = new AbortController();
    treeAbortRef.current = abortController;

    setIsTreeDetectionLoading(true);
    setTreeDetectionStep('uploading');
    setTreeDetectionError('');
    setTreeDetectionResults(null);

    const uploadStepMs =
      treeDetectionInputMode === 'mapView'
        ? 1200
        : getUploadStepDurationMs(treeDetectionImage!);
    if (treeDetectionStepTimeoutRef.current) clearTimeout(treeDetectionStepTimeoutRef.current);
    treeDetectionStepTimeoutRef.current = setTimeout(() => {
      setTreeDetectionStep('detecting');
      treeDetectionStepTimeoutRef.current = null;
    }, uploadStepMs);

    try {
      let data: { success?: boolean };

      if (treeDetectionInputMode === 'mapView') {
        data = (await treeDetectFromViewport(getOlMap!(), {
          tile_size: treeDetectionParams.tile_size,
          overlap: treeDetectionParams.overlap,
          resolution: treeDetectionParams.resolution,
          conf_threshold: treeDetectionParams.conf_threshold,
        }, abortController.signal)) as { success?: boolean };
      } else {
        const formData = new FormData();
        formData.append('image', treeDetectionImage!);
        formData.append('tile_size', treeDetectionParams.tile_size.toString());
        formData.append('overlap', treeDetectionParams.overlap.toString());
        formData.append('resolution', treeDetectionParams.resolution.toString());
        formData.append('conf_threshold', treeDetectionParams.conf_threshold.toString());

        const response = await fetch(
          DIGISAKA_API_TREEDETECTION,
          digisakaAuthRequestInit({
            method: 'POST',
            headers: gptInferenceMultipartAuthHeaders(),
            body: formData,
            signal: abortController.signal,
          })
        );

        notifyInferenceComputeUsage(response);

        if (!response.ok) {
          let msg = `API error: ${response.status}`;
          try {
            const errBody = await response.json();
            if (errBody?.detail) {
              msg = typeof errBody.detail === 'string' ? errBody.detail : JSON.stringify(errBody.detail);
            }
          } catch {
            /* ignore */
          }
          throw new Error(msg);
        }

        data = await response.json();
      }

      if (data.success) {
        console.log('[Tree Detection] API response geojson:', (data as any).geojson);
        console.log('[Tree Detection] geojson features count:', (data as any).geojson?.features?.length ?? 0);
        setTreeDetectionResults(data);
        onTreeDetectionDataUpdate?.(data);
      } else {
        setTreeDetectionError('Failed to process tree detection. Please try again.');
      }
    } catch (error: unknown) {
      if (abortController.signal.aborted || isAnalysisAbortError(error)) {
        return;
      }
      console.error('Error fetching tree detection:', error);
      setTreeDetectionError(
        error instanceof Error ? error.message : 'Failed to process tree detection. Please try again.'
      );
    } finally {
      if (treeAbortRef.current === abortController) treeAbortRef.current = null;
      if (treeDetectionStepTimeoutRef.current) {
        clearTimeout(treeDetectionStepTimeoutRef.current);
        treeDetectionStepTimeoutRef.current = null;
      }
      setIsTreeDetectionLoading(false);
      setTreeDetectionStep('idle');
    }
  };

  const cancelTreeDetection = () => {
    treeAbortRef.current?.abort();
  };

  // Handle crown segmentation
  const handleCrownSegmentationSubmit = async () => {
    if (crownSegmentationInputMode === 'file' && !crownSegmentationImage) {
      setCrownSegmentationError('Please select an image file first.');
      return;
    }
    if (crownSegmentationInputMode === 'mapView' && !getOlMap?.()) {
      setCrownSegmentationError('Map is not ready. Use the analysis panel with the map visible, or choose file upload.');
      return;
    }
    if (crownSegmentationInputMode === 'mapView') {
      const zoomCheck = assertFarmLevelMapZoom(getOlMapZoom(getOlMap?.() ?? null) ?? mapZoom);
      if (!zoomCheck.ok) {
        setCrownSegmentationError(
          `Zoom in to farm level (zoom ${FARM_LEVEL_MIN_ZOOM}+) to draw fields or run AI delineation on the map view.`,
        );
        return;
      }
    }
    if (isTreeDetectionLoading || isCropfieldDelineationLoading) {
      setCrownSegmentationError('Another analysis is still running. Wait for it to finish, then try again.');
      return;
    }

    const abortController = new AbortController();
    crownAbortRef.current = abortController;

    setIsCrownSegmentationLoading(true);
    setCrownSegmentationStep('uploading');
    setCrownSegmentationError('');
    setCrownSegmentationResults(null);

    const uploadStepMs =
      crownSegmentationInputMode === 'mapView'
        ? 1200
        : getUploadStepDurationMs(crownSegmentationImage!);
    if (crownSegmentationStepTimeoutRef.current) clearTimeout(crownSegmentationStepTimeoutRef.current);
    crownSegmentationStepTimeoutRef.current = setTimeout(() => {
      setCrownSegmentationStep('segmenting');
      crownSegmentationStepTimeoutRef.current = null;
    }, uploadStepMs);

    try {
      let data: { success?: boolean };

      if (crownSegmentationInputMode === 'mapView') {
        data = (await crownSegmentationFromViewport(getOlMap!(), {
          tile_size: crownSegmentationParams.tile_size,
          overlap: crownSegmentationParams.overlap,
          resolution: crownSegmentationParams.resolution,
          conf_threshold: crownSegmentationParams.conf_threshold,
          sam_model_type: crownSegmentationParams.sam_model_type,
          device: crownSegmentationParams.device,
        }, abortController.signal)) as { success?: boolean };
      } else {
        const formData = new FormData();
        formData.append('image', crownSegmentationImage!);
        formData.append('tile_size', crownSegmentationParams.tile_size.toString());
        formData.append('overlap', crownSegmentationParams.overlap.toString());
        formData.append('resolution', crownSegmentationParams.resolution.toString());
        formData.append('conf_threshold', crownSegmentationParams.conf_threshold.toString());
        formData.append('sam_model_type', crownSegmentationParams.sam_model_type);
        formData.append('device', crownSegmentationParams.device);

        const response = await fetch(
          DIGISAKA_API_CROWN_SEGMENTATION,
          digisakaAuthRequestInit({
            method: 'POST',
            headers: gptInferenceMultipartAuthHeaders(),
            body: formData,
            signal: abortController.signal,
          })
        );

        notifyInferenceComputeUsage(response);

        if (!response.ok) {
          let msg = `API error: ${response.status}`;
          try {
            const errBody = await response.json();
            if (errBody?.detail) {
              msg = typeof errBody.detail === 'string' ? errBody.detail : JSON.stringify(errBody.detail);
            }
          } catch {
            /* ignore */
          }
          throw new Error(msg);
        }

        data = await response.json();
      }

      if (data.success) {
        setCrownSegmentationResults(data);
        onCrownSegmentationResultsUpdate?.(data);
      } else {
        setCrownSegmentationError('Failed to process crown segmentation. Please try again.');
      }
    } catch (error: unknown) {
      if (abortController.signal.aborted || isAnalysisAbortError(error)) {
        return;
      }
      console.error('Error fetching crown segmentation:', error);
      setCrownSegmentationError(
        error instanceof Error ? error.message : 'Failed to process crown segmentation. Please try again.'
      );
    } finally {
      if (crownAbortRef.current === abortController) crownAbortRef.current = null;
      if (crownSegmentationStepTimeoutRef.current) {
        clearTimeout(crownSegmentationStepTimeoutRef.current);
        crownSegmentationStepTimeoutRef.current = null;
      }
      setIsCrownSegmentationLoading(false);
      setCrownSegmentationStep('idle');
    }
  };

  const cancelCrownSegmentation = () => {
    crownAbortRef.current?.abort();
  };

  // Crop-field boundary delineation (file upload vs map viewport)
  const handleCropfieldDelineationSubmit = async () => {
    if (cropfieldDelineationInputMode === 'file' && !cropfieldDelineationImage) {
      setCropfieldDelineationError('Please select a GeoTIFF file.');
      return;
    }
    if (cropfieldDelineationInputMode === 'mapView' && !getOlMap?.()) {
      setCropfieldDelineationError('Map is not ready. Use the analysis panel with the map visible, or choose file upload.');
      return;
    }
    if (cropfieldDelineationInputMode === 'mapView') {
      const zoomCheck = assertFarmLevelMapZoom(getOlMapZoom(getOlMap?.() ?? null) ?? mapZoom);
      if (!zoomCheck.ok) {
        setCropfieldDelineationError(zoomCheck.message);
        return;
      }
    }
    if (isTreeDetectionLoading || isCrownSegmentationLoading) {
      setCropfieldDelineationError('Another analysis is still running. Wait for it to finish, then try again.');
      return;
    }

    const abortController = new AbortController();
    cropfieldAbortRef.current = abortController;

    setIsCropfieldDelineationLoading(true);
    setCropfieldDelineationStep('uploading');
    setCropfieldDelineationError('');
    setCropfieldDelineationResults(null);

    const uploadStepMs =
      cropfieldDelineationInputMode === 'mapView'
        ? 1200
        : getUploadStepDurationMs(cropfieldDelineationImage!);
    if (cropfieldDelineationStepTimeoutRef.current) clearTimeout(cropfieldDelineationStepTimeoutRef.current);
    cropfieldDelineationStepTimeoutRef.current = setTimeout(() => {
      setCropfieldDelineationStep('delineating');
      cropfieldDelineationStepTimeoutRef.current = null;
    }, uploadStepMs);

    try {
      let data: { success?: boolean };

      if (cropfieldDelineationInputMode === 'mapView') {
        data = (await cropfieldDelineateFromViewport(getOlMap!(), {
          ...cropfieldDelineationParams,
          model: cropfieldDelineationModel,
        }, abortController.signal)) as { success?: boolean };
      } else {
        const formData = new FormData();
        formData.append('image', cropfieldDelineationImage!);
        formData.append('include_preview', 'false');
        formData.append('tta', 'false');
        formData.append('smooth_epsilon', cropfieldDelineationParams.smooth_epsilon.toString());
        formData.append(
          'max_polygon_vertices',
          cropfieldDelineationParams.max_polygon_vertices.toString()
        );
        formData.append('model', cropfieldDelineationModel);
        formData.append('tile_size', cropfieldDelineationParams.tile_size.toString());
        formData.append('overlap', cropfieldDelineationParams.overlap.toString());
        formData.append('resolution', cropfieldDelineationParams.resolution.toString());
        formData.append('conf_threshold', cropfieldDelineationParams.conf_threshold.toString());

        const response = await fetch(
          DIGISAKA_API_FIELDDELINEATION,
          digisakaAuthRequestInit({
            method: 'POST',
            headers: gptInferenceMultipartAuthHeaders(),
            body: formData,
            signal: abortController.signal,
          })
        );

        notifyInferenceComputeUsage(response);

        if (!response.ok) {
          let msg = `API error: ${response.status}`;
          try {
            const errBody = await response.json();
            if (errBody?.detail) {
              msg = typeof errBody.detail === 'string' ? errBody.detail : JSON.stringify(errBody.detail);
            }
          } catch {
            /* ignore */
          }
          throw new Error(msg);
        }

        data = await response.json();
      }

      if (data.success) {
        setCropfieldDelineationResults(data);
        onCropfieldDelineationUpdate?.(data);
      } else {
        setCropfieldDelineationError('Delineation failed. Please try again.');
      }
    } catch (error: unknown) {
      if (abortController.signal.aborted || isAnalysisAbortError(error)) {
        return;
      }
      console.error('Crop-field delineation:', error);
      setCropfieldDelineationError(
        error instanceof Error ? error.message : 'Failed to run boundary delineation.'
      );
    } finally {
      if (cropfieldAbortRef.current === abortController) cropfieldAbortRef.current = null;
      if (cropfieldDelineationStepTimeoutRef.current) {
        clearTimeout(cropfieldDelineationStepTimeoutRef.current);
        cropfieldDelineationStepTimeoutRef.current = null;
      }
      setIsCropfieldDelineationLoading(false);
      setCropfieldDelineationStep('idle');
    }
  };

  const cancelCropfieldDelineation = () => {
    cropfieldAbortRef.current?.abort();
  };

  // Fetch provinces for straw burn
  useEffect(() => {
    const fetchProvinces = async () => {
      setIsLoadingProvinces(true);
      try {
        const response = await fetch(
          DIGISAKA_API_ORIGIN + '/api/provinces/',
          digisakaAuthRequestInit({ headers: { ...digisakaMeteredAuthHeaders() } })
        );
        if (!response.ok) throw new Error('Failed to fetch provinces');
        const data = await response.json();
        // Handle different response formats
        if (Array.isArray(data)) {
          setProvinces(data);
        } else if (data.provinces && Array.isArray(data.provinces)) {
          setProvinces(data.provinces);
        } else if (data.success && data.data && Array.isArray(data.data)) {
          setProvinces(data.data);
        } else {
          console.error('Unexpected provinces data format:', data);
          setStrawBurnError('Unexpected data format from provinces API');
          setProvinces([]);
        }
      } catch (error) {
        console.error('Error fetching provinces:', error);
        setStrawBurnError('Failed to load provinces');
        setProvinces([]);
      } finally {
        setIsLoadingProvinces(false);
      }
    };

    if (expandedTab === 'lowCarbon' && provinces.length === 0) {
      fetchProvinces();
    }
    if (expandedTab === 'multiTemporal' && provinces.length === 0) {
      fetchProvinces();
    }
  }, [expandedTab, provinces.length]);

  // Pass straw burn results to parent for display below map.
  // Use a ref for the callback so we only re-run when `strawBurnData` changes — not when parent
  // `layerControls` is recreated (e.g. on every opacity slider move), which would re-trigger
  // Digisaka GPT "Analyse?" offers.
  const onStrawBurnResultsUpdateRef = useRef(onStrawBurnResultsUpdate);
  onStrawBurnResultsUpdateRef.current = onStrawBurnResultsUpdate;
  useEffect(() => {
    if (strawBurnData && strawBurnData.success) {
      onStrawBurnResultsUpdateRef.current?.(strawBurnData);
    }
  }, [strawBurnData]);

  // Fetch municipalities only when "Municipality" analysis mode is active (not needed for province-wide)
  useEffect(() => {
    if (strawBurnScope !== 'municipality') {
      setMunicipalities([]);
      setIsLoadingMunicipalities(false);
      return;
    }

    const fetchMunicipalities = async () => {
      if (!selectedProvince) {
        setMunicipalities([]);
        return;
      }

      setIsLoadingMunicipalities(true);
      setStrawBurnError('');
      try {
        const response = await fetch(
          `${DIGISAKA_API_ORIGIN}/api/municipalities/by-province/${encodeURIComponent(selectedProvince)}`,
          digisakaAuthRequestInit({ headers: { ...digisakaMeteredAuthHeaders() } })
        );
        if (!response.ok) throw new Error('Failed to fetch municipalities');
        const data = await response.json();
        // Handle different response formats
        if (Array.isArray(data)) {
          setMunicipalities(data);
        } else if (data.municipalities && Array.isArray(data.municipalities)) {
          setMunicipalities(data.municipalities);
        } else if (data.success && data.data && Array.isArray(data.data)) {
          setMunicipalities(data.data);
        } else {
          console.error('Unexpected municipalities data format:', data);
          setStrawBurnError('Unexpected data format from municipalities API');
          setMunicipalities([]);
        }
      } catch (error) {
        console.error('Error fetching municipalities:', error);
        setStrawBurnError('Failed to load municipalities');
        setMunicipalities([]);
      } finally {
        setIsLoadingMunicipalities(false);
      }
    };

    fetchMunicipalities();
  }, [selectedProvince, strawBurnScope]);

  const tabNames: Record<LayerTogglePanelTabId, string> = {
    forecast: 'Rice Yield Forecast',
    multiTemporal: 'Multi-Temporal Maps',
    droneData: 'Drone',
    fieldServices: 'Field services',
    cropArea: 'Crop Type Maps',
    disasterLossDamage: 'Disaster Loss & Damages',
    lowCarbon: 'Low Carbon Rice',
    treeDetection: 'AI Detection: Trees, Crowns & Fields'
  };

  useEffect(() => {
    if (allowedEntitlementSet == null) return;
    if (expandedTab && !isPanelTabUnlocked(allowedEntitlementSet, expandedTab)) {
      const order = [...LAYER_TOGGLE_PANEL_TAB_ORDER];
      const next = order.find((t) => isPanelTabUnlocked(allowedEntitlementSet, t)) ?? null;
      setExpandedTab(next);
    }
  }, [allowedEntitlementSet, expandedTab]);

  // Helper function to check if map is currently zoomed to a region
  const isZoomedToRegion = (region: 'isabela' | 'laguna' | 'nuevaEcija' | 'albay' | 'bataan' | 'batangas' | 'bulacan' | 'cagayan' | 'ilocosNorte' | 'camarinesSur' | 'cavite' | 'ilocosSur' | 'nuevaVizcaya' | 'pampanga' | 'pangasinan' | 'quezon' | 'quirino' | 'rizal' | 'sorsogon' | 'tarlac' | 'zambales'): boolean => {
    if (!mapCenter || !mapZoom) return false;

    const regionBounds: Record<string, { bbox: [number, number, number, number]; minZoom: number }> = {
      isabela: {
        bbox: [121.33059452348952, 16.41096259946589, 122.51457406795903, 17.548229749161205],
        minZoom: 9
      },
      laguna: {
        bbox: [121.00531455910985, 13.985331669286357, 121.62030120261807, 14.57588413706653],
        minZoom: 9
      },
      nuevaEcija: {
        bbox: [120.61499656815991, 15.19670982992153, 121.36796443930888, 16.138323910735615],
        minZoom: 9
      },
      albay: {
        bbox: [123.27544711360828, 12.994579742430936, 124.21238995494492, 13.5234179501921],
        minZoom: 9
      },
      bataan: {
        bbox: [120.24812460612549, 14.75896079197009, 120.44674211544431, 14.92281349979349],
        minZoom: 9
      },
      batangas: {
        bbox: [120.48698664017287, 13.547043642164443, 121.46992322405644, 14.240004052334243],
        minZoom: 9
      },
      bulacan: {
        bbox: [120.68659229630424, 14.709643282871927, 121.34757268235937, 15.272258145315984],
        minZoom: 9
      },
      cagayan: {
        bbox: [120.96102761560275, 17.529454959723104, 122.32898213025993, 19.96613516789731],
        minZoom: 9
      },
      ilocosNorte: {
        bbox: [120.41054000949431, 17.7248385340191, 120.95653603918215, 18.65145074958839],
        minZoom: 9
      },
      camarinesSur: {
        bbox: [122.54817105958512, 13.28329827474695, 123.98511618806269, 14.128792620160246],
        minZoom: 9
      },
      cavite: {
        bbox: [120.61275077994961, 14.063485099004758, 121.04969133414534, 14.500335821672081],
        minZoom: 9
      },
      ilocosSur: {
        bbox: [120.34397484694105, 16.68054701623016, 120.84990601495717, 17.904321927786185],
        minZoom: 9
      },
      nuevaVizcaya: {
        bbox: [120.75899650820428, 15.791664042593892, 121.46597063680633, 16.748998640880068],
        minZoom: 9
      },
      pampanga: {
        bbox: [120.35601227174826, 14.77279484734553, 120.94800204398301, 15.278726015361645],
        minZoom: 9
      },
      pangasinan: {
        bbox: [119.74964945496758, 15.639040275821985, 120.77857978139807, 16.443930770393077],
        minZoom: 9
      },
      quezon: {
        bbox: [121.23734939699791, 10.445699955270202, 125.220299703727, 15.216472766172162],
        minZoom: 9
      },
      quirino: {
        bbox: [121.2936737653122, 15.937730107791726, 122.11563225028156, 16.64156013289937],
        minZoom: 9
      },
      rizal: {
        bbox: [121.09334945695356, 14.311779443535393, 121.46129939732892, 14.892270780133428],
        minZoom: 9
      },
      sorsogon: {
        bbox: [123.48035282991594, 12.544433953558645, 124.187326958518, 13.118187925525785],
        minZoom: 9
      },
      tarlac: {
        bbox: [120.16512027387286, 15.18790634013716, 120.75711004610761, 15.862451286982509],
        minZoom: 9
      },
      zambales: {
        bbox: [119.78576172938918, 14.758960791970088, 120.44674211544431, 15.874219217204475],
        minZoom: 9
      }
    };

    const bounds = regionBounds[region];
    if (!bounds) return false;
    const [minLon, minLat, maxLon, maxLat] = bounds.bbox;
    const [centerLon, centerLat] = mapCenter;

    // Check if map center is within the bbox and zoom level is sufficient
    const isWithinBounds =
      centerLon >= minLon &&
      centerLon <= maxLon &&
      centerLat >= minLat &&
      centerLat <= maxLat &&
      mapZoom >= bounds.minZoom;

    return isWithinBounds;
  };

  // Get all active legends for display - only show legends for the current tab
  const getActiveLegends = (): Array<{
    type: 'multiTemporal' | 'cropArea' | 'forecast' | 'bsri' | 'strawBurn' | 'droneData' | 'plantingMethod' | 'riceGrowthStage' | 'irrigationType';
    key: string;
    label: string;
    url?: string;
    data?: { colors: string[]; labels: string[]; description: string; totalArea?: number };
  }> => {
    const legends = [];

    // Only show legends for the current expanded tab
    if (!expandedTab) {
      return legends; // No tab expanded, no legends
    }

    // Multi-temporal layers (yield, vhi, lst, bsri, planting method, growth stage, irrigation):
    // legends are shown ON THE MAP only (MapView) — not here, to avoid duplicates.

    // Crop type legends are drawn on the map (MapView), same place as CADI.

    // Rice Yield Forecast legend is now shown only on the map (MapView) to avoid duplicates

    // BSRI legend is shown on the map only (MapView) - not in panel, to avoid duplicate legends.

    // Check Straw Burn Detection layer (only if lowCarbon tab is active)
    if (expandedTab === 'lowCarbon') {
      if (layerVisibility.strawBurn && cropAreaLegends.strawBurn) {
        legends.push({
          type: 'strawBurn' as const,
          key: 'strawBurn',
          label: 'Straw Burn Detection',
          data: cropAreaLegends.strawBurn
        });
      }
    }

    return legends;
  };

  const activeLegends = getActiveLegends();

  const tabIcons: Record<LayerTogglePanelTabId, string> = {
    forecast: 'ri-bar-chart-box-fill',
    multiTemporal: 'ri-line-chart-fill',
    droneData: 'ri-flight-takeoff-fill',
    fieldServices: 'ri-base-station-line',
    cropArea: 'ri-plant-fill',
    disasterLossDamage: 'ri-flood-fill',
    lowCarbon: 'ri-leaf-fill',
    treeDetection: 'ri-tree-line'
  };

  // Helper to check if current tab has loaded data
  const hasLoadedData = (tab: LayerTogglePanelTabId): boolean => {
    switch(tab) {
      case 'forecast':
        // Treat only large-scale forecasts as "loaded" for this tab.
        // Farm-level forecasts are marked with scope === 'farm' in the legend data.
        {
          const riceLegend = cropAreaLegends?.riceForecast as (typeof cropAreaLegends[keyof typeof cropAreaLegends] & { scope?: string }) | undefined;
          const hasLargeScaleLayer =
            layerVisibility.riceForecast &&
            riceLegend &&
            riceLegend.scope !== 'farm';
          return hasLargeScaleLayer || forecastResults !== null;
        }
      case 'multiTemporal':
        return layerConfigs.multiTemporal.some(
          (config) =>
            layerVisibility[config.key] &&
            (config.key !== 'yield' || SHOW_PAST_YIELD_LAYER) &&
            (config.key !== 'bsri' || SHOW_BSRI_LAYER)
        )
          || (SHOW_AGRICULTURAL_DROUGHT_VHI_LAYER && layerVisibility.vhi)
          || layerVisibility.lst
          || layerVisibility.plantingMethodDry2025
          || layerVisibility.plantingMethodDry2024
          || layerVisibility.cadiDrought
          || layerVisibility.pestRisk;
      case 'droneData':
        return (
          layerConfigs.droneData.some(config => layerVisibility[config.key]) ||
          layerVisibility.isabelaPhilriceNDVI ||
          layerVisibility.isabelaPhilriceOrtho ||
          layerVisibility.caviteSilangNDVI ||
          layerVisibility.caviteSilangOrtho ||
          !!droneSites?.activeLayerId
        );
      case 'fieldServices':
        return (
          layerConfigs.droneData.some(config => layerVisibility[config.key]) ||
          layerVisibility.isabelaPhilriceNDVI ||
          layerVisibility.isabelaPhilriceOrtho ||
          layerVisibility.caviteSilangNDVI ||
          layerVisibility.caviteSilangOrtho ||
          !!droneSites?.activeLayerId
        );
      case 'cropArea':
        return layerConfigs.cropArea.some(config => layerVisibility[config.key]);
      case 'disasterLossDamage':
        return false; // Flood damage doesn't have persistent layer data
      case 'lowCarbon':
        return layerVisibility.strawBurn || strawBurnData !== null;
      case 'treeDetection':
        return (
          layerVisibility.treeDetection ||
          treeDetectionResults !== null ||
          crownSegmentationResults !== null ||
          cropfieldDelineationResults !== null
        );
      default:
        return false;
    }
  };

  // Clear data for a specific tab
  const clearTabData = (tab: LayerTogglePanelTabId) => {
    switch(tab) {
      case 'forecast':
        if (layerVisibility.riceForecast) {
          onLayerToggle('riceForecast', false);
        }
        setForecastResults(null);
    // Clear forecast results in parent
    if (onForecastResultsUpdate) {
      onForecastResultsUpdate(null);
    }
        setSelectedMonth('');
        setSelectedRegion('');
        break;
      case 'multiTemporal':
        // Always clear all multiTemporal layers, regardless of current visibility state
        layerConfigs.multiTemporal.forEach(config => {
          onLayerToggle(config.key, false);
        });
        // Clear WMS layers (vhi, lst) that aren't in layerConfigs
        if (layerVisibility.vhi) onLayerToggle('vhi', false);
        if (layerVisibility.lst) onLayerToggle('lst', false);
        // Clear planting method layers
        if (layerVisibility.plantingMethodDry2025) onLayerToggle('plantingMethodDry2025', false);
        if (layerVisibility.plantingMethodDry2024) onLayerToggle('plantingMethodDry2024', false);
        if (layerVisibility.cadiDrought) onLayerToggle('cadiDrought', false);
        if (layerVisibility.pestRisk) onLayerToggle('pestRisk', false);
        if (layerVisibility.riceGrowthStage) onLayerToggle('riceGrowthStage', false);
        if (layerVisibility.irrigationType) onLayerToggle('irrigationType', false);
            // Also clear any API-based multi-temporal tiles (LST/VHI Philippines)
        if (clearMultiTemporalAPITiles) {
          clearMultiTemporalAPITiles();
        }
        break;
      case 'droneData':
        layerConfigs.droneData.forEach(config => {
          if (layerVisibility[config.key]) {
            onLayerToggle(config.key, false);
          }
        });
        if (layerVisibility.isabelaPhilriceNDVI) onLayerToggle('isabelaPhilriceNDVI', false);
        if (layerVisibility.isabelaPhilriceOrtho) onLayerToggle('isabelaPhilriceOrtho', false);
        if (layerVisibility.caviteSilangNDVI) onLayerToggle('caviteSilangNDVI', false);
        if (layerVisibility.caviteSilangOrtho) onLayerToggle('caviteSilangOrtho', false);
        break;
      case 'fieldServices':
        layerConfigs.droneData.forEach(config => {
          if (layerVisibility[config.key]) {
            onLayerToggle(config.key, false);
          }
        });
        if (layerVisibility.isabelaPhilriceNDVI) onLayerToggle('isabelaPhilriceNDVI', false);
        if (layerVisibility.isabelaPhilriceOrtho) onLayerToggle('isabelaPhilriceOrtho', false);
        if (layerVisibility.caviteSilangNDVI) onLayerToggle('caviteSilangNDVI', false);
        if (layerVisibility.caviteSilangOrtho) onLayerToggle('caviteSilangOrtho', false);
        break;
      case 'cropArea':
        layerConfigs.cropArea.forEach(config => {
          if (layerVisibility[config.key]) {
            onLayerToggle(config.key, false);
          }
        });
        break;
      case 'disasterLossDamage':
        // Flood damage doesn't have persistent data to clear
        break;
      case 'lowCarbon':
        if (layerVisibility.strawBurn) {
          onLayerToggle('strawBurn', false);
        }
        onStrawBurnResultsUpdate?.(null);
        setSelectedProvince('');
        setSelectedStrawBurnMunicipality('');
        break;
      case 'treeDetection':
        if (layerVisibility.treeDetection) {
          onLayerToggle('treeDetection', false);
        }
        setTreeDetectionResults(null);
        setTreeDetectionImage(null);
        onTreeDetectionDataUpdate?.(null);
        setCrownSegmentationResults(null);
        onCrownSegmentationResultsUpdate?.(null);
        setCropfieldDelineationResults(null);
        setCropfieldDelineationImage(null);
        onCropfieldDelineationUpdate?.(null);
        break;
    }
  };

  const toggleTab = (tab: LayerTogglePanelTabId) => {
    // If clicking the same tab, just collapse it
    if (expandedTab === tab) {
      setExpandedTab(null);
      return;
    }

    // If switching to a different tab and either current or target tab has loaded data, show confirmation
    const currentTabHasData = expandedTab && hasLoadedData(expandedTab);
    const targetTabHasData = hasLoadedData(tab);
    
    if (expandedTab && expandedTab !== tab && (currentTabHasData || targetTabHasData)) {
      setPendingTab(tab);
      setShowConfirmDialog(true);
    } else {
      // No confirmation needed, but still clear layers from current tab if switching away
      if (expandedTab && expandedTab !== tab) {
        clearTabData(expandedTab);
      }
      // Also clear layers from target tab if it has any
      if (targetTabHasData) {
        clearTabData(tab);
      }
      
      // CRITICAL: Clear conflicting layers BEFORE switching tabs
      // When switching TO a specific tab, clear layers from conflicting tabs
      if (tab === 'droneData' || tab === 'fieldServices') {
        layerConfigs.multiTemporal.forEach(config => {
          onLayerToggle(config.key, false);
        });
        if (layerVisibility.vhi) onLayerToggle('vhi', false);
        if (layerVisibility.lst) onLayerToggle('lst', false);
        if (layerVisibility.plantingMethodDry2025) onLayerToggle('plantingMethodDry2025', false);
        if (layerVisibility.plantingMethodDry2024) onLayerToggle('plantingMethodDry2024', false);
        if (clearMultiTemporalAPITiles) clearMultiTemporalAPITiles();
      } else if (tab === 'multiTemporal') {
        layerConfigs.droneData.forEach(config => {
          onLayerToggle(config.key, false);
        });
        onLayerToggle('isabelaPhilriceNDVI', false);
        onLayerToggle('isabelaPhilriceOrtho', false);
        onLayerToggle('caviteSilangNDVI', false);
        onLayerToggle('caviteSilangOrtho', false);
      } else {
        layerConfigs.multiTemporal.forEach(config => {
          onLayerToggle(config.key, false);
        });
        layerConfigs.droneData.forEach(config => {
          onLayerToggle(config.key, false);
        });
        onLayerToggle('isabelaPhilriceNDVI', false);
        onLayerToggle('isabelaPhilriceOrtho', false);
        onLayerToggle('caviteSilangNDVI', false);
        onLayerToggle('caviteSilangOrtho', false);
        if (layerVisibility.vhi) onLayerToggle('vhi', false);
        if (layerVisibility.lst) onLayerToggle('lst', false);
        if (layerVisibility.plantingMethodDry2025) onLayerToggle('plantingMethodDry2025', false);
        if (layerVisibility.plantingMethodDry2024) onLayerToggle('plantingMethodDry2024', false);
        if (clearMultiTemporalAPITiles) clearMultiTemporalAPITiles();
      }
      
      // Switch to new tab AFTER clearing layers
      setExpandedTab(tab);
    }
  };

  const handleConfirmSwitch = () => {
    if (expandedTab && pendingTab) {
      // Clear data from current tab
      clearTabData(expandedTab);
      
      // Also clear data from target tab if it has any loaded data
      if (hasLoadedData(pendingTab)) {
        clearTabData(pendingTab);
      }
      
      // CRITICAL: Clear conflicting layers BEFORE switching tabs
      if (pendingTab === 'droneData' || pendingTab === 'fieldServices') {
        layerConfigs.multiTemporal.forEach(config => {
          onLayerToggle(config.key, false);
        });
        if (layerVisibility.vhi) onLayerToggle('vhi', false);
        if (layerVisibility.lst) onLayerToggle('lst', false);
        if (layerVisibility.plantingMethodDry2025) onLayerToggle('plantingMethodDry2025', false);
        if (layerVisibility.plantingMethodDry2024) onLayerToggle('plantingMethodDry2024', false);
        if (clearMultiTemporalAPITiles) clearMultiTemporalAPITiles();
      } else if (pendingTab === 'multiTemporal') {
        layerConfigs.droneData.forEach(config => {
          onLayerToggle(config.key, false);
        });
        onLayerToggle('isabelaPhilriceNDVI', false);
        onLayerToggle('isabelaPhilriceOrtho', false);
        onLayerToggle('caviteSilangNDVI', false);
        onLayerToggle('caviteSilangOrtho', false);
      } else {
        layerConfigs.multiTemporal.forEach(config => {
          onLayerToggle(config.key, false);
        });
        layerConfigs.droneData.forEach(config => {
          onLayerToggle(config.key, false);
        });
        onLayerToggle('isabelaPhilriceNDVI', false);
        onLayerToggle('isabelaPhilriceOrtho', false);
        onLayerToggle('caviteSilangNDVI', false);
        onLayerToggle('caviteSilangOrtho', false);
        if (layerVisibility.vhi) onLayerToggle('vhi', false);
        if (layerVisibility.lst) onLayerToggle('lst', false);
        if (layerVisibility.plantingMethodDry2025) onLayerToggle('plantingMethodDry2025', false);
        if (layerVisibility.plantingMethodDry2024) onLayerToggle('plantingMethodDry2024', false);
        if (clearMultiTemporalAPITiles) clearMultiTemporalAPITiles();
      }

      // Switch to pending tab AFTER clearing layers
      setExpandedTab(pendingTab);
    }

    // Close dialog and reset pending
    setShowConfirmDialog(false);
    setPendingTab(null);
  };

  const handleCancelSwitch = () => {
    setShowConfirmDialog(false);
    setPendingTab(null);
  };

  const sectionsLiveInHeader = Boolean(onClosePinnedSection);
  const showNavSection = (id: string) =>
    sectionsLiveInHeader ? pinnedSectionId === id : !pinnedSectionId || pinnedSectionId === id;
  const showDroneSamplePanel =
    expandedTab === 'droneData' ||
    (expandedTab === 'fieldServices' && !isFieldServiceSensorNavSection(pinnedSectionId));

  useEffect(() => {
    if (!pinnedSectionId) return;
    if (pinnedSectionId === 'cadi') setIsCadiExpanded(true);
    if (pinnedSectionId === 'pest') setIsPestRiskExpanded(true);
    if (pinnedSectionId === 'planting') setIsPlantingMethodExpanded(true);
    if (pinnedSectionId === 'growthStage') setIsRiceGrowthStageExpanded(true);
    if (pinnedSectionId === 'irrigation') setIsIrrigationTypeExpanded(true);
    if (pinnedSectionId === 'lst') setIsLstExpanded(true);
    if (pinnedSectionId === 'vhi') setIsVhiExpanded(true);
    if (pinnedSectionId === 'bsri') setIsBsriExpanded(true);
    if (pinnedSectionId === 'isabela') setIsIsabelaPhilriceExpanded(true);
    if (pinnedSectionId === 'cavite') setIsCaviteSilangExpanded(true);
    if (pinnedSectionId === 'trees') setIsTreeDetectionExpanded(true);
    if (pinnedSectionId === 'crowns') setIsCrownSegmentationExpanded(true);
    if (pinnedSectionId === 'fields') setIsCropfieldDelineationExpanded(true);
  }, [pinnedSectionId]);

  const onInfoSectionHandledRef = useRef(onInfoSectionHandled);
  onInfoSectionHandledRef.current = onInfoSectionHandled;
  useEffect(() => {
    if (!infoSectionId) return;
    if (infoSectionId === 'cadi') setShowCadiInfo(true);
    else if (infoSectionId === 'pest') setShowPestRiskInfo(true);
    else if (infoSectionId === 'straw') setShowStrawBurnInfo(true);
    else if (infoSectionId === 'flood') setShowFloodDamageInfo(true);
    else {
      const copy = HEADER_LAYER_INFO[infoSectionId];
      if (copy) setGenericSectionInfo(copy);
    }
    onInfoSectionHandledRef.current?.();
  }, [infoSectionId]);

  // Render panel content (shared between embedded and non-embedded modes)
  const renderPanelContent = () => (
    <>
    <div className={`${embedded ? 'h-full' : 'absolute top-12 sm:top-16 right-0 w-[calc(100vw-1rem)] sm:w-[450px] max-w-[450px]'} ${lt.panel} ${embedded ? '' : 'rounded-[14px] shadow-[0_1px_2px_rgba(0,0,0,.4),0_10px_26px_-14px_rgba(0,0,0,.7)]'} flex flex-col sm:flex-row ${embedded ? '' : 'max-h-[calc(100vh-4rem)] sm:max-h-[calc(100vh-100px)]'} overflow-hidden`}>
          {/* Left Sidebar - Layer Tabs (hidden when the parent renders the icon rail externally via `hideTabStrip`). */}
          {!hideTabStrip && (
          <div className={`w-full sm:w-[57px] lg:w-[73px] ${lt.tabRail} border-b sm:border-b-0 sm:border-r flex-shrink-0 overflow-x-auto sm:overflow-y-auto max-h-[120px] sm:max-h-none`}>
            {/* Tab Navigation - Horizontal on mobile, Vertical on desktop */}
            <div className="flex flex-row sm:flex-col gap-2 sm:gap-3 p-1 sm:p-1.5">
              {LAYER_TOGGLE_PANEL_TAB_ORDER.filter((tab) => isPanelTabUnlocked(allowedEntitlementSet, tab)).map((tab) => {
                const visibleCount = getVisibleLayersCount(tab);
                const isExpanded = expandedTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => toggleTab(tab)}
                    className={`relative py-1.5 sm:py-2.5 px-1.5 sm:px-2 text-[9px] sm:text-xs font-medium rounded-[10px] transition-all duration-200 flex flex-row sm:flex-col items-center justify-center gap-0.5 sm:gap-1 min-w-[60px] sm:min-w-0 ${
                      isExpanded ? lt.tabActive : lt.tabIdle
                    }`}
                  >
                    <i className={`${tabIcons[tab]} text-base sm:text-xl relative z-10 ${
                      isExpanded ? 'text-white' : visibleCount > 0 ? lt.selectedText : lt.muted
                    }`}></i>
                    <span className="text-[7px] sm:text-[9px] font-bold text-center leading-tight px-0.5 sm:px-1 relative z-10">
                      {tabNames[tab]}
                    </span>
                    {visibleCount > 0 && (
                      <span className={`absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 text-[7px] sm:text-[9px] rounded-full font-bold z-10 ${
                        isExpanded
                          ? isDark ? 'bg-[#161b22] text-[#34d399]' : 'bg-white text-[#008540]'
                          : isDark
                            ? 'bg-[#008540] text-white'
                            : 'bg-[#008540] text-white'
                      }`}>
                        {visibleCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          )}

          {/* Right Content Area */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            {/* Content Body - Scrollable */}
            <div className={`overflow-y-auto flex-1 min-h-0 ${lt.content} ${
              pinnedSectionId
                ? 'px-2 pt-3 [&_.layer-section-header]:hidden'
                : isCompactEmbedded
                  ? 'layer-toggle-content px-1 py-0.5'
                  : 'p-1.5 sm:p-2'
            }`} style={{ WebkitOverflowScrolling: 'touch' }}>
              {pinnedSectionId && onClosePinnedSection && (
                <div className="mb-2 flex justify-end">
                  <button
                    type="button"
                    onClick={onClosePinnedSection}
                    title="Close this layer"
                    aria-label="Close this layer"
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-[#008540] text-white shadow-[0_0_0_3px_rgba(52,211,153,.35)]"
                  >
                    <i className="ri-close-line text-base" aria-hidden />
                  </button>
                </div>
              )}
              {!expandedTab ? (
                // Empty state when no tab is selected
                <div className="flex flex-col items-center justify-center h-full text-center py-6 sm:py-8">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 ${lt.emptyIconWrap} rounded-full flex items-center justify-center mb-2 sm:mb-3`}>
                    <i className="ri-map-2-line text-xl sm:text-2xl"></i>
                  </div>
                  <h4 className={`text-sm font-semibold mb-0.5 sm:mb-1 ${lt.heading}`}>Select a Layer</h4>
                  <p className={`text-sm px-3 sm:px-4 ${lt.muted}`}>
                    Choose from the sidebar to view layer options.
                  </p>
                </div>
              ) : sectionsLiveInHeader && !pinnedSectionId ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-6 sm:py-8">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 ${lt.emptyIconWrap} rounded-full flex items-center justify-center mb-2 sm:mb-3`}>
                    <i className="ri-stack-line text-xl sm:text-2xl"></i>
                  </div>
                  <h4 className={`text-sm font-semibold mb-0.5 sm:mb-1 ${lt.heading}`}>Layer cleared</h4>
                  <p className={`text-sm px-3 sm:px-4 ${lt.muted}`}>
                    Choose a layer from the top bar to open its controls.
                  </p>
                </div>
              ) : (
                <div className={pinnedSectionId ? '' : isCompactEmbedded ? 'space-y-1.5' : 'space-y-2'}>
                  {expandedTab === 'multiTemporal' && (
                    <>
                      {/* CADI Agricultural Drought Forecast — featured / primary layer */}
                      {showNavSection('cadi') && canShow('cadiDrought') && (
                      <div
                        className={lt.section}
                      >
                        <div
                          className={`layer-section-header flex items-center justify-between ${compactSectionPad} cursor-pointer transition-colors ${lt.sectionHover}`}
                          onClick={() => setIsCadiExpanded(!isCadiExpanded)}
                        >
                          <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0">
                            <i className={`ri-drop-line text-base sm:text-lg shrink-0 ${lt.sectionIcon}`}></i>
                            <h4 className={`layer-section-title font-semibold truncate text-base ${lt.heading}`}>
                              El Niño CADI Drought Forecast
                            </h4>
                            <span className={`shrink-0 text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${lt.featured}`}>
                              Featured
                            </span>
                            <button
                              type="button"
                              title="About CADI"
                              aria-label="About CADI drought index"
                              aria-expanded={showCadiInfo}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowCadiInfo(true);
                              }}
                              className={`shrink-0 p-0.5 rounded-full transition-colors ${lg.infoBtn}`}
                            >
                              <i className="ri-information-line text-sm sm:text-base"></i>
                            </button>
                          </div>
                          <i className={`ri-arrow-${isCadiExpanded ? 'up' : 'down'}-s-line text-base sm:text-lg transition-transform shrink-0 ${lt.chevron}`}></i>
                        </div>

                        {isCadiExpanded && (
                          <div className="px-2 sm:px-3 pb-2 sm:pb-3">
                            <p className={`text-sm mb-2 sm:mb-3 ${lt.muted}`}>
                              El Niño–driven Combined Agricultural Drought Index (CADI) monthly forecast over the Philippines.
                              Enable the layer to view the forecast timelapse on the map (Sep 2026 – Aug 2027).
                            </p>

                            <label
                              className={`flex items-center justify-between p-1.5 sm:p-2 rounded-lg cursor-pointer transition-colors ${
                                layerVisibility.cadiDrought
                                  ? lt.toggleOn
                                  : lt.toggleOff
                              }`}
                            >
                              <div className="flex items-center space-x-1.5 sm:space-x-2">
                                <input
                                  type="checkbox"
                                  checked={layerVisibility.cadiDrought}
                                  onChange={() => {
                                    if (!layerVisibility.cadiDrought) {
                                      clearAllMultiTemporalLayers();
                                      onLayerToggle('cadiDrought', true);
                                    } else {
                                      onLayerToggle('cadiDrought', false);
                                    }
                                  }}
                                  className={`rounded ${isDark ? 'accent-[#34d399]' : 'accent-[#008540]'}`}
                                />
                                <span className={`text-sm font-medium ${lt.heading}`}>
                                  Show CADI forecast on map
                                </span>
                              </div>
                              <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${layerVisibility.cadiDrought ? lt.radioDot : (isDark ? 'bg-white/20' : 'bg-[#e5e7eb]')}`}></div>
                            </label>

                            {layerVisibility.cadiDrought && (
                              <div className="mt-2 space-y-1.5">
                                <div className="flex items-center justify-between">
                                  <span className={`text-sm ${lt.selectedText}`}>Opacity</span>
                                  <span className={`text-sm font-medium ${lt.selectedText}`}>{Math.round(layerOpacity.cadiDrought * 100)}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.05"
                                  value={layerOpacity.cadiDrought}
                                  onChange={(e) => onOpacityChange('cadiDrought', parseFloat(e.target.value))}
                                  {...opacitySliderProps(isDark, layerOpacity.cadiDrought)}
                                />
                                {onZonalProvinceChange && onZonalMunicipalitySelect && (
                                  <MultitemporalZonalPanel
                                    embedded
                                    state={zonalState ?? { status: 'idle' }}
                                    layer="cadi"
                                    frameLabel={zonalFrameLabel}
                                    pestLabel={zonalPestLabel}
                                    provinces={zonalProvinces}
                                    selectedProvince={zonalSelectedProvince}
                                    onProvinceChange={onZonalProvinceChange}
                                    onSelectMunicipality={onZonalMunicipalitySelect}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      )}

                      {/* Pest Occurrence Probability Index — quarterly seasonal layers */}
                      {showNavSection('pest') && canShow('pestRisk') && (
                      <div
                        className={`${lt.section} mb-2 sm:mb-3`}
                      >
                        <div
                          className={`layer-section-header flex items-center justify-between ${compactSectionPad} cursor-pointer transition-colors ${lt.sectionHover}`}
                          onClick={() => setIsPestRiskExpanded(!isPestRiskExpanded)}
                        >
                          <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0">
                            <i className={`ri-bug-line text-base sm:text-lg shrink-0 ${lt.sectionIcon}`}></i>
                            <h4 className={`layer-section-title font-semibold truncate text-base ${lt.heading}`}>
                              Pest Occurrence Probability Index
                            </h4>
                            <button
                              type="button"
                              title="About Pest Occurrence Probability Index"
                              aria-label="About Pest Occurrence Probability Index"
                              aria-expanded={showPestRiskInfo}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowPestRiskInfo(true);
                              }}
                              className={`shrink-0 p-0.5 rounded-full transition-colors ${lg.infoBtn}`}
                            >
                              <i className="ri-information-line text-sm sm:text-base"></i>
                            </button>
                          </div>
                          <i className={`ri-arrow-${isPestRiskExpanded ? 'up' : 'down'}-s-line text-base sm:text-lg transition-transform shrink-0 ${lt.chevron}`}></i>
                        </div>

                        {isPestRiskExpanded && (
                          <div className="px-2 sm:px-3 pb-2 sm:pb-3">
                            <p className={`text-sm mb-2 sm:mb-3 ${lt.muted}`}>
                              Seasonal pest and disease occurrence probability across the Philippines. Use the time slider below the map to step through quarters (Q1–Q4).
                            </p>

                            <label className={`block text-sm font-medium mb-1 ${lt.muted}`}>
                              Pest / disease
                            </label>
                            <select
                              value={pestRiskPestKey}
                              onChange={(e) => handlePestRiskPestSelect(e.target.value as PestRiskPestKey)}
                              className={`w-full mb-2 sm:mb-3 text-sm rounded-lg border px-2 py-1.5 ${lt.input}`}
                            >
                              {PEST_RISK_PESTS.map((pest) => (
                                <option key={pest.key} value={pest.key}>
                                  {pest.label}
                                </option>
                              ))}
                            </select>

                            <label
                              className={`flex items-center justify-between p-1.5 sm:p-2 rounded-lg cursor-pointer transition-colors ${
                                layerVisibility.pestRisk
                                  ? lt.toggleOn
                                  : lt.toggleOff
                              }`}
                            >
                              <div className="flex items-center space-x-1.5 sm:space-x-2">
                                <input
                                  type="checkbox"
                                  checked={layerVisibility.pestRisk}
                                  onChange={() => {
                                    if (!layerVisibility.pestRisk) {
                                      clearAllMultiTemporalLayers();
                                      handlePestRiskPestSelect(pestRiskPestKey);
                                      onLayerToggle('pestRisk', true);
                                    } else {
                                      onLayerToggle('pestRisk', false);
                                    }
                                  }}
                                  className={`rounded ${isDark ? 'accent-[#34d399]' : 'accent-[#008540]'}`}
                                />
                                <span className={`text-sm font-medium ${lt.heading}`}>
                                  Show on map
                                </span>
                              </div>
                              <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${layerVisibility.pestRisk ? lt.radioDot : (isDark ? 'bg-white/20' : 'bg-[#e5e7eb]')}`}></div>
                            </label>

                            {layerVisibility.pestRisk && (
                              <div className="mt-2 space-y-1.5">
                                <div className="flex items-center justify-between">
                                  <span className={`text-sm ${lt.selectedText}`}>Opacity</span>
                                  <span className={`text-sm font-medium ${lt.selectedText}`}>{Math.round(layerOpacity.pestRisk * 100)}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.05"
                                  value={layerOpacity.pestRisk}
                                  onChange={(e) => onOpacityChange('pestRisk', parseFloat(e.target.value))}
                                  {...opacitySliderProps(isDark, layerOpacity.pestRisk)}
                                />
                                {onZonalProvinceChange && onZonalMunicipalitySelect && (
                                  <MultitemporalZonalPanel
                                    embedded
                                    state={zonalState ?? { status: 'idle' }}
                                    layer="pestRisk"
                                    frameLabel={zonalFrameLabel}
                                    pestLabel={zonalPestLabel}
                                    provinces={zonalProvinces}
                                    selectedProvince={zonalSelectedProvince}
                                    onProvinceChange={onZonalProvinceChange}
                                    onSelectMunicipality={onZonalMunicipalitySelect}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      )}


                      {/* Past Yield — hidden; CADI is the featured drought layer */}
                      {SHOW_PAST_YIELD_LAYER && (() => {
                        const pastYieldConfig = layerConfigs.multiTemporal.find((c) => c.key === 'yield');
                        if (!pastYieldConfig) return null;
                        return (
                          <div className="mb-2 sm:mb-3">
                            <h5
                              className={`mb-1 sm:mb-2 text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}
                            >
                              Past Yield (historical time-series)
                            </h5>
                            {renderMultiTemporalLayerItem(pastYieldConfig)}
                          </div>
                        );
                      })()}
                      {layerConfigs.multiTemporal.filter(config =>
                        config.key !== 'yield' &&
                        config.key !== 'bsri'
                      ).map(renderMultiTemporalLayerItem)}

                      {showNavSection('lst') && canShow('lst') && (
                      <div className={lt.section}>
                        {/* LST Philippines (MODIS) */}
                        <div
                          className={`layer-section-header flex items-center justify-between ${compactSectionPad} cursor-pointer transition-colors ${lt.sectionHover}`}
                          onClick={() => setIsLstExpanded(!isLstExpanded)}
                        >
                          <div className="flex items-center space-x-1.5 sm:space-x-2">
                            <i className={`ri-temp-hot-line text-base sm:text-lg ${lt.sectionIcon}`}></i>
                            <h4 className={`layer-section-title font-semibold text-base ${lt.heading}`}>Land Surface Temperature</h4>
                          </div>
                          <i className={`ri-arrow-${isLstExpanded ? 'up' : 'down'}-s-line text-base sm:text-lg transition-transform ${lt.chevron}`}></i>
                        </div>

                        {isLstExpanded && (
                          <div className="px-2 sm:px-3 pb-2 sm:pb-3">
                            <p className={`text-sm mb-2 sm:mb-3 ${lt.muted}`}>
                              MODIS LST daytime time series. Select a date range within available data to load tiles.
                            </p>

                            {/* Date Range Section */}
                            <div className="space-y-1.5 sm:space-y-2 mb-2 sm:mb-3">
                              <div className={`text-sm font-medium mb-0.5 sm:mb-1 ${lt.selectedText}`}>
                                Select Date Range to Load
                              </div>
                              <div className="space-y-1 sm:space-y-1.5">
                                <div>
                                  <label className={`text-xs block mb-0.5 ${lt.sectionIcon}`}>Start Date</label>
                                  <input
                                    type="date"
                                    value={lstStartDate}
                                    min={lstAvailMinDate || undefined}
                                    max={lstAvailMaxDate || undefined}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (lstAvailMinDate && v < lstAvailMinDate) return;
                                      if (lstAvailMaxDate && v > lstAvailMaxDate) return;
                                      const next = clampDateRangeToAvailability(
                                        onStartDateChange(v, lstEndDate, lstStartDate),
                                        lstAvailMinDate,
                                        lstAvailMaxDate
                                      );
                                      setLstStartDate(next.start);
                                      setLstEndDate(next.end);
                                    }}
                                    disabled={lstIsLoading || !lstAvailMinDate}
                                    className={`w-full text-sm px-1.5 sm:px-2 py-0.5 sm:py-1 rounded focus:outline-none focus:ring-1 focus:ring-[#008540] disabled:opacity-50 disabled:cursor-not-allowed ${lt.input}`}
                                  />
                                </div>
                                <div>
                                  <label className={`text-xs block mb-0.5 ${lt.sectionIcon}`}>End Date</label>
                                  <input
                                    type="date"
                                    value={lstEndDate}
                                    min={lstAvailMinDate || undefined}
                                    max={lstAvailMaxDate || undefined}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (lstAvailMinDate && v < lstAvailMinDate) return;
                                      if (lstAvailMaxDate && v > lstAvailMaxDate) return;
                                      const next = clampDateRangeToAvailability(
                                        onEndDateChange(lstStartDate, v, lstEndDate),
                                        lstAvailMinDate,
                                        lstAvailMaxDate
                                      );
                                      setLstStartDate(next.start);
                                      setLstEndDate(next.end);
                                    }}
                                    disabled={lstIsLoading || !lstAvailMaxDate}
                                    className={`w-full text-sm px-1.5 sm:px-2 py-0.5 sm:py-1 rounded focus:outline-none focus:ring-1 focus:ring-[#008540] disabled:opacity-50 disabled:cursor-not-allowed ${lt.input}`}
                                  />
                                </div>
                                <button
                                  onClick={() => {
                                    console.log('[LST] Load Data clicked', {
                                      lstStartDate,
                                      lstEndDate,
                                      lstAvailMinDate,
                                      lstAvailMaxDate,
                                      hasHandler: !!onLSTPHDateRangeUpdate,
                                      lstIsLoading
                                    });
                                    if (!onLSTPHDateRangeUpdate || !lstStartDate || !lstEndDate) return;
                                    if (lstAvailMinDate && lstStartDate < lstAvailMinDate) return;
                                    if (lstAvailMaxDate && lstEndDate > lstAvailMaxDate) return;

                                    // Clear all other multi-temporal layers first
                                    clearAllMultiTemporalLayers();

                                    console.log('[LST] Invoking onLSTPHDateRangeUpdate with range', {
                                      start_date: lstStartDate,
                                      end_date: lstEndDate
                                    });
                                    onLSTPHDateRangeUpdate({
                                      start_date: lstStartDate,
                                      end_date: lstEndDate
                                    });
                                  }}
                                  disabled={lstIsLoading || !lstStartDate || !lstEndDate}
                                  className={`w-full py-1 sm:py-1.5 px-1.5 sm:px-2 ${lt.primaryBtn} text-sm rounded-[8px] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                  {lstIsLoading ? (
                                    <span className="flex items-center justify-center">
                                      <div className="animate-spin h-2.5 w-2.5 sm:h-3 sm:w-3 border-2 border-white rounded-full border-t-transparent mr-1"></div>
                                      Loading...
                                    </span>
                                  ) : (
                                    <>
                                      <i className={`${layerVisibility.lst ? 'ri-refresh-line' : 'ri-download-line'} mr-1`}></i>
                                      {layerVisibility.lst ? 'Update Data' : 'Load Data'}
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>

                            {/* Layer Status */}
                            {layerVisibility.lst && (
                              <div className={`rounded p-1.5 sm:p-2 text-sm border ${lt.infoNote}`}>
                                <i className="ri-check-line mr-1"></i>
                                LST Philippines time-series layer is active on map
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      )}

                      {/* VHI Philippines Section — disabled; use CADI Drought Forecast instead */}
                      {showNavSection('vhi') && SHOW_AGRICULTURAL_DROUGHT_VHI_LAYER && canShow('vhi') && (
                      <div className={`${lt.section} mt-2`}>
                        <div
                          className={`layer-section-header flex items-center justify-between ${compactSectionPad} cursor-pointer transition-colors ${lt.sectionHover}`}
                          onClick={() => setIsVhiExpanded(!isVhiExpanded)}
                        >
                          <div className="flex items-center space-x-1.5 sm:space-x-2">
                            <i className={`ri-leaf-line text-base sm:text-lg ${lt.sectionIcon}`}></i>
                            <h4 className={`layer-section-title font-semibold text-base ${lt.heading}`}>Agricultural Drought (VHI)</h4>
                          </div>
                          <i className={`ri-arrow-${isVhiExpanded ? 'up' : 'down'}-s-line text-base sm:text-lg transition-transform ${lt.chevron}`}></i>
                        </div>

                        {isVhiExpanded && (
                          <div className="px-2 sm:px-3 pb-2 sm:pb-3">
                            <p className={`text-sm mb-2 sm:mb-3 ${lt.muted}`}>
                              MODIS-based Vegetation Health Index (VHI) agricultural drought time series over croplands in the Philippines.
                              Select a date range within available data to load tiles.
                            </p>

                            {/* Date Range Section */}
                            <div className="space-y-1.5 sm:space-y-2 mb-2 sm:mb-3">
                              <div className={`text-sm font-medium mb-0.5 sm:mb-1 ${lt.selectedText}`}>
                                Select Date Range to Load
                              </div>
                              <div className="space-y-1 sm:space-y-1.5">
                                <div>
                                  <label className={`text-xs block mb-0.5 ${lt.sectionIcon}`}>Start Date</label>
                                  <input
                                    type="date"
                                    value={vhiStartDate}
                                    min={vhiAvailMinDate || undefined}
                                    max={vhiAvailMaxDate || undefined}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (vhiAvailMinDate && v < vhiAvailMinDate) return;
                                      if (vhiAvailMaxDate && v > vhiAvailMaxDate) return;
                                      const next = clampDateRangeToAvailability(
                                        onStartDateChange(v, vhiEndDate, vhiStartDate),
                                        vhiAvailMinDate,
                                        vhiAvailMaxDate
                                      );
                                      setVhiStartDate(next.start);
                                      setVhiEndDate(next.end);
                                    }}
                                    disabled={vhiIsLoading || !vhiAvailMinDate}
                                    className={`w-full text-sm px-1.5 sm:px-2 py-0.5 sm:py-1 rounded focus:outline-none focus:ring-1 focus:ring-[#008540] disabled:opacity-50 disabled:cursor-not-allowed ${lt.input}`}
                                  />
                                </div>
                                <div>
                                  <label className={`text-xs block mb-0.5 ${lt.sectionIcon}`}>End Date</label>
                                  <input
                                    type="date"
                                    value={vhiEndDate}
                                    min={vhiAvailMinDate || undefined}
                                    max={vhiAvailMaxDate || undefined}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (vhiAvailMinDate && v < vhiAvailMinDate) return;
                                      if (vhiAvailMaxDate && v > vhiAvailMaxDate) return;
                                      const next = clampDateRangeToAvailability(
                                        onEndDateChange(vhiStartDate, v, vhiEndDate),
                                        vhiAvailMinDate,
                                        vhiAvailMaxDate
                                      );
                                      setVhiStartDate(next.start);
                                      setVhiEndDate(next.end);
                                    }}
                                    disabled={vhiIsLoading || !vhiAvailMaxDate}
                                    className={`w-full text-sm px-1.5 sm:px-2 py-0.5 sm:py-1 rounded focus:outline-none focus:ring-1 focus:ring-[#008540] disabled:opacity-50 disabled:cursor-not-allowed ${lt.input}`}
                                  />
                                </div>
                                <button
                                  onClick={() => {
                                    if (!onVHIPHDateRangeUpdate || !vhiStartDate || !vhiEndDate) return;
                                    if (vhiAvailMinDate && vhiStartDate < vhiAvailMinDate) return;
                                    if (vhiAvailMaxDate && vhiEndDate > vhiAvailMaxDate) return;

                                    // Clear all other multi-temporal layers first
                                    clearAllMultiTemporalLayers();

                                    onVHIPHDateRangeUpdate({
                                      start_date: vhiStartDate,
                                      end_date: vhiEndDate
                                    });
                                  }}
                                  disabled={vhiIsLoading || !vhiStartDate || !vhiEndDate}
                                  className={`w-full py-1 sm:py-1.5 px-1.5 sm:px-2 ${lt.primaryBtn} text-sm rounded-[8px] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                  {vhiIsLoading ? (
                                    <span className="flex items-center justify-center">
                                      <div className="animate-spin h-2.5 w-2.5 sm:h-3 sm:w-3 border-2 border-white rounded-full border-t-transparent mr-1"></div>
                                      Loading...
                                    </span>
                                  ) : (
                                    <>
                                      <i className="ri-download-line mr-1"></i>
                                      Load Data
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      )}

                      {/* BSRI Section — hidden for now */}
                      {showNavSection('bsri') && SHOW_BSRI_LAYER && canShow('bsri') && (
                      <div className={lt.section}>
                        <div
                          className={`layer-section-header flex items-center justify-between ${compactSectionPad} cursor-pointer transition-colors ${lt.sectionHover}`}
                          onClick={() => setIsBsriExpanded(!isBsriExpanded)}
                        >
                          <div className="flex items-center space-x-1.5 sm:space-x-2">
                            <i className={`ri-plant-line text-base sm:text-lg ${lt.sectionIcon}`}></i>
                            <h4 className={`layer-section-title font-semibold text-base ${lt.heading}`}>Black Sigatoka Risk</h4>
                          </div>
                          <i className={`ri-arrow-${isBsriExpanded ? 'up' : 'down'}-s-line text-base sm:text-lg transition-transform ${lt.chevron}`}></i>
                        </div>

                        {isBsriExpanded && (
                          <div className="px-2 sm:px-3 pb-2 sm:pb-3">
                            <p className={`text-sm mb-2 sm:mb-3 ${lt.muted}`}>
                              Weekly Black Sigatoka risk for banana. Recent and upcoming weeks use ECMWF IFS at about 9 km; earlier weeks use ERA5-Land.
                            </p>

                        {/* Date Range Section */}
                        <div className="space-y-1.5 sm:space-y-2 mb-2 sm:mb-3">
                          <div className={`text-sm font-medium mb-0.5 sm:mb-1 ${lt.selectedText}`}>
                            Select Date Range to Load
                          </div>
                          <div className="space-y-1 sm:space-y-1.5">
                            <div>
                              <label className={`text-xs block mb-0.5 ${lt.sectionIcon}`}>Start Date</label>
                              <input
                                type="date"
                                value={bsriStartDate}
                                onChange={(e) => {
                                  const next = onStartDateChange(
                                    e.target.value,
                                    bsriEndDate,
                                    bsriStartDate
                                  );
                                  setBsriStartDate(next.start);
                                  setBsriEndDate(next.end);
                                }}
                                disabled={bsriIsLoading}
                                className={`w-full text-sm px-1.5 sm:px-2 py-0.5 sm:py-1 rounded focus:outline-none focus:ring-1 focus:ring-[#008540] disabled:opacity-50 disabled:cursor-not-allowed ${lt.input}`}
                              />
                            </div>
                            <div>
                              <label className={`text-xs block mb-0.5 ${lt.sectionIcon}`}>End Date</label>
                              <input
                                type="date"
                                value={bsriEndDate}
                                onChange={(e) => {
                                  const next = onEndDateChange(
                                    bsriStartDate,
                                    e.target.value,
                                    bsriEndDate
                                  );
                                  setBsriStartDate(next.start);
                                  setBsriEndDate(next.end);
                                }}
                                disabled={bsriIsLoading}
                                className={`w-full text-sm px-1.5 sm:px-2 py-0.5 sm:py-1 rounded focus:outline-none focus:ring-1 focus:ring-[#008540] disabled:opacity-50 disabled:cursor-not-allowed ${lt.input}`}
                              />
                            </div>
                            <button
                              onClick={() => {
                                // Clear all other multi-temporal layers first
                                clearAllMultiTemporalLayers();

                                const range = {
                                  start_date: bsriStartDate,
                                  end_date: bsriEndDate,
                                };
                                if (onBSRIDateRangeUpdate) {
                                  onBSRIDateRangeUpdate(range);
                                }
                                // Toggle layer on after loading data
                                if (!layerVisibility.bsri) {
                                  onLayerToggle('bsri', true);
                                }
                              }}
                              disabled={bsriIsLoading}
                              className={`w-full py-1 sm:py-1.5 px-1.5 sm:px-2 ${lt.primaryBtn} text-sm rounded-[8px] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {bsriIsLoading ? (
                                <span className="flex items-center justify-center">
                                  <div className="animate-spin h-2.5 w-2.5 sm:h-3 sm:w-3 border-2 border-white rounded-full border-t-transparent mr-1"></div>
                                  Loading...
                                </span>
                              ) : (
                                <>
                                  <i className={`${layerVisibility.bsri ? 'ri-refresh-line' : 'ri-download-line'} mr-1`}></i>
                                  {layerVisibility.bsri ? 'Update Data' : 'Load Data'}
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Layer Status */}
                        {layerVisibility.bsri && (
                          <div className={`rounded p-1.5 sm:p-2 text-sm border ${lt.infoNote}`}>
                            <i className="ri-check-line mr-1"></i>
                            BSRI layer is active on map
                          </div>
                        )}

                        {canShowBlsForecast && (
                          <BlsForecastCard
                            headingClass={lt.heading}
                            mutedClass={lt.muted}
                            inputClass={lt.input}
                            compact={isCompactEmbedded}
                            fieldGeoJson={fieldGeoJson}
                            mapCenter={mapCenter}
                            farmId={farmId}
                            requireField
                          />
                        )}

                        {/* Info */}
                        <div className={`mt-2 sm:mt-3 pt-2 sm:pt-3 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                          <i className="ri-information-line mr-1"></i>
                          Select dates and click "Load Data" to visualize the risk index timelapse
                        </div>
                          </div>
                        )}
                      </div>
                      )}

                      {/* Rice Planting Method (Dry 2024 / Dry 2025) - Collapsible */}
                      {showNavSection('planting') && (canShow('plantingMethodDry2025') || canShow('plantingMethodDry2024')) && (
                      <div className={lt.section}>
                        <div
                          className={`layer-section-header flex items-center justify-between ${compactSectionPad} cursor-pointer transition-colors ${lt.sectionHover}`}
                          onClick={() => setIsPlantingMethodExpanded(!isPlantingMethodExpanded)}
                        >
                          <div className="flex items-center space-x-1.5 sm:space-x-2">
                            <i className={`ri-map-2-line text-base sm:text-lg ${lt.sectionIcon}`}></i>
                            <h4 className={`layer-section-title font-semibold text-base ${lt.heading}`}>Rice Planting Method</h4>
                            {(layerVisibility.plantingMethodDry2025 || layerVisibility.plantingMethodDry2024) && (
                              <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${lt.radioDot}`}></span>
                            )}
                          </div>
                          <i className={`ri-arrow-${isPlantingMethodExpanded ? 'up' : 'down'}-s-line text-base sm:text-lg transition-transform ${lt.chevron}`}></i>
                        </div>

                        {isPlantingMethodExpanded && (
                        <div className="px-2 sm:px-3 pb-2 sm:pb-3 space-y-2">
                          <p className={`text-sm mb-1 ${lt.muted}`}>
                            Philippines rice planting method classification (Direct-seeded vs Transplanted). Select one season at a time.
                          </p>
                          {/* Dry 2025 */}
                          <div className={`rounded-lg ${compactLayerPad} border transition-all duration-200 ${
                            layerVisibility.plantingMethodDry2025 ? lt.layerSelected : lt.layerIdle
                          }`}>
                            <div className="flex items-center justify-between mb-0.5 sm:mb-1">
                              <label className="flex items-center space-x-1 sm:space-x-1.5 cursor-pointer flex-1 min-w-0">
                                <input
                                  type="checkbox"
                                  checked={layerVisibility.plantingMethodDry2025}
                                  onChange={() => {
                                    if (!layerVisibility.plantingMethodDry2025) {
                                      clearAllMultiTemporalLayers();
                                      if (layerVisibility.plantingMethodDry2024) onLayerToggle('plantingMethodDry2024', false);
                                      onLayerToggle('plantingMethodDry2025', true);
                                    } else {
                                      onLayerToggle('plantingMethodDry2025', false);
                                    }
                                  }}
                                  className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded flex-shrink-0 ${isDark ? 'accent-[#34d399] border-white/20' : 'accent-[#008540] border-[#e5e7eb]'}`}
                                />
                                <i className={`ri-map-pin-line text-xs sm:text-sm flex-shrink-0 ${lt.sectionIcon}`}></i>
                                <span className={`text-sm font-medium truncate ${
                                  layerVisibility.plantingMethodDry2025
                                    ? lt.selectedText
                                    : lt.heading
                                }`}>
                                  Dry 2025
                                </span>
                              </label>
                              <div className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${layerVisibility.plantingMethodDry2025 ? lt.radioDot : (isDark ? 'bg-white/20' : 'bg-[#e5e7eb]')}`}></div>
                            </div>

                            {layerVisibility.plantingMethodDry2025 && (
                              <div className="space-y-0.5 sm:space-y-1" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center justify-between">
                                  <span className={`text-sm ${lt.sectionIcon}`}>Opacity</span>
                                  <span className={`text-sm font-medium ${lt.selectedText}`}>{Math.round(layerOpacity.plantingMethodDry2025 * 100)}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.1"
                                  value={layerOpacity.plantingMethodDry2025}
                                  onChange={(e) => onOpacityChange('plantingMethodDry2025', parseFloat(e.target.value))}
                                  className={`w-full h-1 sm:h-1.5 rounded-lg appearance-none cursor-pointer opacity-slider ${isDark ? 'bg-[#253347]' : 'bg-[#e5e7eb]'}`}
                                />
                              </div>
                            )}
                          </div>

                          {/* Dry 2024 */}
                          <div className={`rounded-lg ${compactLayerPad} border transition-all duration-200 ${
                            layerVisibility.plantingMethodDry2024 ? lt.layerSelected : lt.layerIdle
                          }`}>
                            <div className="flex items-center justify-between mb-0.5 sm:mb-1">
                              <label className="flex items-center space-x-1 sm:space-x-1.5 cursor-pointer flex-1 min-w-0">
                                <input
                                  type="checkbox"
                                  checked={layerVisibility.plantingMethodDry2024}
                                  onChange={() => {
                                    if (!layerVisibility.plantingMethodDry2024) {
                                      clearAllMultiTemporalLayers();
                                      if (layerVisibility.plantingMethodDry2025) onLayerToggle('plantingMethodDry2025', false);
                                      onLayerToggle('plantingMethodDry2024', true);
                                    } else {
                                      onLayerToggle('plantingMethodDry2024', false);
                                    }
                                  }}
                                  className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded flex-shrink-0 ${isDark ? 'accent-[#34d399] border-white/20' : 'accent-[#008540] border-[#e5e7eb]'}`}
                                />
                                <i className={`ri-map-pin-line text-xs sm:text-sm flex-shrink-0 ${lt.sectionIcon}`}></i>
                                <span className={`text-sm font-medium truncate ${
                                  layerVisibility.plantingMethodDry2024
                                    ? lt.selectedText
                                    : lt.heading
                                }`}>
                                  Dry 2024
                                </span>
                              </label>
                              <div className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${layerVisibility.plantingMethodDry2024 ? lt.radioDot : (isDark ? 'bg-white/20' : 'bg-[#e5e7eb]')}`}></div>
                            </div>

                            {layerVisibility.plantingMethodDry2024 && (
                              <div className="space-y-0.5 sm:space-y-1" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center justify-between">
                                  <span className={`text-sm ${lt.sectionIcon}`}>Opacity</span>
                                  <span className={`text-sm font-medium ${lt.selectedText}`}>{Math.round(layerOpacity.plantingMethodDry2024 * 100)}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.1"
                                  value={layerOpacity.plantingMethodDry2024}
                                  onChange={(e) => onOpacityChange('plantingMethodDry2024', parseFloat(e.target.value))}
                                  className={`w-full h-1 sm:h-1.5 rounded-lg appearance-none cursor-pointer opacity-slider ${isDark ? 'bg-[#253347]' : 'bg-[#e5e7eb]'}`}
                                />
                              </div>
                            )}
                          </div>

                          {/* Legend shown in top-right overlay when layer is active */}
                        </div>
                        )}
                      </div>
                      )}

                      {/* Rice Crop Growth Stage — wet 2026 monthly COG */}
                      {showNavSection('growthStage') && canShow('riceGrowthStage') && (
                      <div className={lt.section}>
                        <div
                          className={`layer-section-header flex items-center justify-between ${compactSectionPad} cursor-pointer transition-colors ${lt.sectionHover}`}
                          onClick={() => setIsRiceGrowthStageExpanded(!isRiceGrowthStageExpanded)}
                        >
                          <div className="flex items-center space-x-1.5 sm:space-x-2">
                            <i className={`ri-seedling-line text-base sm:text-lg ${lt.sectionIcon}`}></i>
                            <h4 className={`layer-section-title font-semibold text-base ${lt.heading}`}>Rice Crop Growth Stage</h4>
                            {growthStageBusy ? (
                              <i className={`ri-loader-4-line animate-spin text-xs ${lt.sectionIcon}`} aria-hidden />
                            ) : growthStageChecked ? (
                              <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${lt.radioDot}`}></span>
                            ) : null}
                          </div>
                          <i className={`ri-arrow-${isRiceGrowthStageExpanded ? 'up' : 'down'}-s-line text-base sm:text-lg transition-transform ${lt.chevron}`}></i>
                        </div>

                        {isRiceGrowthStageExpanded && (
                        <div className="px-2 sm:px-3 pb-2 sm:pb-3 space-y-2">
                          <p className={`text-sm mb-1 leading-snug ${lt.muted}`}>
                            Philippines rice growth stage for the 2026 wet season. More months can be added as new COGs are published.
                          </p>
                          <div className={`relative overflow-hidden rounded-lg ${compactLayerPad} border transition-all duration-200 ${
                            growthStageChecked ? lt.layerSelected : lt.layerIdle
                          }`}>
                            <div className="flex items-center justify-between mb-0.5 sm:mb-1">
                              <label className="flex items-center space-x-1 sm:space-x-1.5 cursor-pointer flex-1 min-w-0">
                                <input
                                  type="checkbox"
                                  checked={growthStageChecked}
                                  onChange={() => {
                                    if (!growthStageChecked) {
                                      growthStageSawTiles.current = false;
                                      setGrowthStageArmed(true);
                                      clearAllMultiTemporalLayers();
                                      onLayerToggle('riceGrowthStage', true);
                                    } else {
                                      setGrowthStageArmed(false);
                                      growthStageSawTiles.current = false;
                                      onLayerToggle('riceGrowthStage', false);
                                    }
                                  }}
                                  className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded flex-shrink-0 transition-transform duration-200 ${growthStageChecked ? 'scale-110' : 'scale-100'} ${isDark ? 'accent-[#34d399] border-white/20' : 'accent-[#008540] border-[#e5e7eb]'}`}
                                />
                                {growthStageBusy ? (
                                  <i className={`ri-loader-4-line animate-spin text-xs sm:text-sm flex-shrink-0 ${lt.sectionIcon}`} aria-hidden />
                                ) : (
                                  <i className={`ri-seedling-line text-xs sm:text-sm flex-shrink-0 ${lt.sectionIcon}`}></i>
                                )}
                                <span className={`text-sm font-medium truncate ${
                                  growthStageChecked ? lt.selectedText : lt.heading
                                }`}>
                                  {growthStageBusy ? 'Loading map…' : RICE_GROWTH_STAGE_LABEL}
                                </span>
                              </label>
                              <div className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${growthStageChecked ? lt.radioDot : (isDark ? 'bg-white/20' : 'bg-[#e5e7eb]')}`}></div>
                            </div>

                            {growthStageBusy && (
                              <div className={`mb-1 h-0.5 overflow-hidden rounded-full ${isDark ? 'bg-white/10' : 'bg-[#e5e7eb]'}`} aria-hidden>
                                <div className={`h-full w-full origin-left animate-pulse rounded-full ${isDark ? 'bg-[#008540]' : 'bg-[#008540]'}`} />
                              </div>
                            )}

                            {layerVisibility.riceGrowthStage && !growthStageBusy && (
                              <div className="space-y-0.5 sm:space-y-1" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center justify-between">
                                  <span className={`text-xs ${lt.sectionIcon}`}>Opacity</span>
                                  <span className={`text-xs font-medium ${lt.selectedText}`}>{Math.round(layerOpacity.riceGrowthStage * 100)}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.1"
                                  value={layerOpacity.riceGrowthStage}
                                  onChange={(e) => onOpacityChange('riceGrowthStage', parseFloat(e.target.value))}
                                  className={`w-full h-1 sm:h-1.5 rounded-lg appearance-none cursor-pointer opacity-slider ${isDark ? 'bg-[#253347]' : 'bg-[#e5e7eb]'}`}
                                />
                              </div>
                            )}
                          </div>
                          <RiceGrowthStageAreaChart />
                        </div>
                        )}
                      </div>
                      )}

                      {showNavSection('irrigation') && canShow('irrigationType') && (
                      <div className={lt.section}>
                        <div
                          className={`layer-section-header flex items-center justify-between ${compactSectionPad} cursor-pointer transition-colors ${lt.sectionHover}`}
                          onClick={() => setIsIrrigationTypeExpanded(!isIrrigationTypeExpanded)}
                        >
                          <div className="flex items-center space-x-1.5 sm:space-x-2">
                            <i className={`ri-drop-line text-base sm:text-lg ${lt.sectionIcon}`}></i>
                            <h4 className={`layer-section-title font-semibold text-base ${lt.heading}`}>Irrigation Type</h4>
                            {irrigationBusy ? (
                              <i className={`ri-loader-4-line animate-spin text-xs ${lt.sectionIcon}`} aria-hidden />
                            ) : irrigationChecked ? (
                              <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${lt.radioDot}`}></span>
                            ) : null}
                          </div>
                          <i className={`ri-arrow-${isIrrigationTypeExpanded ? 'up' : 'down'}-s-line text-base sm:text-lg transition-transform ${lt.chevron}`}></i>
                        </div>

                        {isIrrigationTypeExpanded && (
                        <div className="px-2 sm:px-3 pb-2 sm:pb-3 space-y-2">
                          <p className={`text-sm mb-1 leading-snug ${lt.muted}`}>
                            Philippines irrigation type for the second half of September 2026. No active crop is left transparent.
                          </p>
                          <div className={`relative overflow-hidden rounded-lg ${compactLayerPad} border transition-all duration-200 ${
                            irrigationChecked ? lt.layerSelected : lt.layerIdle
                          }`}>
                            <div className="flex items-center justify-between mb-0.5 sm:mb-1">
                              <label className="flex items-center space-x-1 sm:space-x-1.5 cursor-pointer flex-1 min-w-0">
                                <input
                                  type="checkbox"
                                  checked={irrigationChecked}
                                  onChange={() => {
                                    if (!irrigationChecked) {
                                      irrigationSawTiles.current = false;
                                      setIrrigationArmed(true);
                                      clearAllMultiTemporalLayers();
                                      onLayerToggle('irrigationType', true);
                                    } else {
                                      setIrrigationArmed(false);
                                      irrigationSawTiles.current = false;
                                      onLayerToggle('irrigationType', false);
                                    }
                                  }}
                                  className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded flex-shrink-0 transition-transform duration-200 ${irrigationChecked ? 'scale-110' : 'scale-100'} ${isDark ? 'accent-[#34d399] border-white/20' : 'accent-[#008540] border-[#e5e7eb]'}`}
                                />
                                {irrigationBusy ? (
                                  <i className={`ri-loader-4-line animate-spin text-xs sm:text-sm flex-shrink-0 ${lt.sectionIcon}`} aria-hidden />
                                ) : (
                                  <i className={`ri-drop-line text-xs sm:text-sm flex-shrink-0 ${lt.sectionIcon}`}></i>
                                )}
                                <span className={`text-sm font-medium truncate ${
                                  irrigationChecked ? lt.selectedText : lt.heading
                                }`}>
                                  {irrigationBusy ? 'Loading map…' : IRRIGATION_TYPE_LABEL}
                                </span>
                              </label>
                              <div className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${irrigationChecked ? lt.radioDot : (isDark ? 'bg-white/20' : 'bg-[#e5e7eb]')}`}></div>
                            </div>

                            {irrigationBusy && (
                              <div className={`mb-1 h-0.5 overflow-hidden rounded-full ${isDark ? 'bg-white/10' : 'bg-[#e5e7eb]'}`} aria-hidden>
                                <div className={`h-full w-full origin-left animate-pulse rounded-full ${isDark ? 'bg-[#008540]' : 'bg-[#008540]'}`} />
                              </div>
                            )}

                            {layerVisibility.irrigationType && !irrigationBusy && (
                              <div className="space-y-0.5 sm:space-y-1" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center justify-between">
                                  <span className={`text-xs ${lt.sectionIcon}`}>Opacity</span>
                                  <span className={`text-xs font-medium ${lt.selectedText}`}>{Math.round(layerOpacity.irrigationType * 100)}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.1"
                                  value={layerOpacity.irrigationType}
                                  onChange={(e) => onOpacityChange('irrigationType', parseFloat(e.target.value))}
                                  className={`w-full h-1 sm:h-1.5 rounded-lg appearance-none cursor-pointer opacity-slider ${isDark ? 'bg-[#253347]' : 'bg-[#e5e7eb]'}`}
                                />
                              </div>
                            )}
                          </div>
                          <IrrigationAreaChart />
                        </div>
                        )}
                      </div>
                      )}
                    </>
                  )}
                  {showDroneSamplePanel && (
                    <>
                      {!pinnedSectionId && (
                      <p className={`text-center text-sm py-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Sample Data
                      </p>
                      )}
                      {/* Isabela PhilRice COGs (Titiler) — Collapsible */}
                      {showNavSection('isabela') && (canShow('isabelaPhilriceNDVI') || canShow('isabelaPhilriceOrtho')) && (
                      <div className={`mt-2 sm:mt-3 ${lt.section}`}>
                        <div
                          className={`layer-section-header flex items-center justify-between ${compactSectionPad} cursor-pointer transition-colors ${lt.sectionHover}`}
                          onClick={() => setIsIsabelaPhilriceExpanded(!isIsabelaPhilriceExpanded)}
                        >
                          <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0">
                            <i className={`ri-drone-fill text-base sm:text-lg flex-shrink-0 ${lt.sectionIcon}`}></i>
                            <h4 className={`text-xs sm:text-sm font-semibold truncate ${lt.heading}`}>Isabela_Philrice</h4>
                            {(layerVisibility.isabelaPhilriceNDVI ||
                              layerVisibility.isabelaPhilriceOrtho) && (
                              <span className={`w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0 ${lt.radioDot}`}></span>
                            )}
                          </div>
                          <i className={`ri-arrow-${isIsabelaPhilriceExpanded ? 'up' : 'down'}-s-line text-base sm:text-lg transition-transform flex-shrink-0 ${lt.chevron}`}></i>
                        </div>

                        {isIsabelaPhilriceExpanded && (
                          <div className="px-2 sm:px-3 pb-2 sm:pb-3 space-y-2">
                            <p className={`text-sm mb-1 ${lt.muted}`}>
                              High-resolution PhilRice Isabela drone NDVI and orthomosaic. Tap NDVI to view the map and open yield analysis.
                            </p>

                            {/* NDVI */}
                            <div
                              role="button"
                              tabIndex={0}
                              aria-pressed={layerVisibility.isabelaPhilriceNDVI}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.currentTarget.click();
                                }
                              }}
                              className={`rounded-lg ${compactLayerPad} border transition-all duration-200 cursor-pointer outline-none ${
                                layerVisibility.isabelaPhilriceNDVI ? lt.layerSelected : lt.layerIdle
                              }`}
                              onClick={() => {
                                if (layerVisibility.isabelaPhilriceNDVI) {
                                  onLayerToggle('isabelaPhilriceNDVI', false);
                                  onClearDronePrescriptionStats?.();
                                } else {
                                  onLayerToggle('isabelaPhilriceNDVI', true);
                                  onRequestDronePrescriptionStats?.(
                                    DRONE_MISSIONS.find((m) => m.key === 'isabela')!
                                  );
                                }
                              }}
                            >
                              <div className="flex gap-2 sm:gap-2.5 items-start pointer-events-none">
                                <div
                                  className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-md overflow-hidden border ${
                                    isDark ? 'border-gray-600 bg-gray-900' : 'border-gray-200 bg-gray-100'
                                  }`}
                                  aria-hidden
                                >
                                  <img
                                    src={buildIsabelaPhilricePreviewUrl('ndvi')}
                                    alt=""
                                    width={72}
                                    height={72}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1">
                                    <i className={`ri-leaf-line text-xs sm:text-sm flex-shrink-0 ${lt.sectionIcon}`}></i>
                                    <span
                                      className={`text-sm font-semibold truncate ${
                                        layerVisibility.isabelaPhilriceNDVI ? lt.selectedText : lt.heading
                                      }`}
                                    >
                                      Philrice Isabela_NDVI.tif
                                    </span>
                                  </div>
                                  {layerVisibility.isabelaPhilriceNDVI && (
                                    <div
                                      className="space-y-0.5 sm:space-y-1 pointer-events-auto"
                                      onClick={(e) => e.stopPropagation()}
                                      onKeyDown={(e) => e.stopPropagation()}
                                    >
                                      <div className="flex items-center justify-between">
                                        <span className={`text-sm ${lt.sectionIcon}`}>Opacity</span>
                                        <span className={`text-sm font-medium ${lt.selectedText}`}>
                                          {Math.round(layerOpacity.isabelaPhilriceNDVI * 100)}%
                                        </span>
                                      </div>
                                      <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.1"
                                        value={layerOpacity.isabelaPhilriceNDVI}
                                        onChange={(e) => onOpacityChange('isabelaPhilriceNDVI', parseFloat(e.target.value))}
                                        className={`w-full h-1 sm:h-1.5 rounded-lg appearance-none cursor-pointer opacity-slider ${isDark ? 'bg-[#253347]' : 'bg-[#e5e7eb]'}`}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Ortho */}
                            <div
                              role="button"
                              tabIndex={0}
                              aria-pressed={layerVisibility.isabelaPhilriceOrtho}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.currentTarget.click();
                                }
                              }}
                              className={`rounded-lg ${compactLayerPad} border transition-all duration-200 cursor-pointer outline-none ${
                                layerVisibility.isabelaPhilriceOrtho ? lt.layerSelected : lt.layerIdle
                              }`}
                              onClick={() => {
                                if (layerVisibility.isabelaPhilriceOrtho) {
                                  onLayerToggle('isabelaPhilriceOrtho', false);
                                } else {
                                  onLayerToggle('isabelaPhilriceOrtho', true);
                                }
                              }}
                            >
                              <div className="flex gap-2 sm:gap-2.5 items-start pointer-events-none">
                                <div
                                  className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-md overflow-hidden border ${
                                    isDark ? 'border-gray-600 bg-gray-900' : 'border-gray-200 bg-gray-100'
                                  }`}
                                  aria-hidden
                                >
                                  <img
                                    src={buildIsabelaPhilricePreviewUrl('ortho')}
                                    alt=""
                                    width={72}
                                    height={72}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1">
                                    <i className={`ri-image-2-line text-xs sm:text-sm flex-shrink-0 ${lt.sectionIcon}`}></i>
                                    <span
                                      className={`text-sm font-semibold truncate ${
                                        layerVisibility.isabelaPhilriceOrtho ? lt.selectedText : lt.heading
                                      }`}
                                    >
                                      Philrice Isabela_Ortho.tif
                                    </span>
                                  </div>
                                  {layerVisibility.isabelaPhilriceOrtho && (
                                    <div
                                      className="space-y-0.5 sm:space-y-1 pointer-events-auto"
                                      onClick={(e) => e.stopPropagation()}
                                      onKeyDown={(e) => e.stopPropagation()}
                                    >
                                      <div className="flex items-center justify-between">
                                        <span className={`text-sm ${lt.sectionIcon}`}>Opacity</span>
                                        <span className={`text-sm font-medium ${lt.selectedText}`}>
                                          {Math.round(layerOpacity.isabelaPhilriceOrtho * 100)}%
                                        </span>
                                      </div>
                                      <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.1"
                                        value={layerOpacity.isabelaPhilriceOrtho}
                                        onChange={(e) => onOpacityChange('isabelaPhilriceOrtho', parseFloat(e.target.value))}
                                        className={`w-full h-1 sm:h-1.5 rounded-lg appearance-none cursor-pointer opacity-slider ${isDark ? 'bg-[#253347]' : 'bg-[#e5e7eb]'}`}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      )}

                      {/* Cavite Silang COGs (Titiler) — Collapsible */}
                      {showNavSection('cavite') && (canShow('caviteSilangNDVI') || canShow('caviteSilangOrtho')) && (
                      <div className={`mt-2 sm:mt-3 ${lt.section}`}>
                        <div
                          className={`layer-section-header flex items-center justify-between ${compactSectionPad} cursor-pointer transition-colors ${lt.sectionHover}`}
                          onClick={() => setIsCaviteSilangExpanded(!isCaviteSilangExpanded)}
                        >
                          <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0">
                            <i className={`ri-drone-fill text-base sm:text-lg flex-shrink-0 ${lt.sectionIcon}`}></i>
                            <h4 className={`text-xs sm:text-sm font-semibold truncate ${lt.heading}`}>Cavite_Silang</h4>
                            {(layerVisibility.caviteSilangNDVI ||
                              layerVisibility.caviteSilangOrtho) && (
                              <span className={`w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0 ${lt.radioDot}`}></span>
                            )}
                          </div>
                          <i className={`ri-arrow-${isCaviteSilangExpanded ? 'up' : 'down'}-s-line text-base sm:text-lg transition-transform flex-shrink-0 ${lt.chevron}`}></i>
                        </div>

                        {isCaviteSilangExpanded && (
                          <div className="px-2 sm:px-3 pb-2 sm:pb-3 space-y-2">
                            <p className={`text-sm mb-1 ${lt.muted}`}>
                              Leads Agri drone survey (Cavite Silang). NDVI and orthomosaic. Tap NDVI to view the map and open yield analysis.
                            </p>

                            {/* NDVI */}
                            <div
                              role="button"
                              tabIndex={0}
                              aria-pressed={layerVisibility.caviteSilangNDVI}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.currentTarget.click();
                                }
                              }}
                              className={`rounded-lg ${compactLayerPad} border transition-all duration-200 cursor-pointer outline-none ${
                                layerVisibility.caviteSilangNDVI ? lt.layerSelected : lt.layerIdle
                              }`}
                              onClick={() => {
                                if (layerVisibility.caviteSilangNDVI) {
                                  onLayerToggle('caviteSilangNDVI', false);
                                  onClearDronePrescriptionStats?.();
                                } else {
                                  onLayerToggle('caviteSilangNDVI', true);
                                  onRequestDronePrescriptionStats?.(
                                    DRONE_MISSIONS.find((m) => m.key === 'cavite')!
                                  );
                                }
                              }}
                            >
                              <div className="flex gap-2 sm:gap-2.5 items-start pointer-events-none">
                                <div
                                  className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-md overflow-hidden border ${
                                    isDark ? 'border-gray-600 bg-gray-900' : 'border-gray-200 bg-gray-100'
                                  }`}
                                  aria-hidden
                                >
                                  <img
                                    src={buildCaviteSilangPreviewUrl('ndvi')}
                                    alt=""
                                    width={72}
                                    height={72}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1">
                                    <i className={`ri-leaf-line text-xs sm:text-sm flex-shrink-0 ${lt.sectionIcon}`}></i>
                                    <span
                                      className={`text-sm font-semibold truncate ${
                                        layerVisibility.caviteSilangNDVI ? lt.selectedText : lt.heading
                                      }`}
                                    >
                                      SilangFarmLeadsOct_NDVI.tif
                                    </span>
                                  </div>
                                  {layerVisibility.caviteSilangNDVI && (
                                    <div
                                      className="space-y-0.5 sm:space-y-1 pointer-events-auto"
                                      onClick={(e) => e.stopPropagation()}
                                      onKeyDown={(e) => e.stopPropagation()}
                                    >
                                      <div className="flex items-center justify-between">
                                        <span className={`text-sm ${lt.sectionIcon}`}>Opacity</span>
                                        <span className={`text-sm font-medium ${lt.selectedText}`}>
                                          {Math.round(layerOpacity.caviteSilangNDVI * 100)}%
                                        </span>
                                      </div>
                                      <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.1"
                                        value={layerOpacity.caviteSilangNDVI}
                                        onChange={(e) => onOpacityChange('caviteSilangNDVI', parseFloat(e.target.value))}
                                        className={`w-full h-1 sm:h-1.5 rounded-lg appearance-none cursor-pointer opacity-slider ${isDark ? 'bg-[#253347]' : 'bg-[#e5e7eb]'}`}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Ortho */}
                            <div
                              role="button"
                              tabIndex={0}
                              aria-pressed={layerVisibility.caviteSilangOrtho}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.currentTarget.click();
                                }
                              }}
                              className={`rounded-lg ${compactLayerPad} border transition-all duration-200 cursor-pointer outline-none ${
                                layerVisibility.caviteSilangOrtho ? lt.layerSelected : lt.layerIdle
                              }`}
                              onClick={() => {
                                if (layerVisibility.caviteSilangOrtho) {
                                  onLayerToggle('caviteSilangOrtho', false);
                                } else {
                                  onLayerToggle('caviteSilangOrtho', true);
                                }
                              }}
                            >
                              <div className="flex gap-2 sm:gap-2.5 items-start pointer-events-none">
                                <div
                                  className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-md overflow-hidden border ${
                                    isDark ? 'border-gray-600 bg-gray-900' : 'border-gray-200 bg-gray-100'
                                  }`}
                                  aria-hidden
                                >
                                  <img
                                    src={buildCaviteSilangPreviewUrl('ortho')}
                                    alt=""
                                    width={72}
                                    height={72}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1">
                                    <i className={`ri-image-2-line text-xs sm:text-sm flex-shrink-0 ${lt.sectionIcon}`}></i>
                                    <span
                                      className={`text-sm font-semibold truncate ${
                                        layerVisibility.caviteSilangOrtho ? lt.selectedText : lt.heading
                                      }`}
                                    >
                                      SilangFarmLeadsOct_Ortho.tif
                                    </span>
                                  </div>
                                  {layerVisibility.caviteSilangOrtho && (
                                    <div
                                      className="space-y-0.5 sm:space-y-1 pointer-events-auto"
                                      onClick={(e) => e.stopPropagation()}
                                      onKeyDown={(e) => e.stopPropagation()}
                                    >
                                      <div className="flex items-center justify-between">
                                        <span className={`text-sm ${lt.sectionIcon}`}>Opacity</span>
                                        <span className={`text-sm font-medium ${lt.selectedText}`}>
                                          {Math.round(layerOpacity.caviteSilangOrtho * 100)}%
                                        </span>
                                      </div>
                                      <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.1"
                                        value={layerOpacity.caviteSilangOrtho}
                                        onChange={(e) => onOpacityChange('caviteSilangOrtho', parseFloat(e.target.value))}
                                        className={`w-full h-1 sm:h-1.5 rounded-lg appearance-none cursor-pointer opacity-slider ${isDark ? 'bg-[#253347]' : 'bg-[#e5e7eb]'}`}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      )}

                      {/* Leads Agri drone outputs by province (registry-driven, see droneSites.ts) */}
                      {isDroneSitesAllowed(allowedEntitlementSet) &&
                        getDroneSitesByProvince().map(({ province }) =>
                          showNavSection(droneProvinceNavId(province)) ? (
                            <div key={province}>
                              {!pinnedSectionId && (
                                <p className={`mt-3 text-xs font-semibold uppercase tracking-wide ${lt.sectionIcon}`}>
                                  {province}
                                </p>
                              )}
                              <DroneSitesSection
                                province={province}
                                controls={droneSites}
                                isDark={isDark}
                                lt={lt}
                                compactSectionPad={compactSectionPad}
                                compactLayerPad={compactLayerPad}
                              />
                            </div>
                          ) : null
                        )}

                      {/* Drone Services Advertisement */}
                      <div className={`mt-3 sm:mt-4 p-3 sm:p-4 ${lt.section}`}>
                        <div className="flex items-start space-x-2 sm:space-x-3">
                          {/* Icon */}
                          <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center ${
                            lt.emptyIconWrap
                          }`}>
                            <i className={`ri-flight-takeoff-line ${lt.sectionIcon} text-lg sm:text-xl`}></i>
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1">
                            <h4 className={`text-xs sm:text-sm font-semibold mb-1 sm:mb-1.5 ${
                              lt.heading
                            }`}>
                              Need more detailed map analysis?
                            </h4>
                            <p className={`text-sm mb-2 sm:mb-3 ${
                              lt.muted
                            }`}>
                              Request for our drone services
                            </p>
                            
                            {/* Service Links */}
                            <div className="space-y-1.5 sm:space-y-2">
                              <button
                                type="button"
                                onClick={() => setDroneServiceType('mapping')}
                                className={`drone-service-cta-btn w-full py-1.5 sm:py-2 px-3 sm:px-4 rounded-md font-medium text-sm transition-all duration-200 flex items-center justify-center space-x-1.5 sm:space-x-2 ${lt.primaryBtn} border-0 text-white ${
                                  embedded
                                    ? 'shadow-md hover:shadow-lg'
                                    : 'shadow-sm hover:shadow-md'
                                }`}
                              >
                                <i className="ri-customer-service-2-line text-white"></i>
                                <span className="text-white">Need Drone Mapping?</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => setDroneServiceType('spraying')}
                                className={`drone-service-cta-btn w-full py-1.5 sm:py-2 px-3 sm:px-4 rounded-md font-medium text-sm transition-all duration-200 flex items-center justify-center space-x-1.5 sm:space-x-2 ${lt.primaryBtn} border-0 text-white ${
                                  embedded
                                    ? 'shadow-md hover:shadow-lg'
                                    : 'shadow-sm hover:shadow-md'
                                }`}
                              >
                                <i className="ri-customer-service-2-line text-white"></i>
                                <span className="text-white">Need Drone Spraying?</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {expandedTab === 'cropArea' && layerConfigs.cropArea.filter((config) => showNavSection(config.key)).map(renderCropAreaLayerItem)}

              {/* Forecast Tab Content */}
              {expandedTab === 'forecast' && (
                <div className={`${lt.section} ${isCompactEmbedded ? 'p-1.5 px-1.5' : 'p-3 sm:p-4'}`}>
                  {canShow('riceForecast') && (
                  <>
                  {/* Header */}
                  <div className={`flex items-center ${isCompactEmbedded ? 'gap-1.5 mb-2' : 'space-x-2 sm:space-x-3 mb-3 sm:mb-4'}`}>
                    <div className={`rounded-lg ${isCompactEmbedded ? 'p-1' : 'p-1.5 sm:p-2'} ${lt.emptyIconWrap}`}>
                      <i className={`ri-seedling-fill ${isCompactEmbedded ? 'text-base' : 'text-lg sm:text-xl'} ${lt.sectionIcon}`}></i>
                    </div>
                    <div>
                      <h4 className={`font-bold text-base ${lt.heading}`}>Rice Yield Forecast</h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        MODIS NDVI-based predictions
                      </p>
                    </div>
                  </div>

                  {!isCompactEmbedded && (
                    <p className={`text-sm mb-3 sm:mb-4 ${lt.muted} leading-relaxed`}>
                      Get rice yield predictions for Philippine regions using MODIS NDVI data
                    </p>
                  )}

                  {/* Month Selection */}
                  <div className={isCompactEmbedded ? 'mb-2' : 'mb-3 sm:mb-4'}>
                    <label className={`flex items-center font-semibold text-sm mb-1.5 ${lt.body}`}>
                      <i className={`ri-calendar-line mr-1 ${lt.sectionIcon}`}></i>
                      Select Month
                    </label>
                    <div className="relative">
                      <select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className={`w-full rounded-lg focus:ring-2 focus:ring-[#008540] focus:border-transparent transition-all pl-8 pr-3 py-2 text-sm rounded-lg ${
                          embedded 
                            ? lt.input 
                            : `${lt.input}`}
                        }`}
                      >
                        <option value="">Choose a month...</option>
                        {forecastMonths.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      <i className={`ri-arrow-down-s-line absolute top-1/2 -translate-y-1/2 ${lt.sectionIcon} pointer-events-none ${isCompactEmbedded ? 'left-2 text-sm' : 'left-2.5 sm:left-3'}`}></i>
                    </div>
                  </div>

                  {/* Region Selection */}
                  <div className={isCompactEmbedded ? 'mb-2' : 'mb-3 sm:mb-4'}>
                    <label className={`flex items-center font-semibold text-sm mb-1.5 ${lt.body}`}>
                      <i className={`ri-map-pin-line mr-1 ${lt.sectionIcon}`}></i>
                      Select Region
                    </label>
                    <div className="relative">
                      <select
                        value={selectedRegion}
                        onChange={(e) => setSelectedRegion(e.target.value)}
                        className={`w-full rounded-lg focus:ring-2 focus:ring-[#008540] focus:border-transparent transition-all pl-8 pr-3 py-2 text-sm rounded-lg ${
                          embedded 
                            ? lt.input 
                            : `${lt.input}`}
                        }`}
                      >
                        <option value="">Choose a region...</option>
                        {forecastRegions.map((region) => (
                          <option key={region} value={region}>
                            {region}
                          </option>
                        ))}
                      </select>
                      <i className={`ri-arrow-down-s-line absolute top-1/2 -translate-y-1/2 ${lt.sectionIcon} pointer-events-none ${isCompactEmbedded ? 'left-2 text-sm' : 'left-2.5 sm:left-3'}`}></i>
                    </div>
                  </div>

                  {/* Province (optional) — narrows AOI; required for GeoTIFF when region is “All Regions” */}
                  <div className={isCompactEmbedded ? 'mb-2' : 'mb-3 sm:mb-4'}>
                    <label className={`flex items-center font-semibold text-sm mb-1.5 ${lt.body}`}>
                      <i className={`ri-building-4-line mr-1 ${lt.sectionIcon}`}></i>
                      Province <span className={`ml-1 font-normal ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>(optional)</span>
                    </label>
                    <div className="relative">
                      <select
                        value={forecastSelectedProvince}
                        onChange={(e) => setForecastSelectedProvince(e.target.value)}
                        disabled={!selectedRegion || forecastProvincesLoading}
                        className={`w-full rounded-lg focus:ring-2 focus:ring-[#008540] focus:border-transparent transition-all disabled:opacity-60 pl-8 pr-3 py-2 text-sm rounded-lg ${
                          embedded
                            ? lt.input
                            : `${lt.input}`}
                        }`}
                      >
                        <option value="">
                          {forecastProvincesLoading ? 'Loading provinces…' : 'Whole region (no province filter)'}
                        </option>
                        {forecastProvinceOptions.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                      <i className={`ri-arrow-down-s-line absolute top-1/2 -translate-y-1/2 ${lt.sectionIcon} pointer-events-none ${isCompactEmbedded ? 'left-2 text-sm' : 'left-2.5 sm:left-3'}`}></i>
                    </div>
                  </div>

                  {/* Municipality (optional) — LGU-level AOI */}
                  <div className={isCompactEmbedded ? 'mb-2' : 'mb-3 sm:mb-4'}>
                    <label className={`flex items-center font-semibold text-sm mb-1.5 ${lt.body}`}>
                      <i className={`ri-community-line mr-1 ${lt.sectionIcon}`}></i>
                      Municipality <span className={`ml-1 font-normal ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>(optional)</span>
                    </label>
                    <div className="relative">
                      <select
                        value={forecastSelectedMunicipality}
                        onChange={(e) => setForecastSelectedMunicipality(e.target.value)}
                        disabled={!forecastSelectedProvince || forecastMunicipalitiesLoading}
                        className={`w-full rounded-lg focus:ring-2 focus:ring-[#008540] focus:border-transparent transition-all disabled:opacity-60 pl-8 pr-3 py-2 text-sm rounded-lg ${
                          embedded
                            ? lt.input
                            : `${lt.input}`}
                        }`}
                      >
                        <option value="">
                          {!forecastSelectedProvince
                            ? 'Select a province first…'
                            : forecastMunicipalitiesLoading
                              ? 'Loading municipalities…'
                              : 'Whole province (no municipality filter)'}
                        </option>
                        {forecastMunicipalityOptions.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                      <i className={`ri-arrow-down-s-line absolute top-1/2 -translate-y-1/2 ${lt.sectionIcon} pointer-events-none ${isCompactEmbedded ? 'left-2 text-sm' : 'left-2.5 sm:left-3'}`}></i>
                    </div>
                  </div>

                  {/* Error Message */}
                  {forecastError && (
                    <div className={`${isCompactEmbedded ? 'mb-2 p-1.5' : 'mb-3 sm:mb-4 p-2 sm:p-2.5'} rounded-lg text-sm flex items-center space-x-1.5 ${isDark ? 'bg-red-900/40 border border-red-700/50 text-red-300' : 'bg-red-50 border border-red-200 text-red-700 shadow-sm'}`}>
                      <i className="ri-error-warning-line text-xs"></i>
                      <span>{forecastError}</span>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    onClick={handleRiceForecastSubmit}
                    disabled={isForecastLoading || !selectedMonth || !selectedRegion}
                    className={`w-full px-3 font-semibold rounded-lg transition-all duration-200 flex items-center justify-center space-x-1.5 py-2.5 text-sm shadow-sm ${
                      isForecastLoading || !selectedMonth || !selectedRegion
                        ? embedded 
                          ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed shadow-none'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                        : isDark
                          ? lt.primaryBtn + ' hover:scale-[1.02] active:scale-[0.98]'
                          : lt.primaryBtn + ' hover:scale-[1.02] active:scale-[0.98]'
                    }`}
                  >
                    {isForecastLoading ? (
                      <>
                        <div className="animate-spin h-3 w-3 border-2 border-white rounded-full border-t-transparent"></div>
                        <span>Loading Forecast...</span>
                      </>
                    ) : (
                      <>
                        <i className={`ri-bar-chart-box-line ${isCompactEmbedded ? 'text-xs' : 'text-sm sm:text-base'}`}></i>
                        <span>Generate Forecast</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleDownloadForecastTif}
                    disabled={
                      isForecastTifDownloading ||
                      !selectedMonth ||
                      !selectedRegion ||
                      (selectedRegion === 'All Regions' && !forecastSelectedProvince)
                    }
                    title={
                      selectedRegion === 'All Regions' && !forecastSelectedProvince
                        ? 'Pick a province when using “All Regions”, or choose a specific region'
                        : 'Download forecast raster as GeoTIFF'
                    }
                    className={`w-full mt-1.5 px-3 font-semibold rounded-lg transition-all duration-200 flex items-center justify-center space-x-1.5 py-2.5 text-sm shadow-sm ${
                      isForecastTifDownloading ||
                      !selectedMonth ||
                      !selectedRegion ||
                      (selectedRegion === 'All Regions' && !forecastSelectedProvince)
                        ? isDark
                          ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed shadow-none'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                        : isDark
                          ? lt.toggleOff + ' ' + lt.selectedText + ' active:scale-[0.98]'
                          : lt.toggleOff + ' ' + lt.selectedText + ' active:scale-[0.98]'
                    }`}
                  >
                    {isForecastTifDownloading ? (
                      <>
                        <div className="animate-spin h-3 w-3 border-2 border-current rounded-full border-t-transparent"></div>
                        <span>Preparing download...</span>
                      </>
                    ) : (
                      <>
                        <i className={`ri-download-cloud-2-line ${isCompactEmbedded ? 'text-xs' : 'text-sm sm:text-base'}`}></i>
                        <span>Download Forecast TIF</span>
                      </>
                    )}
                  </button>

                  <div className={`grid grid-cols-2 ${isCompactEmbedded ? 'mt-1.5 gap-1.5' : 'mt-2 gap-2'}`}>
                    <button
                      type="button"
                      onClick={handleExportForecastCsv}
                      disabled={!forecastResults}
                      className={`rounded-lg font-medium transition-all flex items-center justify-center gap-1 py-2 px-2 text-sm ${
                        !forecastResults
                          ? isDark
                            ? 'bg-gray-700/40 text-gray-600 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : isDark
                            ? lt.toggleOff + ' ' + lt.selectedText
                            : lt.toggleOff + ' ' + lt.selectedText
                      }`}
                    >
                      <i className="ri-file-text-line"></i>
                      CSV
                    </button>
                    <button
                      type="button"
                      onClick={handleExportForecastJson}
                      disabled={!forecastResults}
                      className={`rounded-lg font-medium transition-all flex items-center justify-center gap-1 py-2 px-2 text-sm ${
                        !forecastResults
                          ? isDark
                            ? 'bg-gray-700/40 text-gray-600 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : isDark
                            ? lt.toggleOff + ' ' + lt.selectedText
                            : lt.toggleOff + ' ' + lt.selectedText
                      }`}
                    >
                      <i className="ri-braces-line"></i>
                      JSON
                    </button>
                  </div>

                  {/* Info */}
                  {!isCompactEmbedded && (
                    <div className={`mt-3 sm:mt-4 pt-3 sm:pt-4 border-t ${isDark ? 'border-gray-700/50 text-gray-400' : 'border-gray-200 text-gray-500'} text-sm flex items-start space-x-2`}>
                      <i className={`ri-information-line ${lt.sectionIcon} mt-0.5`}></i>
                      <span>Forecast data includes yield predictions, visualizations, and statistical summaries</span>
                    </div>
                  )}
                  </>
                  )}
                </div>
              )}

              {/* Disaster Loss & Damages Tab Content */}
              {expandedTab === 'disasterLossDamage' && (
                <div className="space-y-1.5 sm:space-y-2">
                  {/* Flood L&D Section */}
                  <div className={`${lt.section} ${compactCardPad}`}>
                    <div className="flex items-center space-x-1.5 sm:space-x-2 mb-2 sm:mb-3">
                      <i className={`ri-flood-line text-base sm:text-lg ${lt.sectionIcon}`}></i>
                      <h4 className={`layer-section-title font-semibold min-w-0 text-base ${lt.heading}`}>Typhoon and Drought Loss & Damage Estimates</h4>
                      <button
                        type="button"
                        title="About Total, CI low, and CI high"
                        aria-label="About Total, CI low, and CI high"
                        aria-expanded={showFloodDamageInfo}
                        onClick={() => setShowFloodDamageInfo(true)}
                        className={`shrink-0 p-0.5 rounded-full transition-colors ${lg.infoBtn}`}
                      >
                        <i className="ri-information-line text-sm"></i>
                      </button>
                    </div>

                    {floodDamageEvents.length > 0 && (
                      <div className="mb-2 sm:mb-3">
                        <p className={`mb-1 text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Flood event
                        </p>
                        <div
                          className={`flex p-0.5 rounded-[10px] ${isDark ? 'bg-[#161b22]' : 'bg-[#f3f4f6]'}`}
                          role="tablist"
                          aria-label="Flood event"
                        >
                          {floodDamageEvents.map((event) => {
                            const isActive = event.key === activeFloodDamageEventKey;
                            return (
                              <button
                                key={event.key}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                onClick={() => handleFloodDamageEventChange(event.key)}
                                className={`flex-1 min-w-0 rounded-[8px] px-2 py-1.5 text-center transition-[background-color,color] duration-150 ease-out active:scale-[0.98] ${
                                  isActive
                                    ? lt.primaryBtn
                                    : isDark
                                      ? 'text-[#8b949e] hover:text-[#e6edf3]'
                                      : 'text-[#6b7280] hover:text-[#111827]'
                                }`}
                              >
                                <span className={`block truncate text-sm font-semibold`}>
                                  {event.label}
                                </span>
                                <span className={`block truncate text-xs ${isActive ? 'text-white/80' : lt.muted}`}>
                                  {event.dateLabel ? `${event.dateLabel} · ${event.count}` : event.count}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  {/* Search Box */}
                  <div className="mb-2 sm:mb-3">
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search municipality..."
                        className={`w-full px-2 sm:px-3 py-1.5 sm:py-2 pl-7 sm:pl-8 text-sm rounded-[10px] border ${lt.input}`}
                      />

                    </div>
                  </div>

                  {visibleFloodDamageMunicipalities.length > 0 ? (
                  <div className="max-h-[300px] sm:max-h-[400px] overflow-y-auto">
                    <table className="w-full table-fixed border-collapse">
                      <thead className={`sticky top-0 z-10 ${isDark ? 'bg-[#1c2432]/95 backdrop-blur-sm' : 'bg-white/95 backdrop-blur-sm'}`}>
                        <tr className={`text-xs uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          <th className={`text-left font-medium py-1.5 pl-1.5 pr-1 w-[38%] ${isDark ? 'border-b border-gray-700' : 'border-b border-gray-200'}`}>Municipality</th>
                          {([
                            ['total', 'w-[22%] px-1'] as const,
                            ['ciLow', 'w-[20%] px-1'] as const,
                            ['ciHigh', 'w-[20%] pl-1 pr-1'] as const,
                          ]).map(([key, pad]) => {
                            const col = FLOOD_DAMAGE_COLUMN_HELP[key];
                            return (
                              <th
                                key={key}
                                title={col.title}
                                className={`text-right font-medium py-1.5 ${pad} ${isDark ? 'border-b border-gray-700' : 'border-b border-gray-200'}`}
                              >
                                <span className="inline-flex items-center justify-end gap-0.5">
                                  {col.label}
                                  <button
                                    type="button"
                                    title={col.title}
                                    aria-label={col.title}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setShowFloodDamageInfo(true);
                                    }}
                                    className={`p-0 rounded-full transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                                  >
                                    <i className="ri-information-line text-[10px] leading-none"></i>
                                  </button>
                                </span>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                    {visibleFloodDamageMunicipalities.map((municipality) => {
                      const row = municipality.data[0];
                      const isSelected = selectedFloodDamageId != null && municipality.id === selectedFloodDamageId;
                      return (
                        <tr
                          key={municipality.id}
                          onClick={() => {
                            if (isSelected) onFloodDamageSelect?.(null);
                            else onFloodDamageSelect?.(municipality);
                          }}
                          className={`cursor-pointer transition-[background-color] duration-150 ease-out ${
                            isSelected
                              ? isDark
                                ? 'bg-blue-900/70'
                                : 'bg-blue-100'
                              : isDark
                                ? 'hover:bg-gray-700/80'
                                : 'hover:bg-blue-50'
                          }`}
                        >
                          <td className={`py-1.5 pl-1.5 pr-1 text-sm truncate ${
                            isSelected
                              ? isDark ? 'text-blue-100 font-semibold' : 'text-blue-900 font-semibold'
                              : lt.heading
                          }`}>
                            <span className="inline-flex items-center gap-1 min-w-0">
                              {municipality.raster && (
                                <i
                                  className={`ri-stack-line shrink-0 text-[10px] ${isSelected ? (isDark ? 'text-blue-300' : 'text-blue-600') : (isDark ? 'text-blue-400' : 'text-blue-500')}`}
                                  title={`${municipality.raster.province} damage raster`}
                                />
                              )}
                              <span className="truncate">{municipality.municipalityName}</span>
                            </span>
                          </td>
                          <td className={`py-1.5 px-1 text-right text-sm tabular-nums ${isSelected ? (isDark ? 'text-blue-200' : 'text-blue-800') : (isDark ? 'text-gray-300' : 'text-gray-600')}`} title={formatFloodDamagePhpExact(row?.TotalDamage)}>
                            {formatFloodDamagePhp(row?.TotalDamage)}
                          </td>
                          <td className={`py-1.5 px-1 text-right text-sm tabular-nums ${isSelected ? (isDark ? 'text-blue-200' : 'text-blue-800') : (isDark ? 'text-gray-400' : 'text-gray-500')}`} title={formatFloodDamagePhpExact(row?.CI_Lower)}>
                            {formatFloodDamagePhp(row?.CI_Lower)}
                          </td>
                          <td className={`py-1.5 pl-1 pr-1.5 text-right text-sm tabular-nums ${isSelected ? (isDark ? 'text-blue-200' : 'text-blue-800') : (isDark ? 'text-gray-400' : 'text-gray-500')}`} title={formatFloodDamagePhpExact(row?.CI_Upper)}>
                            {formatFloodDamagePhp(row?.CI_Upper)}
                          </td>
                        </tr>
                      );
                    })}
                      </tbody>
                    </table>
                  </div>
                  ) : (
                    <div className="text-center py-6 sm:py-8">
                      <i className={`ri-search-line text-2xl sm:text-3xl mb-1.5 sm:mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}></i>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No municipalities found</p>
                    </div>
                  )}

                    <div className={`mt-2 sm:mt-3 pt-2 sm:pt-3 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                      <i className="ri-information-line mr-1"></i>
                      {availableFloodDamageMunicipalities.length} municipali{availableFloodDamageMunicipalities.length === 1 ? 'ty' : 'ties'} · click a row to show its damage raster
                    </div>
                  </div>

         
                </div>
              )}

              {/* Low Carbon Rice Tab Content */}
              {expandedTab === 'lowCarbon' && (
                <div className="space-y-1.5 sm:space-y-2">
                  {/* AWD maps (coming soon!) */}
                  {showNavSection('awd') && (
                  <div className={`${lt.section} ${compactCardPad}`}>
                    <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                      <div className="flex items-center space-x-1.5 sm:space-x-2">
                        <i className={`ri-drop-line text-base sm:text-lg ${lt.sectionIcon}`}></i>
                        <h4 className={`layer-section-title font-semibold text-base ${lt.heading}`}>AWD maps</h4>
                      </div>
                      <span className={`text-sm font-medium px-1.5 py-0.5 rounded-full ${lt.featured}`}>Coming soon!</span>
                    </div>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Alternate Wetting and Drying (AWD) maps will be available here.
                    </p>
                  </div>
                  )}

                  {/* Rice Straw Burning Section */}
                  {showNavSection('straw') && (
                  <div className={`${lt.section} ${compactCardPad}`}>
                    <div className="flex items-center space-x-1.5 sm:space-x-2 mb-2 sm:mb-3">
                      <i className={`ri-fire-line text-base sm:text-lg ${lt.sectionIcon}`}></i>
                      <h4 className={`layer-section-title font-semibold text-base ${lt.heading}`}>Rice Straw Burning</h4>
                      <button
                        type="button"
                        title="About rice straw burning detection"
                        aria-label="About rice straw burning detection"
                        aria-expanded={showStrawBurnInfo}
                        onClick={() => setShowStrawBurnInfo(true)}
                        className={`p-0.5 rounded-full transition-colors ${lg.infoBtn}`}
                      >
                        <i className="ri-information-line text-sm"></i>
                      </button>
                  </div>

                  <p className={`text-sm mb-2 sm:mb-3 ${lt.muted}`}>
                    Detect and monitor rice straw burning by province or municipality
                  </p>

                  {/* Province vs municipality scope */}
                  <div
                    className={`mb-2 sm:mb-3 flex flex-wrap gap-2 sm:gap-3 text-sm ${lt.body}`}
                    role="radiogroup"
                    aria-label="Analysis area"
                  >
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="strawBurnScope"
                        checked={strawBurnScope === 'province'}
                        onChange={() => setStrawBurnScope('province')}
                        disabled={strawBurnIsLoading}
                        className="accent-red-600"
                      />
                      <span>Province (whole area)</span>
                    </label>
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="strawBurnScope"
                        checked={strawBurnScope === 'municipality'}
                        onChange={() => setStrawBurnScope('municipality')}
                        disabled={strawBurnIsLoading}
                        className="accent-red-600"
                      />
                      <span>Municipality</span>
                    </label>
                  </div>

                  {/* Province Selection */}
                  <div className="mb-2 sm:mb-3">
                    <label className={`block text-sm font-medium mb-0.5 sm:mb-1 ${lt.body}`}>
                      Select Province
                    </label>
                    {isLoadingProvinces ? (
                      <div className={`flex items-center justify-center py-1.5 sm:py-2 rounded-md border ${isDark ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-200'}`}>
                        <div className="animate-spin h-2.5 w-2.5 sm:h-3 sm:w-3 border-2 border-red-500 rounded-full border-t-transparent mr-1.5 sm:mr-2"></div>
                        <span className={`text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}>Loading provinces...</span>
                      </div>
                    ) : (
                      <select
                        value={selectedProvince}
                        onChange={(e) => {
                          setSelectedProvince(e.target.value);
                          setSelectedStrawBurnMunicipality('');
                        }}
                        disabled={strawBurnIsLoading}
                        className={`w-full px-1.5 sm:px-2 py-1.5 sm:py-2 text-sm rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white hover:border-red-400' : 'border border-gray-300 bg-white hover:border-red-400'}`}
                      >
                        <option value="">Choose a province...</option>
                        {provinces.map((province, index) => (
                          <option key={`${province}-${index}`} value={province}>
                            {province}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Municipality Selection — only when analyzing a single LGU (hidden for province-wide analysis) */}
                  {strawBurnScope === 'municipality' && (
                    <div className="mb-2 sm:mb-3">
                      <label className={`block text-sm font-medium mb-0.5 sm:mb-1 flex items-center justify-between ${lt.body}`}>
                        <span>Select Municipality</span>
                        {municipalities.length > 0 && (
                          <span className={`text-sm font-normal ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            ({municipalities.length} available)
                          </span>
                        )}
                      </label>
                      {isLoadingMunicipalities ? (
                        <div className={`flex items-center justify-center py-1.5 sm:py-2 rounded-md border ${isDark ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-200'}`}>
                          <div className="animate-spin h-2.5 w-2.5 sm:h-3 sm:w-3 border-2 border-red-500 rounded-full border-t-transparent mr-1.5 sm:mr-2"></div>
                          <span className={`text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}>Loading municipalities...</span>
                        </div>
                      ) : !selectedProvince ? (
                        <div className={`py-1.5 sm:py-2 px-2 sm:px-3 rounded-md border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                          <i className="ri-information-line mr-1"></i>
                          Please select a province first
                        </div>
                      ) : municipalities.length === 0 ? (
                        <div className={`py-1.5 sm:py-2 px-2 sm:px-3 rounded-md border text-sm ${isDark ? 'bg-yellow-900/30 border-yellow-700 text-yellow-300' : 'bg-yellow-50 border-yellow-200 text-yellow-700'}`}>
                          <i className="ri-error-warning-line mr-1"></i>
                          No municipalities available for this province
                        </div>
                      ) : (
                        <select
                          value={selectedStrawBurnMunicipality}
                          onChange={(e) => setSelectedStrawBurnMunicipality(e.target.value)}
                          className={`w-full px-1.5 sm:px-2 py-1.5 sm:py-2 text-sm rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white hover:border-red-400' : 'border border-gray-300 bg-white hover:border-red-400'}`}
                          disabled={strawBurnIsLoading}
                        >
                          <option value="">Choose a municipality...</option>
                          {municipalities.map((municipality, index) => (
                            <option key={`${municipality}-${index}`} value={municipality}>
                              {municipality}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  {/* Date Range Section */}
                  <div className="mb-2 sm:mb-3">
                    <div className={`text-sm font-medium mb-1.5 sm:mb-2 flex items-center gap-1 ${lt.body}`}>
                      <i className={`ri-calendar-line ${isDark ? 'text-red-400' : 'text-red-600'}`}></i>
                      Analysis Period
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                      <div>
                        <label className={`text-sm block mb-0.5 sm:mb-1 font-medium ${lt.muted}`}>Start Date</label>
                        <input
                          type="date"
                          value={strawBurnStartDate}
                          onChange={(e) => {
                            const next = onStartDateChange(
                              e.target.value,
                              strawBurnEndDate,
                              strawBurnStartDate
                            );
                            setStrawBurnStartDate(next.start);
                            setStrawBurnEndDate(next.end);
                          }}
                          disabled={strawBurnIsLoading}
                          className={`w-full text-sm px-1.5 sm:px-2 py-1 sm:py-1.5 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'bg-gray-700 border-gray-600 text-white disabled:bg-[#1c2432]' : 'border border-gray-300 disabled:bg-gray-100'}`}
                        />
                      </div>
                      <div>
                        <label className={`text-sm block mb-0.5 sm:mb-1 font-medium ${lt.muted}`}>End Date</label>
                        <input
                          type="date"
                          value={strawBurnEndDate}
                          onChange={(e) => {
                            const next = onEndDateChange(
                              strawBurnStartDate,
                              e.target.value,
                              strawBurnEndDate
                            );
                            setStrawBurnStartDate(next.start);
                            setStrawBurnEndDate(next.end);
                          }}
                          disabled={strawBurnIsLoading}
                          className={`w-full text-sm px-1.5 sm:px-2 py-1 sm:py-1.5 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'bg-gray-700 border-gray-600 text-white disabled:bg-[#1c2432]' : 'border border-gray-300 disabled:bg-gray-100'}`}
                        />
                      </div>
                    </div>
                    <div className={`mt-0.5 sm:mt-1 text-xs flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      <i className="ri-information-line"></i>
                      Default: March 1 - April 30, {currentYear} (Harvest season)
                    </div>
                  </div>

                  {/* Get Data Button */}
                  <button
                    onClick={() => {
                      if (!onStrawBurnDataFetch) return;
                      if (strawBurnScope === 'province') {
                        if (!selectedProvince) return;
                        const payload: StrawBurnFetchPayload = {
                          mode: 'province',
                          province_name: selectedProvince,
                        };
                        if (strawBurnStartDate) payload.start_date = strawBurnStartDate;
                        if (strawBurnEndDate) payload.end_date = strawBurnEndDate;
                        onStrawBurnDataFetch(payload);
                        return;
                      }
                      if (!selectedStrawBurnMunicipality) return;
                      const payload: StrawBurnFetchPayload = {
                        mode: 'municipality',
                        municipality_name: selectedStrawBurnMunicipality,
                      };
                      if (strawBurnStartDate) payload.start_date = strawBurnStartDate;
                      if (strawBurnEndDate) payload.end_date = strawBurnEndDate;
                      onStrawBurnDataFetch(payload);
                    }}
                    disabled={
                      strawBurnIsLoading ||
                      !selectedProvince ||
                      (strawBurnScope === 'municipality' && !selectedStrawBurnMunicipality)
                    }
                    className={`w-full py-1.5 sm:py-2 px-2 sm:px-3 text-sm font-medium rounded-md transition-colors ${
                      strawBurnIsLoading ||
                      !selectedProvince ||
                      (strawBurnScope === 'municipality' && !selectedStrawBurnMunicipality)
                        ? embedded 
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                  >
                    {strawBurnIsLoading ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin h-2.5 w-2.5 sm:h-3 sm:w-3 border-2 border-white rounded-full border-t-transparent mr-1.5 sm:mr-2"></div>
                        Loading Data...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <i className="ri-download-line mr-1"></i>
                        Get Data
                      </span>
                    )}
                  </button>

                  {/* Transparency — same pattern as farm-level heat map */}
                  {strawBurnData?.success && strawBurnData?.tile_url && (
                    <div
                      className={`mb-2 sm:mb-3 ${compactCardPad} rounded-lg ${isDark ? 'bg-gray-700/50 border border-gray-600' : 'bg-red-50 border border-red-100'}`}
                    >
                      <label
                        className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-800'}`}
                      >
                        Rice Straw Burning Transparency:{' '}
                        {Math.round((layerOpacity.strawBurn ?? 1) * 100)}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={layerOpacity.strawBurn ?? 1}
                        onChange={(e) =>
                          onOpacityChange('strawBurn', parseFloat(e.target.value))
                        }
                        className={`w-full h-1.5 sm:h-2 rounded-lg appearance-none cursor-pointer opacity-slider ${isDark ? 'bg-gray-600' : 'bg-gray-200'}`}
                      />
                      <div
                        className={`flex justify-between text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}
                      >
                        <span>0% (Hidden)</span>
                        <span>100% (Solid)</span>
                      </div>
                    </div>
                  )}

                    {/* Info */}
                    <div className={`mt-3 pt-3 border-t text-xs ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                      <i className="ri-information-line mr-1"></i>
                      Choose province (and municipality if using municipality mode), then click &quot;Get Data&quot;
                    </div>
                  </div>
                  )}

                </div>
              )}

              {expandedTab === 'fieldServices' && showNavSection('fieldSensorCh4') && (
                <FieldServiceRequestCta
                  iconClass="ri-base-station-line"
                  title="CH4 & AWD field sensors"
                  description="Track localized methane and alternate wetting and drying with in-field sensors."
                  buttons={[{ label: 'Request CH4 & AWD sensors', serviceType: 'ch4_awd' }]}
                  onRequest={setDroneServiceType}
                  lt={lt}
                  embedded={embedded}
                />
              )}
              {expandedTab === 'fieldServices' && showNavSection('fieldSensorSoil') && (
                <FieldServiceRequestCta
                  iconClass="ri-plant-line"
                  title="Soil & nutrient sensors"
                  description="Monitor soil health and nutrients for your project area."
                  buttons={[{ label: 'Request soil & nutrient sensors', serviceType: 'soil_nutrient' }]}
                  onRequest={setDroneServiceType}
                  lt={lt}
                  embedded={embedded}
                />
              )}

              {/* Tree Detection & Crown Segmentation Tab Content */}
              {expandedTab === 'treeDetection' && (
                <div className="space-y-3">
                  {/* Tree Detection Section - Collapsible */}
                  {showNavSection('trees') && canShowTreeInferenceTree && (
                  <div className={lt.section}>
                    <button
                      onClick={() => setIsTreeDetectionExpanded(!isTreeDetectionExpanded)}
                      className={`layer-section-header w-full flex items-center justify-between p-3 transition-colors rounded-t-[14px] ${lt.sectionHover}`}
                    >
                      <div className="flex items-center space-x-2">
                        <i className={`ri-tree-line text-lg ${lt.sectionIcon}`}></i>
                        <h4 className={`text-sm font-semibold ${lt.heading}`}>Tree Detection</h4>
                      </div>
                      <i className={`ri-arrow-${isTreeDetectionExpanded ? 'up' : 'down'}-s-line ${lt.chevron}`}></i>
                    </button>

                    {isTreeDetectionExpanded && (
                      <div className="p-3 pt-0">
                        <p className={`mb-3 text-xs leading-snug ${lt.muted}`}>
                          Find individual trees. Results show on the map.
                        </p>
                        <AiSourceChoices
                          isDark={isDark}
                          mode={treeDetectionInputMode}
                          disabled={isTreeDetectionLoading || isCrownSegmentationLoading || isCropfieldDelineationLoading}
                          mapDetail="Use the farm you can see right now"
                          fileDetail="Use a photo you already have"
                          accept="image/*,.tif,.tiff"
                          fileName={treeDetectionImage?.name ?? null}
                          mapReady={Boolean(getOlMap?.())}
                          zoomReady={isFarmLevelMapZoom}
                          onModeChange={(mode) => {
                            setTreeDetectionInputMode(mode);
                            if (mode === 'mapView') setTreeDetectionImage(null);
                                  setTreeDetectionError('');
                                }}
                          onFile={(file) => {
                                  setTreeDetectionImage(file);
                                  setTreeDetectionError('');
                          }}
                        />

                  {/* Error Message */}
                  {treeDetectionError && (
                    <div className={`mb-3 p-2 rounded text-xs border ${isDark ? 'bg-red-900/30 text-red-300 border-red-700' : 'bg-red-50 text-red-600 border-red-200'}`}>
                      <i className="ri-error-warning-line mr-1"></i>
                      {treeDetectionError}
                    </div>
                  )}

                  {(isCrownSegmentationLoading || isCropfieldDelineationLoading) && !isTreeDetectionLoading && (
                    <p className={`mb-3 text-xs ${lt.muted}`}>
                      Another tool is running — finish it before starting tree detection.
                    </p>
                  )}

                  {/* Animated steps when loading */}
                  {isTreeDetectionLoading && (
                    <div className={`mb-3 p-3 space-y-2.5 text-xs ${lt.infoNote}`}>
                      <div className={`flex items-center gap-2 ${treeDetectionStep === 'uploading' ? lt.selectedText : (isDark ? 'text-gray-400' : 'text-gray-500')}`}>
                        {treeDetectionStep === 'uploading' ? (
                          <div className="animate-spin h-3.5 w-3.5 border-2 border-current rounded-full border-t-transparent flex-shrink-0" />
                        ) : (
                          <i className={`ri-checkbox-circle-fill ${lt.sectionIcon} flex-shrink-0`} />
                        )}
                        <span className={treeDetectionStep === 'uploading' ? 'font-medium animate-pulse' : ''}>
                          {treeDetectionInputMode === 'mapView'
                            ? 'Preparing from map view...'
                            : 'Uploading your file...'}
                        </span>
                      </div>
                      <div className={`flex items-center gap-2 ${treeDetectionStep === 'detecting' ? lt.selectedText : (isDark ? 'text-gray-400' : 'text-gray-500')}`}>
                        {treeDetectionStep === 'detecting' ? (
                          <div className="animate-spin h-3.5 w-3.5 border-2 border-current rounded-full border-t-transparent flex-shrink-0" />
                        ) : treeDetectionStep === 'uploading' ? (
                          <div className="h-3.5 w-3.5 rounded-full border-2 border-gray-400 flex-shrink-0" />
                        ) : (
                          <i className={`ri-checkbox-circle-fill ${lt.sectionIcon} flex-shrink-0`} />
                        )}
                        <span className={treeDetectionStep === 'detecting' ? 'font-medium animate-pulse' : ''}>
                          {treeDetectionInputMode === 'mapView' || isTifFile(treeDetectionImage)
                            ? 'Detecting trees... Large areas may take longer.'
                            : 'Detecting trees in your image...'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    onClick={handleTreeDetectionSubmit}
                    disabled={
                      isTreeDetectionLoading ||
                      (treeDetectionInputMode === 'file' && !treeDetectionImage) ||
                      (treeDetectionInputMode === 'mapView' && (!getOlMap?.() || !isFarmLevelMapZoom)) ||
                      isCrownSegmentationLoading ||
                      isCropfieldDelineationLoading
                    }
                    className={`w-full py-2 px-3 text-xs font-medium rounded-md transition-colors ${
                      isTreeDetectionLoading ||
                      (treeDetectionInputMode === 'file' && !treeDetectionImage) ||
                      (treeDetectionInputMode === 'mapView' && (!getOlMap?.() || !isFarmLevelMapZoom)) ||
                      isCrownSegmentationLoading ||
                      isCropfieldDelineationLoading
                        ? embedded 
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : lt.primaryBtn
                    }`}
                  >
                    {isTreeDetectionLoading ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin h-3 w-3 border-2 border-white rounded-full border-t-transparent mr-2"></div>
                        {treeDetectionStep === 'uploading'
                          ? treeDetectionInputMode === 'mapView'
                            ? 'Preparing view...'
                            : 'Uploading...'
                          : 'Finding trees...'}
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <i className="ri-search-line mr-1"></i>
                        Find trees
                      </span>
                    )}
                  </button>
                  {isTreeDetectionLoading && (
                    <CancelAnalysisButton isDark={isDark} onClick={cancelTreeDetection} />
                  )}
                  {!isTreeDetectionLoading && treeDetectionInputMode === 'file' && !treeDetectionImage && (
                    <p className={`mt-2 text-sm ${lt.muted}`}>Choose a file, then you can find the trees.</p>
                  )}
                </div>
              )}
            </div>
                  )}

                  {/* Crown Segmentation Section - Collapsible */}
                  {showNavSection('crowns') && canShowTreeInferenceCrown && (
                  <div className={lt.section}>
                    <button
                      onClick={() => setIsCrownSegmentationExpanded(!isCrownSegmentationExpanded)}
                      className={`layer-section-header w-full flex items-center justify-between p-3 transition-colors rounded-t-[14px] ${lt.sectionHover}`}
                    >
                      <div className="flex items-center space-x-2">
                        <i className={`ri-scissors-cut-line text-lg ${lt.sectionIcon}`}></i>
                        <h4 className={`text-sm font-semibold ${lt.heading}`}>Crown Segmentation</h4>
                      </div>
                      <i className={`ri-arrow-${isCrownSegmentationExpanded ? 'up' : 'down'}-s-line ${lt.chevron}`}></i>
                    </button>

                    {isCrownSegmentationExpanded && (
                      <div className="p-3 pt-0">
                        <p className={`mb-3 text-xs leading-snug ${lt.muted}`}>
                          Outline each tree crown. Results show on the map.
                        </p>
                        <AiSourceChoices
                          isDark={isDark}
                          mode={crownSegmentationInputMode}
                          disabled={isCrownSegmentationLoading || isTreeDetectionLoading || isCropfieldDelineationLoading}
                          mapDetail="Use the farm you can see right now"
                          fileDetail="Use a photo you already have"
                          accept="image/*,.tif,.tiff"
                          fileName={crownSegmentationImage?.name ?? null}
                          mapReady={Boolean(getOlMap?.())}
                          zoomReady={isFarmLevelMapZoom}
                          onModeChange={(mode) => {
                            setCrownSegmentationInputMode(mode);
                            if (mode === 'mapView') setCrownSegmentationImage(null);
                                  setCrownSegmentationError('');
                                }}
                          onFile={(file) => {
                                  setCrownSegmentationImage(file);
                                  setCrownSegmentationError('');
                          }}
                        />

                  {/* Error Message */}
                  {crownSegmentationError && (
                    <div className={`mb-3 p-2 rounded text-xs border ${isDark ? 'bg-red-900/30 text-red-300 border-red-700' : 'bg-red-50 text-red-600 border-red-200'}`}>
                      <i className="ri-error-warning-line mr-1"></i>
                      {crownSegmentationError}
                    </div>
                  )}

                  {(isTreeDetectionLoading || isCropfieldDelineationLoading) && !isCrownSegmentationLoading && (
                    <p className={`mb-3 text-xs ${lt.muted}`}>
                      Another tool is running — finish it before starting crown segmentation.
                    </p>
                  )}

                  {/* Animated steps when loading */}
                  {isCrownSegmentationLoading && (
                    <div className={`mb-3 p-3 space-y-2.5 text-xs ${lt.infoNote}`}>
                      <div className={`flex items-center gap-2 ${crownSegmentationStep === 'uploading' ? lt.selectedText : (isDark ? 'text-gray-400' : 'text-gray-500')}`}>
                        {crownSegmentationStep === 'uploading' ? (
                          <div className="animate-spin h-3.5 w-3.5 border-2 border-current rounded-full border-t-transparent flex-shrink-0" />
                        ) : (
                          <i className={`ri-checkbox-circle-fill ${lt.sectionIcon} flex-shrink-0`} />
                        )}
                        <span className={crownSegmentationStep === 'uploading' ? 'font-medium animate-pulse' : ''}>
                          {crownSegmentationInputMode === 'mapView'
                            ? 'Preparing from map view...'
                            : 'Uploading your file...'}
                        </span>
                      </div>
                      <div className={`flex items-center gap-2 ${crownSegmentationStep === 'segmenting' ? lt.selectedText : (isDark ? 'text-gray-400' : 'text-gray-500')}`}>
                        {crownSegmentationStep === 'segmenting' ? (
                          <div className="animate-spin h-3.5 w-3.5 border-2 border-current rounded-full border-t-transparent flex-shrink-0" />
                        ) : crownSegmentationStep === 'uploading' ? (
                          <div className="h-3.5 w-3.5 rounded-full border-2 border-gray-400 flex-shrink-0" />
                        ) : (
                          <i className={`ri-checkbox-circle-fill ${lt.sectionIcon} flex-shrink-0`} />
                        )}
                        <span className={crownSegmentationStep === 'segmenting' ? 'font-medium animate-pulse' : ''}>
                          {crownSegmentationInputMode === 'mapView' || isTifFile(crownSegmentationImage)
                            ? 'Segmenting crowns... Large areas may take longer.'
                            : 'Segmenting crowns in your image...'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    onClick={handleCrownSegmentationSubmit}
                    disabled={
                      isCrownSegmentationLoading ||
                      (crownSegmentationInputMode === 'file' && !crownSegmentationImage) ||
                      (crownSegmentationInputMode === 'mapView' && (!getOlMap?.() || !isFarmLevelMapZoom)) ||
                      isTreeDetectionLoading ||
                      isCropfieldDelineationLoading
                    }
                    className={`w-full py-2 px-3 text-xs font-medium rounded-md transition-colors ${
                      isCrownSegmentationLoading ||
                      (crownSegmentationInputMode === 'file' && !crownSegmentationImage) ||
                      (crownSegmentationInputMode === 'mapView' && (!getOlMap?.() || !isFarmLevelMapZoom)) ||
                      isTreeDetectionLoading ||
                      isCropfieldDelineationLoading
                        ? embedded 
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : lt.primaryBtn
                    }`}
                  >
                    {isCrownSegmentationLoading ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin h-3 w-3 border-2 border-white rounded-full border-t-transparent mr-2"></div>
                        {crownSegmentationStep === 'uploading'
                          ? crownSegmentationInputMode === 'mapView'
                            ? 'Preparing view...'
                            : 'Uploading...'
                          : 'Outlining crowns...'}
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <i className="ri-scissors-line mr-1"></i>
                        Outline crowns
                      </span>
                    )}
                  </button>
                  {isCrownSegmentationLoading && (
                    <CancelAnalysisButton isDark={isDark} onClick={cancelCrownSegmentation} />
                  )}
                  {!isCrownSegmentationLoading && crownSegmentationInputMode === 'file' && !crownSegmentationImage && (
                    <p className={`mt-2 text-sm ${lt.muted}`}>Choose a file, then you can outline the crowns.</p>
                  )}
                      </div>
                    )}
                  </div>
                  )}

                  {/* Field boundary delineation — same layout as Tree / Crown */}
                  {showNavSection('fields') && canShowTreeInferenceField && (
                  <div className={lt.section}>
                    <button
                      type="button"
                      onClick={() => setIsCropfieldDelineationExpanded(!isCropfieldDelineationExpanded)}
                      className={`layer-section-header w-full flex items-center justify-between p-3 transition-colors rounded-t-[14px] ${lt.sectionHover}`}
                    >
                      <div className="flex items-center space-x-2">
                        <i className={`ri-shape-2-line text-lg ${lt.sectionIcon}`}></i>
                        <h4 className={`text-sm font-semibold ${lt.heading}`}>
                          Field boundary delineation
                        </h4>
                      </div>
                      <i className={`ri-arrow-${isCropfieldDelineationExpanded ? 'up' : 'down'}-s-line ${lt.chevron}`}></i>
                    </button>

                    {isCropfieldDelineationExpanded && (
                      <div className="p-3 pt-0">
                        <p className={`mb-3 text-xs leading-snug ${lt.muted}`}>
                          Draw the edges of fields. Results show on the map.
                        </p>
                        <AiSourceChoices
                          isDark={isDark}
                          mode={cropfieldDelineationInputMode}
                          disabled={isCropfieldDelineationLoading || isTreeDetectionLoading || isCrownSegmentationLoading}
                          mapDetail="Use the farm you can see right now"
                          fileDetail="Use a map file you already have"
                              accept="image/*,.tif,.tiff,.geotiff"
                          fileName={cropfieldDelineationImage?.name ?? null}
                          mapReady={Boolean(getOlMap?.())}
                          zoomReady={isFarmLevelMapZoom}
                          onModeChange={(mode) => {
                            setCropfieldDelineationInputMode(mode);
                            if (mode === 'mapView') setCropfieldDelineationImage(null);
                            setCropfieldDelineationError('');
                          }}
                          onFile={(file) => {
                                  setCropfieldDelineationImage(file);
                                  setCropfieldDelineationError('');
                          }}
                        />

                        {(isTreeDetectionLoading || isCrownSegmentationLoading) && !isCropfieldDelineationLoading && (
                          <p className={`mb-3 text-xs ${lt.muted}`}>
                            Another tool is running — finish it before field delineation.
                          </p>
                        )}

                        {cropfieldDelineationError && (
                          <div className={`mb-3 p-2 rounded text-xs border ${isDark ? 'bg-red-900/30 text-red-300 border-red-700' : 'bg-red-50 text-red-600 border-red-200'}`}>
                            <i className="ri-error-warning-line mr-1"></i>
                            {cropfieldDelineationError}
                          </div>
                        )}

                        {isCropfieldDelineationLoading && (
                          <div className={`mb-3 p-3 space-y-2.5 text-xs ${lt.infoNote}`}>
                            <div className={`flex items-center gap-2 ${cropfieldDelineationStep === 'uploading' ? lt.selectedText : (isDark ? 'text-gray-400' : 'text-gray-500')}`}>
                              {cropfieldDelineationStep === 'uploading' ? (
                                <div className="animate-spin h-3.5 w-3.5 border-2 border-current rounded-full border-t-transparent flex-shrink-0" />
                              ) : (
                                <i className={`ri-checkbox-circle-fill ${lt.sectionIcon} flex-shrink-0`} />
                              )}
                              <span className={cropfieldDelineationStep === 'uploading' ? 'font-medium animate-pulse' : ''}>
                                {cropfieldDelineationInputMode === 'mapView'
                                  ? 'Preparing from map view...'
                                  : 'Uploading your file...'}
                              </span>
                            </div>
                            <div className={`flex items-center gap-2 ${cropfieldDelineationStep === 'delineating' ? lt.selectedText : (isDark ? 'text-gray-400' : 'text-gray-500')}`}>
                              {cropfieldDelineationStep === 'delineating' ? (
                                <div className="animate-spin h-3.5 w-3.5 border-2 border-current rounded-full border-t-transparent flex-shrink-0" />
                              ) : cropfieldDelineationStep === 'uploading' ? (
                                <div className="h-3.5 w-3.5 rounded-full border-2 border-gray-400 flex-shrink-0" />
                              ) : (
                                <i className={`ri-checkbox-circle-fill ${lt.sectionIcon} flex-shrink-0`} />
                              )}
                              <span className={cropfieldDelineationStep === 'delineating' ? 'font-medium animate-pulse' : ''}>
                                {cropfieldDelineationInputMode === 'mapView' || isTifFile(cropfieldDelineationImage)
                                  ? 'Finding field boundaries... Large areas may take longer.'
                                  : 'Finding field boundaries...'}
                              </span>
                            </div>
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={handleCropfieldDelineationSubmit}
                          disabled={
                            isCropfieldDelineationLoading ||
                            (cropfieldDelineationInputMode === 'file' && !cropfieldDelineationImage) ||
                            (cropfieldDelineationInputMode === 'mapView' && (!getOlMap?.() || !isFarmLevelMapZoom)) ||
                            isTreeDetectionLoading ||
                            isCrownSegmentationLoading
                          }
                          className={`w-full py-2 px-3 text-xs font-medium rounded-md transition-colors ${
                            isCropfieldDelineationLoading ||
                            (cropfieldDelineationInputMode === 'file' && !cropfieldDelineationImage) ||
                            (cropfieldDelineationInputMode === 'mapView' && (!getOlMap?.() || !isFarmLevelMapZoom)) ||
                            isTreeDetectionLoading ||
                            isCrownSegmentationLoading
                              ? isDark
                                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : lt.primaryBtn
                          }`}
                        >
                          {isCropfieldDelineationLoading ? (
                            <span className="flex items-center justify-center">
                              <div className="animate-spin h-3 w-3 border-2 border-white rounded-full border-t-transparent mr-2"></div>
                              {cropfieldDelineationStep === 'uploading'
                                ? cropfieldDelineationInputMode === 'mapView'
                                  ? 'Preparing view...'
                                  : 'Uploading...'
                                : 'Finding fields...'}
                            </span>
                          ) : (
                            <span className="flex items-center justify-center">
                              <i className="ri-shape-line mr-1"></i>
                              Find fields
                            </span>
                          )}
                        </button>
                        {isCropfieldDelineationLoading && (
                          <CancelAnalysisButton isDark={isDark} onClick={cancelCropfieldDelineation} />
                        )}
                        {!isCropfieldDelineationLoading && cropfieldDelineationInputMode === 'file' && !cropfieldDelineationImage && (
                          <p className={`mt-2 text-sm ${lt.muted}`}>Choose a file, then you can find the fields.</p>
                        )}
                      </div>
                    )}
                  </div>
                  )}
                </div>
              )}

                  {expandedTab && expandedTab !== 'forecast' && expandedTab !== 'disasterLossDamage' && expandedTab !== 'lowCarbon' && expandedTab !== 'treeDetection' && expandedTab !== 'droneData' && expandedTab !== 'fieldServices' && layerConfigs[expandedTab]?.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <i className="ri-landscape-line text-3xl mb-2"></i>
                      <p className="text-sm">No layers available in this category</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

    {/* Layer Switch Confirmation Dialog */}
    <LayerSwitchConfirmDialog
      isOpen={showConfirmDialog}
      currentLayer={expandedTab ? tabNames[expandedTab] : ''}
      nextLayer={pendingTab ? tabNames[pendingTab] : ''}
      onConfirm={handleConfirmSwitch}
      onCancel={handleCancelSwitch}
    />

    <InfoPopupDialog
      open={genericSectionInfo != null}
      onClose={() => setGenericSectionInfo(null)}
      title={genericSectionInfo?.title ?? ''}
      accent="emerald"
    >
      <p>{genericSectionInfo?.body}</p>
    </InfoPopupDialog>

    <InfoPopupDialog
      open={showCadiInfo}
      onClose={() => setShowCadiInfo(false)}
      title="About El Niño CADI"
      accent="amber"
    >
      <p className="mb-3">{CADI_DROUGHT_DESCRIPTION}</p>
      <p className="text-xs opacity-90">
        <span className="font-semibold">Legend: </span>
        {CADI_DROUGHT_LEGEND_DESCRIPTION}
      </p>
    </InfoPopupDialog>

    <InfoPopupDialog
      open={showPestRiskInfo}
      onClose={() => setShowPestRiskInfo(false)}
      title="About Pest Occurrence Probability Index"
      accent="orange"
    >
      <p className="mb-3">{PEST_RISK_DESCRIPTION}</p>
      <p className="text-xs opacity-90">
        <span className="font-semibold">Legend: </span>
        {PEST_RISK_LEGEND_DESCRIPTION}
      </p>
    </InfoPopupDialog>

    <InfoPopupDialog
      open={showStrawBurnInfo}
      onClose={() => setShowStrawBurnInfo(false)}
      title="About rice straw burning detection"
      accent="red"
    >
      <p className="mb-3">
        Calibrated Relative Burn Index (RBI) from Sentinel-2 on the Philippines rice area map
        (SEA_Phil_Rice), excluding water.
      </p>
      <p className="text-xs opacity-90">
        <span className="font-semibold">Threshold: </span>
        Burned if RBI is between 0.0 and 0.9 (drone-calibrated).
      </p>
    </InfoPopupDialog>

    <InfoPopupDialog
      open={showFloodDamageInfo}
      onClose={() => setShowFloodDamageInfo(false)}
      title="About these damage numbers"
      accent="blue"
    >
      <p className="mb-3">
        Each row is one municipality in the selected flood event. All values are in Philippine pesos (₱). The three numbers are an estimate and a likely range around it.
      </p>
      <p className="mb-2">
        <span className="font-semibold">{FLOOD_DAMAGE_COLUMN_HELP.total.label}. </span>
        {FLOOD_DAMAGE_COLUMN_HELP.total.text}
      </p>
      <p className="mb-2">
        <span className="font-semibold">{FLOOD_DAMAGE_COLUMN_HELP.ciLow.label}. </span>
        {FLOOD_DAMAGE_COLUMN_HELP.ciLow.text}
      </p>
      <p className="mb-2">
        <span className="font-semibold">{FLOOD_DAMAGE_COLUMN_HELP.ciHigh.label}. </span>
        {FLOOD_DAMAGE_COLUMN_HELP.ciHigh.text}
      </p>
      <p className="text-xs opacity-90">
        Read it as: damage is probably between <span className="font-semibold">CI low</span> and <span className="font-semibold">CI high</span>, with <span className="font-semibold">Total</span> as the central estimate.
      </p>
    </InfoPopupDialog>


    <style jsx>{`
      .opacity-slider::-webkit-slider-thumb {
        appearance: none;
        height: 12px;
        width: 12px;
        border-radius: 50%;
        background: #008540;
        cursor: pointer;
        border: 2px solid white;
        box-shadow: 0 1px 2px rgba(0,0,0,0.2);
      }

      .opacity-slider--dark::-webkit-slider-thumb {
        background: #34d399;
      }

      .opacity-slider::-moz-range-thumb {
        height: 12px;
        width: 12px;
        border-radius: 50%;
        background: #008540;
        cursor: pointer;
        border: 2px solid white;
        box-shadow: 0 1px 2px rgba(0,0,0,0.2);
      }

      .opacity-slider--dark::-moz-range-thumb {
        background: #34d399;
      }
    `}</style>

    {/* Crown Segmentation Full Screen Modal */}
    </>
  );

  // If embedded, render content directly without toggle button
  if (embedded) {
    const legendContent = activeLegends.length > 0 ? (
      <div
        ref={legendRef}
        className={`${lg.anchorFixed} ${lg.shellCompact}`}
      >
            <div className="space-y-2 sm:space-y-3">
              {activeLegends.map((legend, legendIndex) => (
                <div key={legend.key} className={legendIndex > 0 ? `pt-2 sm:pt-3 ${lg.divider}` : ''}>
                  {/* Show title only for non-crop area, non-forecast, non-bsri, and non-strawBurn legends */}
                  {legend.type !== 'cropArea' && legend.type !== 'forecast' && legend.type !== 'bsri' && legend.type !== 'strawBurn' && legend.type !== 'plantingMethod' && legend.type !== 'riceGrowthStage' && legend.type !== 'irrigationType' && (
                    <h5 className={`${lg.titleSm} mb-1.5`}>{legend.label}</h5>
                  )}

                  {(legend.type === 'multiTemporal' || legend.type === 'droneData') && legend.url && (
                    <div>
                      <img
                        src={legend.url}
                        alt={`${legend.label} Legend`}
                        className={lg.imgBorder}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  {(legend.type === 'plantingMethod' || legend.type === 'riceGrowthStage' || legend.type === 'irrigationType') && legend.data && (
                    <div className="space-y-1.5">
                      <h5 className={`${lg.titleSm} mb-1`}>{legend.label}</h5>
                      {legend.data.colors.map((color, index) => (
                        <div key={index} className="flex items-center space-x-1.5">
                          <div
                            className={lg.swatchBorder}
                            style={{ backgroundColor: color }}
                          ></div>
                          <span className={lg.labelSm}>
                            {legend.data?.labels[index]}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {(legend.type === 'cropArea' || legend.type === 'forecast' || legend.type === 'bsri' || legend.type === 'strawBurn') && legend.data && (
                    <div className="space-y-1.5">
                      {/* Rice Forecast, BSRI, and Straw Burn: Legend styles */}
                      {(legend.type === 'forecast' || legend.type === 'bsri' || legend.type === 'strawBurn') ? (
                        <div className="space-y-1.5">
                          {/* Title - For Forecast, BSRI and Planting Method */}
                          {legend.type === 'forecast' && (
                            <div className={`${lg.subtitle} mb-1.5`} style={{ fontFamily: "'Poppins', sans-serif" }}>
                              Forecasted Yield (t/ha)
                            </div>
                          )}
                          
                          {legend.type === 'bsri' && (
                            <div className={`${lg.titleSm} mb-1.5`}>
                              Black Sigatoka Risk Index
                            </div>
                          )}

                          {/* Straw Burn: Single red rectangle with text label */}
                          {legend.type === 'strawBurn' ? (
                            <div className="flex items-center gap-1.5">
                              <div 
                                className={`${lg.swatchBorder} bg-red-600`}
                              ></div>
                              <span className={lg.labelSm}>Rice Straw Burning Area</span>
                            </div>
                          ) : (
                            /* Forecast & BSRI: Enhanced gradient with value markers */
                            <div className="relative w-full">
                              <div
                                className={lg.gradientBorder}
                                style={{
                                  background: legend.data?.colors && legend.data.colors.length > 0
                                    ? `linear-gradient(to right, ${legend.data.colors.join(', ')})`
                                    : 'linear-gradient(to right, #d73027, #fee08b, #1a9850)'
                                }}
                              ></div>
                              {/* Value markers on gradient (Forecast: t/ha, BSRI: risk index) */}
                              {(legend.type === 'forecast' || legend.type === 'bsri') && legend.data && (() => {
                                const d = legend.data as { min?: number; max?: number };
                                const dataMin = typeof d.min === 'number' ? d.min : 0;
                                const dataMax = typeof d.max === 'number' ? d.max : (legend.type === 'forecast' ? 7.5 : 5);
                                return (
                                  <div className="absolute inset-0 flex items-center justify-between px-1">
                                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
                                      const value = dataMin + ratio * (dataMax - dataMin);
                                      return (
                                        <div key={idx} className="flex flex-col items-center">
                                          <div className="w-0.5 h-1.5 bg-white opacity-80"></div>
                                          {idx % 2 === 0 && (
                                            <span className="text-sm font-semibold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] mt-0.5">
                                              {value.toFixed(1)}
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Crop Area: Discrete color blocks */
                        <>
                          {legend.data?.colors.map((color, index) => (
                            <div key={index} className="flex items-center space-x-1.5">
                              <div
                                className={lg.swatchBorder}
                                style={{ backgroundColor: color }}
                              ></div>
                              <span className={lg.labelSm}>
                                {legend.data?.labels[index]}
                              </span>
                            </div>
                          ))}

                          {/* Total Area Display */}
                          {legend.type === 'cropArea' && legend.data.totalArea && (
                            <div className={`pt-1.5 mt-1.5 ${lg.divider}`}>
                              <div className="flex items-center justify-between text-sm">
                                <span className={lg.totalLabel}>Total Area:</span>
                                <span className={lg.totalValue}>
                                  {legend.data.totalArea.toLocaleString()} ha
                                </span>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
    ) : null;

    return (
      <>
        {renderPanelContent()}
        {/* Legend Panel - Left Side (Outside Panel, just above Time Series Slider) - Rendered via Portal */}
        {typeof document !== 'undefined' && legendContent && createPortal(legendContent, document.body)}
        <DroneServiceRequestModal
          open={droneServiceType !== null}
          serviceType={droneServiceType}
          analysisMode="large-scale"
          onClose={() => setDroneServiceType(null)}
        />
      </>
    );
  }

  return (
    <>
    <div className="absolute top-2 right-2 sm:top-4 sm:right-4 z-20">
      {/* Toggle Button - Menu Icon */}
      <button
        onClick={() => {
          if (onSwitchToLargeScaleMode) {
            // Always switch to large-scale mode in Analysis Panel when clicked
            setIsOpen(false); // Close the overlay panel
            onSwitchToLargeScaleMode();
          } else {
            setIsOpen(!isOpen);
          }
        }}
        className={`bg-white rounded-lg shadow-lg p-2 sm:p-3 hover:shadow-xl transition-all duration-300 border-2 relative ${
          isOpen ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
        }`}
        title="Layer Controls"
      >
        <i className={`ri-menu-3-line text-xl sm:text-2xl ${isOpen ? 'text-emerald-600' : 'text-gray-600'}`}></i>
        {/* Active layers indicator badge */}
        {(getVisibleLayersCount('forecast') + getVisibleLayersCount('multiTemporal') + getVisibleLayersCount('droneData') + getVisibleLayersCount('cropArea') + getVisibleLayersCount('disasterLossDamage') + getVisibleLayersCount('lowCarbon')) > 0 && (
          <div className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-emerald-500 text-white text-[8px] sm:text-[10px] font-bold rounded-full flex items-center justify-center shadow-md">
            {getVisibleLayersCount('forecast') + getVisibleLayersCount('multiTemporal') + getVisibleLayersCount('droneData') + getVisibleLayersCount('cropArea') + getVisibleLayersCount('disasterLossDamage') + getVisibleLayersCount('lowCarbon')}
          </div>
        )}
      </button>

      {/* Panel with Sidebar Layout */}
      {isOpen && renderPanelContent()}
    </div>
    <DroneServiceRequestModal
      open={droneServiceType !== null}
      serviceType={droneServiceType}
      analysisMode="large-scale"
      onClose={() => setDroneServiceType(null)}
    />
    </>
  );
}